<template>
  <transition name="slide">
    <div
      v-if="show"
      class="booking-drawer-overlay"
      :class="{ 'booking-drawer-overlay--cart': cartMode }"
      @click.self="onOverlayClick"
    >
      <div class="booking-drawer">
        <!-- Header -->
        <div class="drawer-header" :style="headerBorderStyle">
          <div class="drawer-header__top">
            <div class="drawer-header__ctas">
              <template v-if="!loading">
                <!-- Public booking with child selected: only show collect for that child.
                     Hidden on the collect-payment screen itself to avoid duplicating the action. -->
                <template v-if="isPublicWithChildren && activeChild">
                  <button
                    v-if="canCreatePayment && parseFloat(activeChild.due) > 0 && drawerView !== 'collect-payment'"
                    class="drawer-header-cta"
                    @click="drawerView = 'collect-payment'"
                  >
                    Collect {{ getAmount(activeChild.due) }}
                  </button>
                  <send-to-pos-button
                    v-if="canCreatePayment && parseFloat(activeChild.due) > 0 && activeChild.transaction_id && drawerView !== 'collect-payment'"
                    variant="header-cta"
                    :transaction-id="Number(activeChild.transaction_id)"
                    :amount="Number(activeChild.due) || null"
                    @sent="onClose"
                    @already-paid="onClose"
                  />
                </template>
                <!-- Public booking overview: Add group / Add to cart hidden once the
                     slot has been upgraded to private — no other group can join.
                     Also hidden on the create-booking screen to avoid recursion. -->
                <template v-else-if="isPublicWithChildren && !activeChild && !isPrivateReservation && drawerView !== 'create-booking'">
                  <button v-if="canCreateBooking" class="drawer-header-cta" @click="drawerView = 'create-booking'">
                    Add group
                  </button>
                  <button v-if="canCreateBooking" class="drawer-header-cta-secondary" @click="emitAddToCart">
                    <i class="tim-icons icon-cart mr-1"></i> Add to cart
                  </button>
                </template>
                <!-- Non-public bookings: original behavior. Book/Add to cart hidden
                     on create-booking screen; Collect hidden on collect-payment. -->
                <template v-else>
                  <template v-if="showBookingCta && canCreateBooking && drawerView !== 'create-booking'">
                    <button class="drawer-header-cta" @click="drawerView = 'create-booking'">
                      Book
                    </button>
                    <button class="drawer-header-cta-secondary" @click="emitAddToCart">
                      <i class="tim-icons icon-cart mr-1"></i> Add to cart
                    </button>
                  </template>
                  <button
                    v-if="(isBooked || isCompleted) && canCreatePayment && currentBookingData && parseFloat(currentBookingData.due) > 0 && drawerView !== 'collect-payment'"
                    class="drawer-header-cta"
                    @click="drawerView = 'collect-payment'"
                  >
                    Collect {{ getAmount(currentBookingData.due) }}
                  </button>
                  <send-to-pos-button
                    v-if="(isBooked || isCompleted) && canCreatePayment && currentBookingData && parseFloat(currentBookingData.due) > 0 && currentBookingData.transaction_id && drawerView !== 'collect-payment'"
                    variant="header-cta"
                    :transaction-id="Number(currentBookingData.transaction_id)"
                    :amount="Number(currentBookingData.due) || null"
                    @sent="onClose"
                    @already-paid="onClose"
                  />
                </template>
              </template>
              <button class="close-btn" @click="onClose" title="Close">
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </div>
          </div>

          <!-- Cart-mode header: hide the per-event identity (game thumb +
               name + date/time) since the drawer in cart mode is composing
               a transaction across MANY events. Show a "Cart (N)" header
               that mirrors the toolbar's count badge instead. -->
          <div v-if="cartMode" class="d-flex align-items-center mt-3">
            <div class="header-title-block">
              <h4 class="mb-0 header-title">
                Cart
                <span v-if="cartCount > 0" class="header-cart-count">({{ cartCount }})</span>
              </h4>
            </div>
          </div>
          <!-- Game identity (non-cart mode) -->
          <div v-else class="d-flex align-items-center mt-3">
            <img
              v-if="headerGameImage"
              v-twin-src="headerGameImage"
              :alt="headerGameName"
              class="game-thumb mr-3"
            />
            <div class="header-title-block">
              <h4 class="mb-0 header-title">{{ headerGameName }}</h4>
              <div v-if="headerMetaParts.length" class="header-meta">
                <template v-for="(part, i) in headerMetaParts">
                  <span :key="'p' + i" class="header-meta__part">{{ part }}</span>
                  <span
                    v-if="i < headerMetaParts.length - 1"
                    :key="'s' + i"
                    class="header-meta__sep"
                    aria-hidden="true"
                  >·</span>
                </template>
              </div>
              <!-- Minimum booking notice (OTC-384): also visible on touch,
                   where the slot cards' title tooltips do not exist -->
              <div v-if="headerSlotNotice" class="mt-2" style="margin-bottom: -6px;">
                <span
                  class="badge"
                  :class="headerSlotNotice.state === 'closed' ? 'badge-danger' : 'badge-warning'"
                  :title="headerSlotNotice.title"
                >
                  <i class="tim-icons mr-1" :class="headerSlotNotice.state === 'closed' ? 'icon-lock-circle' : 'icon-time-alarm'"></i>{{ headerSlotNotice.label }}
                </span>
              </div>
            </div>
          </div>

        </div>

        <!-- ===== DETAIL VIEW ===== -->
        <template v-if="drawerView === 'detail'">

        <!-- Loading state -->
        <div v-if="loading" class="drawer-body">
          <div class="skeleton-loader">
            <!-- Section header -->
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 40%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 3" :key="'a'+n">
                <div class="skeleton-row" style="width: 30%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 45%; height: 12px;"></div>
              </div>
            </div>
            <!-- Section header -->
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 35%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 4" :key="'b'+n">
                <div class="skeleton-row" style="width: 25%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 50%; height: 12px;"></div>
              </div>
            </div>
            <!-- Section header -->
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 30%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 2" :key="'c'+n">
                <div class="skeleton-row" style="width: 35%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 40%; height: 12px;"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div v-else class="drawer-body">

          <!-- Private reservation indicator (slot was upgraded to private) -->
          <div v-if="isPrivateReservation" class="private-reservation-banner">
            <i class="tim-icons icon-check-2"></i>
            <span><strong>Private reservation</strong> &mdash; this booking was upgraded to private</span>
          </div>

          <!-- Pending booking request — rendered as an extension of the drawer
               header chrome (edge to edge, flush under the header) rather than
               a card above the section stack. The header already carries the
               teal status color as its top border; this strip only washes. -->
          <div
            v-if="isRequested && pendingRequest && !loading && (canUpdateBooking || canEdit)"
            class="request-strip"
          >
            <div class="request-strip__text">
              <span class="request-strip__title">Booking request</span>
              <span class="request-strip__hint">
                <template v-if="pendingRequest.payment_capture_mode === 'charge_now' && requestRefundLabel">Customer paid {{ requestRefundLabel }}. Denying releases the slot and refunds automatically.</template>
                <template v-else-if="pendingRequest.payment_capture_mode === 'on_approval'">No charge until approved. Denying releases the slot.</template>
                <template v-else>Approve to confirm the booking, or deny to release the slot.</template>
              </span>
            </div>
            <div class="request-strip__actions">
              <button class="request-strip__approve" :disabled="approvingRequest" @click="approveRequest">
                <i v-if="approvingRequest" class="el-icon-loading mr-1"></i>
                {{ approvingRequest ? 'Approving' : 'Approve' }}
              </button>
              <button class="request-strip__deny" :disabled="approvingRequest" @click="drawerView = 'deny_request'">
                Deny
              </button>
            </div>
          </div>

          <!-- Incomplete booking — slot is "booked" but has no transaction.
               Mirrors Bookings.vue:1694–1737 reset flow. Predicate matches
               legacy: status=booked, no transaction, not a public master. -->
          <div v-if="isIncompleteBooking" class="incomplete-booking-banner">
            <div class="incomplete-booking-banner__alert">
              <i class="tim-icons icon-settings mr-2"></i>
              <div>
                <strong>Incomplete booking</strong> — this slot shows as booked but has no associated transaction due to issues during processing. You can reset it to make the slot available again.
              </div>
            </div>
            <div v-if="drawerData.booking && drawerData.booking.group_size > 0" class="incomplete-booking-banner__meta text-white">
              <i class="tim-icons icon-single-02 mr-2"></i>Group size was set to {{ drawerData.booking.group_size }} people
            </div>
            <div class="incomplete-booking-banner__form">
              <base-input label="Reset slot to status">
                <el-select
                  v-model="incompleteResetStatus"
                  class="select-primary"
                  value-key="id"
                  placeholder="Status"
                >
                  <el-option
                    v-for="(status, index) in slotStatuses"
                    class="select-primary"
                    :label="status.label"
                    :value="{ id: status.id, label: status.label, status: status.status }"
                    :key="'INCOMPLETE_STATUS' + index"
                  />
                </el-select>
              </base-input>
            </div>
            <div class="incomplete-booking-banner__actions">
              <base-button
                size="sm"
                type="primary"
                :loading="resettingIncomplete"
                :disabled="!incompleteResetStatus || resettingIncomplete || !canEditCurrent"
                @click="resetIncompleteSlot"
              >
                Reset slot
              </base-button>
            </div>
          </div>

          <!-- Public booking group switcher -->
          <div v-if="isPublicWithChildren" class="public-booking-nav">
            <span
              class="nav-pill"
              :class="{ active: activeChildId === null }"
              @click="activeChildId = null"
            >Overview</span>
            <span
              v-for="(child, idx) in publicChildren"
              :key="child.id"
              class="nav-pill"
              :class="{ active: activeChildId === child.id }"
              @click="activeChildId = child.id"
            >{{ child.customer_first_name || ('Group ' + (idx + 1)) }}</span>
          </div>

          <!-- Public booking overview -->
          <div v-if="isPublicWithChildren && activeChildId === null" class="overview-section">
            <div class="overview-stats">
              <div class="overview-stat">
                <span class="overview-stat__value text-white">{{ publicAggregated.total_groups }}</span>
                <span class="overview-stat__label">Groups</span>
              </div>
              <div class="overview-stat">
                <span class="overview-stat__value text-white">{{ publicAggregated.total_guests }}</span>
                <span class="overview-stat__label">Guests</span>
              </div>
              <div class="overview-stat">
                <span class="overview-stat__value text-white">{{ getAmount(publicAggregated.total_paid) }}</span>
                <span class="overview-stat__label">Paid</span>
              </div>
              <div class="overview-stat">
                <span class="overview-stat__value" :class="publicAggregated.total_due > 0 ? 'text-warning' : 'text-white'">{{ getAmount(publicAggregated.total_due) }}</span>
                <span class="overview-stat__label">Due</span>
              </div>
            </div>
            <div class="overview-groups-list">
              <div
                v-for="(child, idx) in publicChildren"
                :key="child.id"
                class="overview-group-row"
                @click="activeChildId = child.id"
              >
                <div class="overview-group-row__info">
                  <span class="text-white">{{ child.customer_first_name ? (child.customer_first_name + ' ' + (child.customer_last_name || '')).trim() : ('Group ' + (idx + 1)) }}</span>
                  <span class="text-muted small">{{ child.group_size }} {{ child.group_size === 1 ? 'guest' : 'guests' }}<template v-if="childDurationLabel(child)"> · {{ childDurationLabel(child) }}</template></span>
                </div>
                <div class="d-flex align-items-center" style="gap: 8px;">
                  <span v-if="parseFloat(child.due) > 0" class="text-warning small">{{ getAmount(child.due) }} due</span>
                  <span v-else class="text-success small">Paid</span>
                  <i class="tim-icons icon-minimal-right" style="font-size: 10px; color: rgba(255,255,255,0.3);"></i>
                </div>
              </div>
            </div>

            <!-- Block remaining seats (OTC-1459): hold the leftover capacity on
                 this public slot so no more customers can book it. The existing
                 groups stay; staff can still add a group manually. Reversible. -->
            <div class="remaining-block-row">
              <div class="remaining-block-row__text">
                <span class="remaining-block-row__title text-white"><i class="tim-icons icon-lock-circle"></i>Block remaining seats</span>
                <span class="remaining-block-row__desc">
                  {{ remainingBlocked
                    ? 'Customers can\'t book this slot. You can still add a group manually, and reopen any time.'
                    : 'Stop customers from booking the leftover spots. You can still add a group manually, and reopen any time.' }}
                </span>
              </div>
              <el-switch
                :value="remainingBlocked"
                :disabled="!canEditCurrent || savingRemainingBlocked"
                active-color="#e14eca"
                @change="toggleRemainingBlocked"
              />
            </div>
          </div>

          <!-- Per-group workflow state (kanban only). Each group moves through
               workflow states independently, so it's editable here on the
               group's own tab rather than on the master overview. -->
          <div v-if="isKanban && isPublicWithChildren && activeChild && workflowStates.length > 0" class="bd-section">
            <div class="section-content">
              <div class="detail-row text-white">
                <span><i class="tim-icons icon-components mr-2"></i>Workflow state</span>
                <span
                  v-if="canEditCurrent && !loading"
                  class="status-badge-inline status-badge-inline--editable"
                  :class="{ 'status-badge-inline--unassigned': !workflowState }"
                  :style="workflowDetailBadgeStyle"
                >
                  {{ workflowState ? workflowState.name : 'Unassigned' }}
                  <i v-if="savingWorkflowState" class="el-icon-loading status-badge-inline__icon"></i>
                  <i v-else class="el-icon-arrow-down status-badge-inline__icon"></i>
                  <el-select
                    :value="currentWorkflowStateId"
                    class="status-badge-inline__select"
                    popper-class="booking-drawer__status-popper"
                    placeholder=""
                    :disabled="savingWorkflowState"
                    @change="onWorkflowStateChange"
                  >
                    <el-option :value="null" label="Unassigned"></el-option>
                    <el-option
                      v-for="state in workflowStates"
                      :key="state.id"
                      :value="state.id"
                      :label="state.name"
                    ></el-option>
                  </el-select>
                </span>
                <span v-else class="status-badge-inline" :class="{ 'status-badge-inline--unassigned': !workflowState }" :style="workflowDetailBadgeStyle">
                  {{ workflowState ? workflowState.name : 'Unassigned' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Slot details (all statuses, hidden when viewing a child group) -->
          <div v-if="!activeChild" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.slotDetails }"
              @click="toggleSection('slotDetails')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-calendar-60 mr-2"></i>
                <span>Slot details</span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <!-- Reschedule lives in the neutral action row near the drawer
                     footer (same row public sub-bookings use), not up here. -->
                <button
                  v-if="canEdit && !loading && !isCompleted"
                  class="section-header__action section-header__action--labeled"
                  @click.stop="drawerView = 'edit-slot'"
                >
                  <i class="tim-icons icon-pencil"></i>
                  <span>Edit slot</span>
                </button>
                <i class="tim-icons" :class="sections.slotDetails ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.slotDetails" class="section-content">
              <div class="detail-row text-white" v-if="booking">
                <span>Slot ID</span>
                <span class="d-flex align-items-center" style="gap: 4px;">
                  #{{ booking.id }}
                  <el-tooltip content="Copy slot ID" placement="top">
                    <button class="copy-btn" @click="copySlotId">
                      <i class="el-icon-copy-document"></i>
                    </button>
                  </el-tooltip>
                </span>
              </div>
              <div class="detail-row text-white">
                <span>Status</span>
                <!-- Status read-only once booked/completed (matches backend
                     companies-edit-booking guard at line 1590). For other
                     statuses, an inline picker lets staff change without
                     opening the full Edit Slot form — Morgan's main ask.
                     Uses el-select rather than el-dropdown so the popper
                     reliably appends to body and doesn't get clipped by
                     the drawer's scroll container. -->
                <span
                  v-if="canEdit && !isStatusLocked"
                  class="status-badge-inline status-badge-inline--editable"
                  :style="statusBadgeStyle"
                >
                  {{ bookingStatusLabel }}
                  <i v-if="savingStatus" class="el-icon-loading status-badge-inline__icon"></i>
                  <i v-else class="el-icon-arrow-down status-badge-inline__icon"></i>
                  <el-select
                    :value="drawerData.booking ? drawerData.booking.status : ''"
                    class="status-badge-inline__select"
                    popper-class="booking-drawer__status-popper"
                    placeholder=""
                    :disabled="savingStatus"
                    @change="onStatusChange"
                  >
                    <el-option
                      v-for="opt in availableStatuses"
                      :key="opt.value"
                      :label="opt.label"
                      :value="opt.value"
                    ></el-option>
                  </el-select>
                </span>
                <span v-else class="status-badge-inline" :style="statusBadgeStyle">{{ bookingStatusLabel }}</span>
              </div>
              <!-- Kanban only: hidden for public slots with groups, since workflow
                   state is per group there and lives on each child's tab. Shown
                   for normal bookings, and for public slots in calendar/list
                   views (where the parent slot's state is what's tracked). -->
              <div class="detail-row text-white" v-if="workflowStates.length > 0 && !(isKanban && isPublicWithChildren)">
                <span>Workflow state</span>
                <!-- Editable workflow picker — same inline-badge pattern as
                     Status (el-select is body-rendered to avoid scroll-clip,
                     stretched invisibly over the badge for click target). -->
                <span
                  v-if="canEditCurrent && !loading"
                  class="status-badge-inline status-badge-inline--editable"
                  :class="{ 'status-badge-inline--unassigned': !workflowState }"
                  :style="workflowDetailBadgeStyle"
                >
                  {{ workflowState ? workflowState.name : 'Unassigned' }}
                  <i v-if="savingWorkflowState" class="el-icon-loading status-badge-inline__icon"></i>
                  <i v-else class="el-icon-arrow-down status-badge-inline__icon"></i>
                  <el-select
                    :value="currentWorkflowStateId"
                    class="status-badge-inline__select"
                    popper-class="booking-drawer__status-popper"
                    placeholder=""
                    :disabled="savingWorkflowState"
                    @change="onWorkflowStateChange"
                  >
                    <el-option :value="null" label="Unassigned"></el-option>
                    <el-option
                      v-for="state in workflowStates"
                      :key="state.id"
                      :value="state.id"
                      :label="state.name"
                    ></el-option>
                  </el-select>
                </span>
                <span v-else class="status-badge-inline" :class="{ 'status-badge-inline--unassigned': !workflowState }" :style="workflowDetailBadgeStyle">
                  {{ workflowState ? workflowState.name : 'Unassigned' }}
                </span>
              </div>
              <div class="detail-row text-white" v-if="drawerData.booking">
                <span>Date</span>
                <span v-if="isMultiDay">{{ formattedBookingDate }} - {{ formattedEndDate }}</span>
                <span v-else>{{ formattedBookingDate }}</span>
              </div>
              <div class="detail-row text-white" v-if="drawerData.booking">
                <span>Time</span>
                <span v-if="drawerData.booking.is_all_day">{{ drawerData.booking.all_day_text || 'All day' }}</span>
                <span v-else>{{ formattedStartTime }} - {{ formattedEndTime }}</span>
              </div>
              <div class="detail-row text-white" v-if="drawerData.booking && drawerData.booking.booking_made_at">
                <span>Booked on</span>
                <span>{{ getLocalDate(drawerData.booking.booking_made_at) }}, {{ getLocalTime(drawerData.booking.booking_made_at) }}</span>
              </div>
              <div class="detail-row text-white" v-if="drawerData.booking && drawerData.booking.utm_source">
                <span>Source</span>
                <utm-attribution
                  mode="line"
                  icon="icon-world"
                  :source="drawerData.booking.utm_source"
                  :medium="drawerData.booking.utm_medium"
                  :campaign="drawerData.booking.utm_campaign"
                  :referrer="drawerData.booking.utm_referrer" />
              </div>
              <div class="detail-row detail-row--stack text-white" v-if="drawerData.booking && drawerData.booking.custom_message">
                <span>Custom message</span>
                <span class="custom-message-cell">
                  <span class="custom-message-cell__text">{{ drawerData.booking.custom_message }}</span>
                  <span class="custom-message-cell__meta">
                    <badge type="default" class="custom-message-cell__badge">{{ drawerData.booking.custom_message_display === 'badge' ? 'Badge' : 'Text' }}</badge>
                    <span v-if="!drawerData.booking.custom_message_active" class="custom-message-cell__hidden">Hidden from customers</span>
                  </span>
                </span>
              </div>
            </div>

            <!-- Reschedule row for private booked slots, attached to the
                 Slot details card so it sits next to Edit slot instead of
                 at the drawer foot (venue feedback: bottom placement was
                 hidden behind a scroll). Public sub-bookings keep their own
                 row near the foot; the master overview has no reschedule. -->
            <div
              v-if="isBooked && canUpdateBooking && !isPublicWithChildren"
              class="neutral-action-row neutral-action-row--attached"
            >
              <div class="neutral-action-info">
                <span class="neutral-action-label text-white">Reschedule booking</span>
                <span class="neutral-action-hint text-muted">Move this booking to a different time slot</span>
              </div>
              <base-button
                size="sm"
                simple
                class="neutral-action-btn"
                @click="drawerView = 'reschedule'"
              >
                <i class="tim-icons icon-refresh-02 mr-1" style="font-size: 11px;"></i>
                Reschedule
              </base-button>
            </div>
          </div>

          <!-- Customer (booked, completed, custom w/ txn) -->
          <div v-if="showPerChildSections" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.customer }"
              @click="toggleSection('customer')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-single-02 mr-2"></i>
                <span>Customer</span>
              </div>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <el-tooltip v-if="canUpdateBooking && currentBookingData.transaction_id" content="Edit customer" placement="top">
                  <button
                    class="section-header__action"
                    @click.stop="drawerView = 'edit-customer'"
                  >
                    <i class="tim-icons icon-pencil"></i>
                  </button>
                </el-tooltip>
                <a
                  v-if="canViewCustomer && currentBookingData.end_user_id"
                  class="customer-link text-primary"
                  @click.stop.prevent="viewCustomer"
                  href="#"
                >
                  View details <i class="tim-icons el-icon-top-right" style="font-size: 10px;"></i>
                </a>
                <i class="tim-icons" :class="sections.customer ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.customer" class="section-content">
              <template v-if="currentBookingData.customer_first_name || currentBookingData.customer_last_name">
                <p class="mb-1 text-white">
                  <strong>{{ currentBookingData.customer_first_name }} {{ currentBookingData.customer_last_name }}</strong>
                </p>
                <p v-if="currentBookingData.customer_email" class="mb-1 small text-white">
                  <i class="tim-icons icon-email-85 mr-1"></i>
                  {{ currentBookingData.customer_email }}
                </p>
                <p v-if="currentBookingData.customer_phone" class="mb-0 small text-white">
                  <i class="tim-icons icon-mobile mr-1"></i>
                  {{ formatPhoneDisplay(currentBookingData.customer_phone, countryCode) }}
                </p>
                <!-- The customer's own library tags, read-only (managed on the
                     customer profile); per-group customer on public bookings -->
                <div
                  v-if="displayedCustomerTags.length"
                  class="tags-row mt-2"
                >
                  <span
                    v-for="tag in displayedCustomerTags"
                    :key="'cust-tag' + tag.id"
                    class="tag-chip text-white"
                    :class="{ 'tag-chip--archived': tag.is_archived }"
                    :style="tagChipStyle(tag.color)"
                  >
                    {{ tag.name }}
                  </span>
                </div>
                <info-hint
                  v-if="Number(currentBookingData.customer_is_archived) === 1"
                  variant="warning"
                  icon="icon-alert-circle-exc"
                  class="mb-0"
                >
                  This customer is archived and hidden from your customer list. Open their details to restore them.
                </info-hint>
              </template>
              <p v-else class="text-muted small mb-0">
                <i class="tim-icons icon-lock-circle mr-1"></i>
                No customer assigned to this slot
              </p>
            </div>
          </div>

          <!-- Tickets (booked/completed — shows purchased categories + pricing model) -->
          <div v-if="showPerChildSections && currentTickets.length > 0" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.tickets }"
              @click="toggleSection('tickets')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-badge mr-2"></i>
                <span>Tickets</span>
                <span class="count-badge">{{ totalTicketPlayers }}</span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <el-tooltip v-if="canUpdateBooking" content="Edit tickets" placement="top">
                  <button class="section-header__action" @click.stop="drawerView = 'edit-tickets'">
                    <i class="tim-icons icon-pencil"></i>
                  </button>
                </el-tooltip>
                <i class="tim-icons" :class="sections.tickets ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.tickets" class="section-content">
              <!-- Ticket rows -->
              <div class="tickets-list">
                <div v-for="ticket in currentTickets" :key="ticket.id" class="ticket-row">
                  <div class="ticket-info">
                    <span class="text-white">{{ ticket.category || 'General' }}</span>
                    <span class="text-muted small">{{ ticket.number_of_players }} {{ ticket.number_of_players === 1 ? 'participant' : 'participants' }}</span>
                  </div>
                  <!-- Buyout hides the per-ticket price (a distribution artifact); the
                       single buyout line below carries the amount. -->
                  <span v-if="!isBuyoutUpgrade" class="text-white">{{ getAmount(ticket.price) }}</span>
                </div>
                <!-- Tickets+fee upgrade: show the nominal private fee so the
                     per-ticket lines + fee reconcile to the booking subtotal. -->
                <div v-if="privateBookingFee > 0" class="ticket-row">
                  <div class="ticket-info">
                    <span class="text-white">Private booking fee</span>
                  </div>
                  <span class="text-white">{{ getAmount(privateBookingFee) }}</span>
                </div>
                <!-- Buyout upgrade: one buyout line in place of per-ticket prices. -->
                <div v-if="isBuyoutUpgrade" class="ticket-row">
                  <div class="ticket-info">
                    <span class="text-white">Private buyout</span>
                  </div>
                  <span class="text-white">{{ getAmount(buyoutAmount) }}</span>
                </div>
              </div>

              <!-- Total -->
              <div class="tickets-total">
                <span class="text-white font-weight-bold">Total group size</span>
                <span class="text-white font-weight-bold">{{ totalTicketPlayers }} participants &middot; {{ getAmount(isBuyoutUpgrade ? buyoutAmount : ticketsCardTotal) }}</span>
              </div>

              <!-- Pricing model reference (collapsible) -->
              <div class="pricing-reference mt-3">
                <div class="pricing-reference__header" @click="pricingModelExpanded = !pricingModelExpanded">
                  <span class="text-muted small font-weight-bold">PRICING MODEL</span>
                  <i class="tim-icons" :class="pricingModelExpanded ? 'icon-minimal-up' : 'icon-minimal-down'" style="font-size: 10px; color: rgba(255,255,255,0.4);"></i>
                </div>
                <div v-show="pricingModelExpanded">
                  <div class="detail-row text-white">
                    <span>Source</span>
                    <span>{{ pricingSourceLabel }}</span>
                  </div>
                  <div class="detail-row text-white">
                    <span>Type</span>
                    <span>{{ pricingTypeLabel }}</span>
                  </div>
                  <div class="detail-row text-white">
                    <span>Reservation</span>
                    <span>{{ reservationStyleLabel }}</span>
                  </div>
                  <div class="detail-row text-white" v-if="drawerData.pricing">
                    <span>Cumulative</span>
                    <span>{{ drawerData.pricing.cumulative_category_pricing ? 'Yes' : 'No' }}</span>
                  </div>
                  <div class="detail-row text-white" v-if="drawerData.pricing">
                    <span>Limit one category</span>
                    <span>{{ drawerData.pricing.limit_purchase_category ? 'Yes' : 'No' }}</span>
                  </div>
                  <div class="detail-row text-white" v-if="durationsLabel">
                    <span>Durations</span>
                    <span>{{ durationsLabel }}</span>
                  </div>
                  <div class="detail-row text-white" v-if="bookedDurationLabel">
                    <span>Booked duration</span>
                    <span>{{ bookedDurationLabel }}</span>
                  </div>
                  <div class="detail-row text-white">
                    <span>Deposit</span>
                    <span>{{ depositLabel }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Transaction summary (booked, completed, custom w/ txn) -->
          <div v-if="showPerChildSections" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.transaction }"
              @click="toggleSection('transaction')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-money-coins mr-2"></i>
                <span class="d-none d-sm-inline">Transaction summary</span>
                <span class="d-sm-none">Transaction</span>
                <el-tooltip v-if="parseFloat(transactionFinancials.due) > 0" :content="getAmount(transactionFinancials.due) + ' due'" placement="top" :open-delay="300">
                  <i class="tim-icons icon-alert-circle-exc text-warning ml-2" style="font-size: 14px;"></i>
                </el-tooltip>
              </div>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <a
                  v-if="canViewTransaction && currentBookingData.transaction_id"
                  class="customer-link text-primary"
                  @click.stop.prevent="viewTransaction"
                  href="#"
                >
                  View details <i class="tim-icons el-icon-top-right" style="font-size: 10px;"></i>
                </a>
                <i class="tim-icons" :class="sections.transaction ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.transaction" class="section-content">
              <transaction-summary-block
                :current-booking="{
                  game_name: currentBookingData.game_name,
                  group_size: currentBookingData.group_size,
                  price: currentBookingData.price,
                  display_date: formattedBookingDate,
                  display_time: formattedStartTime
                }"
                :sibling-bookings="siblingBookings"
                :line-items="currentLineItems"
                :gift-cards="currentGiftCards"
                :financials="transactionFinancials"
                :tax-lines="transactionTaxLines"
                :fee-lines="transactionFeeLines"
                :discount-lines="transactionDiscountLines"
                :tip-lines="transactionTipLines"
                :transaction-id="currentBookingData.transaction_id"
                :company-id="companyId"
                :company-group-id="companyGroupId"
                :currency="currency"
                :currency-symbol="currencySymbol"
                :currency-format="currencyFormat"
                :number-format="numberFormat"
                :can-update="canUpdateBooking"
                :can-add-booking="canUpdateBooking && !!currentBookingData.transaction_id"
                @updated="onTransactionSummaryUpdated"
                @add-item="drawerView = 'add-item'"
                @add-booking="onAddAnotherBooking"
              />
            </div>
          </div>

          <!-- Payments (booked, completed, custom w/ txn) -->
          <div v-if="showPerChildSections && currentBookingData.transaction_id" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.payments }"
              @click="toggleSection('payments')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-credit-card mr-2"></i>
                <span>Payments</span>
                <el-tooltip v-if="parseFloat(transactionFinancials.due) > 0" :content="getAmount(transactionFinancials.due) + ' due'" placement="top" :open-delay="300">
                  <i class="tim-icons icon-alert-circle-exc text-warning ml-2" style="font-size: 14px;"></i>
                </el-tooltip>
              </div>
              <i class="tim-icons" :class="sections.payments ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            </div>
            <div v-show="sections.payments" class="section-content">
              <div v-if="currentActivePayments.length === 0" class="empty-state small">
                <i class="tim-icons icon-money-coins text-muted mb-2" style="font-size: 20px; display: block;"></i>
                <p class="text-muted small mb-0">No payments recorded</p>
              </div>
              <div v-else class="payments-list-flat">
                <div v-for="payment in currentActivePayments" :key="payment.id" class="payment-row">
                  <div class="payment-row-left">
                    <span class="payment-method-label text-white">
                      <span v-if="payment.type === 'card'">
                        <i class="tim-icons icon-credit-card mr-1"></i>
                        Card<span v-if="payment.last_four" class="text-muted"> &bull;&bull;&bull;&bull; {{ payment.last_four }}</span>
                      </span>
                      <span v-else-if="payment.type === 'cash'">
                        <i class="tim-icons icon-money-coins mr-1"></i>
                        Cash
                      </span>
                      <span v-else-if="payment.type === 'gift_card'">
                        <i class="tim-icons icon-gift-2 mr-1"></i>
                        Gift card
                      </span>
                      <span v-else-if="payment.type === 'customer_credit'">
                        <i class="tim-icons icon-wallet-43 mr-1"></i>
                        Customer credit
                      </span>
                      <span v-else-if="payment.type === 'pos'">
                        <i class="tim-icons icon-tap-02 mr-1"></i>
                        POS payment
                      </span>
                      <span v-else>
                        <i class="tim-icons icon-bank mr-1"></i>
                        {{ payment.type }}
                      </span>
                    </span>
                    <span class="payment-row-date text-muted">{{ getLocalDate(payment.timestamp) }}</span>
                  </div>
                  <div class="payment-row-right-col">
                    <span class="text-white">{{ getAmount(payment.amount) }}</span>
                    <span v-if="payment.refunded > 0" class="text-danger small">
                      <i class="tim-icons icon-refresh-02 mr-1" style="font-size: 10px;"></i>-{{ getAmount(payment.refunded) }}
                    </span>
                  </div>
                </div>
              </div>
              <button
                v-if="canCreatePayment && parseFloat(transactionFinancials.due) > 0"
                class="collect-payment-link"
                @click="drawerView = 'collect-payment'"
              >
                <i class="tim-icons icon-simple-add mr-1"></i>
                Collect payment
              </button>
            </div>
          </div>

          <!-- Custom fields (booked, completed, custom w/ txn) -->
          <div v-if="showPerChildSections && currentCustomFields.length > 0" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.customFields }"
              @click="toggleSection('customFields')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-bullet-list-67 mr-2"></i>
                <span>Custom fields</span>
                <span class="count-badge">{{ currentCustomFields.length }}</span>
                <span v-if="alertedCustomFields.length > 0" class="alert-badge">
                  <i class="el-icon-warning-outline"></i>
                  {{ alertedCustomFields.length }} alert{{ alertedCustomFields.length === 1 ? '' : 's' }}
                </span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <el-dropdown trigger="click" @command="copyCustomFields" @click.native.stop>
                  <span class="section-header__link text-primary">
                    <i class="tim-icons icon-single-copy-04" style="font-size: 10px;"></i> Copy
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="text">Copy as text</el-dropdown-item>
                    <el-dropdown-item command="csv">Copy as CSV</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                <i class="tim-icons" :class="sections.customFields ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <!-- Alerts always visible when section is collapsed -->
            <div v-if="alertedCustomFields.length > 0 && !sections.customFields" class="section-content section-content--peek">
              <div v-for="field in alertedCustomFields" :key="'alert-' + field.id" class="alert-field">
                <i class="el-icon-warning-outline alert-field__icon"></i>
                <div class="alert-field__body">
                  <span class="alert-field__label text-muted">{{ field.label }}</span>
                  <span class="alert-field__value text-white">{{ formatFieldResponse(field) }}</span>
                </div>
              </div>
            </div>
            <div v-show="sections.customFields" class="section-content">
              <div class="custom-fields-list">
                <div
                  v-for="field in currentCustomFields"
                  :key="field.id"
                  class="custom-field-item"
                  :class="{ 'custom-field-item--alert': field.add_alert && (field.response || field.response === 0 || field.response === '0') }"
                >
                  <span class="field-label text-muted">
                    <i v-if="field.add_alert && (field.response || field.response === 0 || field.response === '0')" class="el-icon-warning-outline custom-field-item__alert-icon"></i>
                    {{ field.label }}
                  </span>
                  <span class="field-value text-white">{{ formatFieldResponse(field) }}</span>
                  <span class="custom-field-item__meta">
                    <template v-if="field.source">
                      <span class="custom-field-item__meta-label">Source:</span>
                      <custom-field-source-badge :source="field.source" />
                    </template>
                    <span v-if="field.timestamp" class="custom-field-item__timestamp">
                      {{ getLocalDate(field.timestamp) }}, {{ getLocalTime(field.timestamp) }}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Customer communication (booked/completed with a transaction).
               Per-child: lives inside the per-child section block so each public
               sub-booking gets its own section against its own customer/transaction. -->
          <div
            v-if="showPerChildSections && currentBookingData.transaction_id && (isBooked || isCompleted)"
            class="bd-section"
          >
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.customerCommunication }"
              @click="toggleSection('customerCommunication')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-email-85 mr-2"></i>
                <span>Customer communication</span>
              </div>
              <i class="tim-icons" :class="sections.customerCommunication ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            </div>
            <div v-show="sections.customerCommunication" class="section-content">
              <!-- Stats summary: counts come from a Send-anchored aggregation
                   of email_events (one count per email, not per raw SES event). -->
              <div v-if="commSectionLoading" class="comm-stats comm-stats--loading">
                <span>Loading…</span>
              </div>
              <div v-else-if="commStats.sent > 0" class="comm-stats">
                <span class="comm-stat">{{ commStats.sent }} email{{ commStats.sent === 1 ? '' : 's' }} sent</span>
                <span class="comm-stat-sep" aria-hidden="true">·</span>
                <span class="comm-stat">{{ commStats.opened }} opened</span>
                <template v-if="commStats.bounced > 0">
                  <span class="comm-stat-sep" aria-hidden="true">·</span>
                  <span class="comm-stat comm-stat--negative">{{ commStats.bounced }} bounced</span>
                </template>
              </div>
              <div v-else class="comm-stats comm-stats--empty">
                <span>No emails sent yet</span>
              </div>

              <div class="comm-actions">
                <button
                  class="comm-action-btn comm-action-btn--primary"
                  @click="drawerView = 'send-email'"
                >
                  <i class="tim-icons icon-send mr-1"></i>
                  Send email
                </button>
                <button
                  class="comm-action-btn"
                  @click="drawerView = 'communication-history'"
                >
                  <i class="tim-icons icon-bullet-list-67 mr-1"></i>
                  View history
                </button>
              </div>
            </div>
          </div>

          <!-- Waivers -->
          <div v-if="currentWaivers.length > 0" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.waivers }"
              @click="toggleSection('waivers')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-notes mr-2"></i>
                <span>Waivers</span>
                <span class="count-badge">{{ currentWaivers.length }}</span>
              </div>
              <i class="tim-icons" :class="sections.waivers ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            </div>
            <div v-show="sections.waivers" class="section-content">
              <div class="waivers-list-flat">
                <div v-for="waiver in currentWaivers" :key="waiver.id" class="waiver-row">
                  <div class="waiver-info">
                    <div class="waiver-name-row">
                      <span class="waiver-name text-white">{{ waiver.first_name }} {{ waiver.last_name }}</span>
                      <span v-if="Number(waiver.is_minor) === 1" class="waiver-tag waiver-tag--minor">Minor</span>
                      <span v-if="Number(waiver.not_participating) === 1" class="waiver-tag waiver-tag--not-participating">Not participating</span>
                    </div>
                    <div class="waiver-contact text-muted">
                      <span v-if="waiver.email"><i class="tim-icons icon-email-85" style="font-size: 10px;"></i> {{ waiver.email }}</span>
                      <span v-if="waiver.phone"><i class="tim-icons icon-mobile" style="font-size: 10px;"></i> {{ formatPhoneDisplay(waiver.phone, countryCode) }}</span>
                    </div>
                  </div>
                  <div class="waiver-row-actions">
                    <span class="waiver-date text-muted">{{ getLocalDate(waiver.created_time) }}, {{ getLocalTime(waiver.created_time) }}</span>
                    <a
                      class="section-header__link text-white waiver-download-link"
                      :class="{ 'is-busy': pdfGeneratingId === waiver.id }"
                      href="#"
                      @click.stop.prevent="downloadWaiver(waiver)"
                    >
                      <i :class="pdfGeneratingId === waiver.id ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'" style="font-size: 14px;"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <!-- Off-screen branded PDF renderer; generate() is called from Download PDF -->
            <waiver-pdf-document ref="waiverPdf" />
          </div>

          <!-- Notes (all statuses, hidden when viewing a child group) -->
          <div v-if="!activeChild" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.notes }"
              @click="toggleSection('notes')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-pencil mr-2"></i>
                <span>Notes</span>
                <span v-if="bookingNotes.length > 0" class="count-badge">{{ bookingNotes.length }}</span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <a v-if="canManageNotesCurrent" class="section-header__link text-primary" href="#" @click.stop.prevent="drawerView = 'notes'">
                  <i class="el-icon-plus" style="font-size: 10px;"></i> Add
                </a>
                <i class="tim-icons" :class="sections.notes ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <!-- Important note always visible when collapsed -->
            <div v-if="importantNote && !sections.notes" class="section-content section-content--peek">
              <div class="important-note">
                <i class="el-icon-star-on important-note__star"></i>
                <p class="important-note__text text-white">{{ importantNote.note }}</p>
                <el-tooltip v-if="canManageNotesCurrent && importantNote.id" content="Delete note" effect="light" :open-delay="300" placement="top">
                  <button class="note-inline__action-btn important-note__delete" @click.stop="deleteNoteFromInline(importantNote)">
                    <i class="el-icon-delete"></i>
                  </button>
                </el-tooltip>
              </div>
            </div>
            <div v-show="sections.notes" class="section-content">
              <div v-if="bookingNotes.length > 0" class="notes-inline-list">
                <div
                  v-for="note in sortedNotes"
                  :key="note.id"
                  class="note-inline"
                  :class="{ 'note-inline--important': note.is_important }"
                >
                  <div class="d-flex align-items-start" style="gap: 6px;">
                    <i v-if="note.is_important" class="el-icon-star-on" style="color: #ffd600; font-size: 12px; margin-top: 2px;"></i>
                    <p class="note-inline__text text-white mb-0">{{ note.note }}</p>
                  </div>
                  <div class="note-inline__footer">
                    <div class="note-inline__meta text-muted">
                      <span v-if="note.first_name || note.last_name">{{ note.first_name }} {{ note.last_name }} &middot; </span>
                      {{ getLocalDate(note.timestamp) }}, {{ getLocalTime(note.timestamp) }}
                    </div>
                    <div v-if="canManageNotesCurrent && note.id" class="note-inline__actions">
                      <el-tooltip content="Delete note" effect="light" :open-delay="300" placement="top">
                        <button class="note-inline__action-btn" @click="deleteNoteFromInline(note)">
                          <i class="el-icon-delete"></i>
                        </button>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state small">
                <i class="tim-icons icon-pencil text-muted mb-2" style="font-size: 20px; display: block;"></i>
                <p class="text-muted small mb-0">No notes yet</p>
              </div>
            </div>
          </div>

          <!-- Tags (all statuses, hidden when viewing a child group) -->
          <div v-if="!activeChild" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.tags }"
              @click="toggleSection('tags')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-tag mr-2"></i>
                <span>Tags</span>
                <span v-if="drawerData.tags.length > 0" class="count-badge">{{ drawerData.tags.length }}</span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <a v-if="canUpdateBooking && !loading" class="section-header__link text-primary" href="#" @click.stop.prevent="openAddTag">
                  <i class="el-icon-plus" style="font-size: 10px;"></i> Add
                </a>
                <i class="tim-icons" :class="sections.tags ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.tags" class="section-content">
              <div class="tags-row">
                <span
                  v-for="tag in drawerData.tags"
                  :key="'bdt' + tag.id"
                  class="tag-chip text-white"
                  :class="{ 'tag-chip--archived': tag.is_archived }"
                  :style="tagChipStyle(tag.color)"
                >
                  {{ tag.name }}
                  <button
                    v-if="canUpdateBooking"
                    class="tag-chip__remove"
                    :disabled="savingTags"
                    :aria-label="'Remove tag ' + tag.name"
                    @click.stop="removeTag(tag)"
                  >
                    <i class="tim-icons icon-simple-remove"></i>
                  </button>
                </span>
                <el-dropdown
                  v-if="canUpdateBooking && !loading"
                  ref="tagAddDropdown"
                  trigger="click"
                  @command="onTagCommand"
                  @visible-change="onTagDropdownVisible"
                  @click.native.stop
                >
                  <span class="tag-chip tag-chip--add">
                    <i v-if="savingTags" class="el-icon-loading mr-1" style="color: #e14eca;"></i>
                    <i v-else class="tim-icons icon-simple-add mr-1"></i>
                    Add tag
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-if="groupTagsLoading" disabled>
                      <i class="el-icon-loading mr-1" style="color: #e14eca;"></i> Loading tags
                    </el-dropdown-item>
                    <template v-else>
                      <el-dropdown-item
                        v-for="tag in availableTags"
                        :key="'gtag' + tag.id"
                        :command="tag.id"
                      >
                        <span class="tag-chip__dot mr-1" :style="{ backgroundColor: tag.color }"></span>
                        {{ tag.name }}
                      </el-dropdown-item>
                      <el-dropdown-item v-if="availableTags.length === 0" disabled>No more tags</el-dropdown-item>
                    </template>
                    <el-dropdown-item divided command="manage">Manage tags</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                <span v-if="drawerData.tags.length === 0 && !canUpdateBooking" class="text-muted small">No tags</span>
              </div>
            </div>
          </div>

          <!-- Staff assignment (hidden when viewing a child group) -->
          <div v-if="!activeChild" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.staff }"
              @click="toggleSection('staff')"
            >
              <div class="d-flex align-items-center">
                <i class="el-icon-user mr-2"></i>
                <span>Staff</span>
                <span v-if="drawerData.staff.length > 0" class="count-badge">{{ drawerData.staff.length }}</span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <a
                  v-if="canEditCurrent && !showStaffPicker"
                  class="section-header__link text-primary"
                  href="#"
                  @click.stop.prevent="showStaffPicker = true; sections.staff = true;"
                >
                  <i class="el-icon-plus" style="font-size: 10px;"></i> Add
                </a>
                <a
                  v-if="canViewProject && drawerData.parent_ticket_id && drawerData.bookings_project_id"
                  class="section-header__link text-primary"
                  :href="'/projects/' + drawerData.bookings_project_id + '/tickets/' + drawerData.parent_ticket_id"
                  target="_blank"
                  @click.stop
                  title="View project ticket"
                >
                  View task <i class="el-icon-top-right" style="font-size: 10px;"></i>
                </a>
                <i class="tim-icons" :class="sections.staff ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.staff" class="section-content">
              <!-- Assigned staff list -->
              <div v-if="drawerData.staff.length > 0 && !showStaffPicker" class="staff-list">
                <div v-for="member in drawerData.staff" :key="member.id" class="staff-item">
                  <div class="staff-item__left">
                    <span class="staff-avatar" :style="{ background: staffColors[member.id % staffColors.length] }">
                      {{ (member.first_name || member.email || '?').charAt(0).toUpperCase() }}
                    </span>
                    <div class="staff-item__info">
                      <span class="staff-item__name text-white">{{ member.first_name ? (member.first_name + ' ' + member.last_name) : member.email }}</span>
                      <span class="staff-item__email text-muted small">{{ member.email }}</span>
                    </div>
                  </div>
                  <div v-if="canEditCurrent" class="staff-item__remove" @click.stop="removeStaff(member)">
                    <el-tooltip content="Remove from slot" placement="top" :open-delay="300">
                      <i class="tim-icons icon-simple-remove"></i>
                    </el-tooltip>
                  </div>
                </div>
              </div>

              <!-- Empty state -->
              <div v-if="drawerData.staff.length === 0 && !showStaffPicker" class="empty-state small">
                <i class="el-icon-user text-muted mb-2" style="font-size: 20px; display: block;"></i>
                <p class="text-muted small mb-0">No staff assigned</p>
              </div>

              <!-- Inline staff picker -->
              <div v-if="showStaffPicker && canEditCurrent" class="staff-picker">
                <div class="staff-picker__input-row">
                  <el-select
                    v-model="staffSearchQuery"
                    placeholder="Search by name or email"
                    class="select-primary"
                    style="flex: 1;"
                    filterable
                    clearable
                    autocomplete="new-password"
                    :filter-method="filterStaff"
                    :disabled="staffSaving"
                    @change="handleStaffSelect"
                    @visible-change="staffFilterText = ''"
                  >
                    <el-option
                      v-for="u in availableStaff"
                      :key="u.id"
                      :label="u.name || (u.first_name + ' ' + u.last_name)"
                      :value="u.id"
                      style="height: auto; padding: 8px 20px; line-height: normal;"
                    >
                      <div class="d-flex align-items-center" style="gap: 8px;">
                        <span class="staff-avatar staff-avatar--sm" :style="{ background: staffColors[u.id % staffColors.length] }">
                          {{ (u.first_name || u.email || '?').charAt(0).toUpperCase() }}
                        </span>
                        <div>
                          <div style="font-size: 13px; color: #344675; line-height: 1.4;">{{ u.first_name }} {{ u.last_name }}</div>
                          <div style="font-size: 11px; color: #9A9A9A; line-height: 1.3;">{{ u.email }}</div>
                        </div>
                      </div>
                    </el-option>
                  </el-select>
                  <button class="staff-picker__cancel" @click="showStaffPicker = false; staffSearchQuery = null; staffFilterText = '';">
                    <i class="tim-icons icon-simple-remove"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Subtasks (kanban only, all statuses, hidden when viewing a child group) -->
          <div v-if="isKanban && !activeChild" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.subtasks }"
              @click="toggleSection('subtasks')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-check-2 mr-2"></i>
                <span>Subtasks</span>
                <span class="ml-2 subtask-progress" :class="{ 'subtask-progress--done': allSubtasksDone }">
                  {{ completedSubtaskCount }}/{{ drawerData.subtasks.length }}
                </span>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <a
                  v-if="drawerData.parent_ticket_id && drawerData.bookings_project_id"
                  class="section-header__link text-primary"
                  :href="'/projects/' + drawerData.bookings_project_id + '/tickets/' + drawerData.parent_ticket_id"
                  target="_blank"
                  @click.stop
                  title="View parent ticket"
                >
                  View task <i class="el-icon-top-right" style="font-size: 10px;"></i>
                </a>
                <a
                  v-if="!showCreateSubtaskForm"
                  class="section-header__link text-primary"
                  href="#"
                  @click.stop.prevent="openCreateSubtaskForm"
                >
                  <i class="el-icon-plus" style="font-size: 10px;"></i> Add
                </a>
                <i class="tim-icons" :class="sections.subtasks ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.subtasks" class="section-content subtasks-section">

              <!-- Subtask rows -->
              <draggable v-model="drawerData.subtasks"
                         handle=".subtask-drag-handle"
                         ghost-class="subtask-ghost"
                         chosen-class="subtask-chosen"
                         :animation="150"
                         :force-fallback="true"
                         :disabled="!!editingSubtaskId || !!showCreateSubtaskForm"
                         @end="onSubtaskDragEnd">
              <div v-for="task in drawerData.subtasks" :key="task.id" v-show="!showCreateSubtaskForm && !editingSubtaskId">

                <!-- View mode -->
                <div class="subtask-row" :class="{ 'subtask-row--done': task.completed }">
                  <i class="el-icon-rank subtask-drag-handle"
                     style="font-size: 11px; cursor: move; flex-shrink: 0; padding: 4px;"
                     @click.stop></i>
                  <input
                    type="checkbox"
                    class="subtask-cb"
                    :checked="task.completed"
                    @change="toggleSubtask(task)"
                  />
                  <a
                    v-if="drawerData.bookings_project_id"
                    class="subtask-title text-white subtask-link"
                    :href="'/projects/' + drawerData.bookings_project_id + '/tickets/' + task.id"
                    target="_blank"
                    @click.stop
                    :style="{ textDecoration: task.completed ? 'line-through' : 'none', opacity: task.completed ? 0.5 : 1 }"
                  >
                    {{ task.title }}
                  </a>
                  <span
                    v-else
                    class="subtask-title text-white"
                    :style="{ textDecoration: task.completed ? 'line-through' : 'none', opacity: task.completed ? 0.5 : 1 }"
                  >
                    {{ task.title }}
                  </span>
                  <el-tooltip v-if="task.assignee_name" :content="task.assignee_name" placement="top">
                    <span class="subtask-assignee-avatar">
                      {{ task.assignee_name.charAt(0).toUpperCase() }}
                    </span>
                  </el-tooltip>
                  <i v-if="getPriority(task.priority)"
                     :class="getPriority(task.priority).icon"
                     :style="{ color: getPriority(task.priority).color, fontSize: '12px', flexShrink: 0 }"
                     :title="getPriority(task.priority).name"></i>
                  <span v-if="task.due_date" style="font-size: 11px; white-space: nowrap; flex-shrink: 0;"
                        :style="{ color: isSubtaskOverdue(task) ? '#ec250d' : '#8898aa' }">
                    <i class="el-icon-date" style="margin-right: 2px;"></i>{{ formatDueDate(task.due_date) }}
                  </span>
                  <div class="subtask-actions" @click.stop>
                    <i class="tim-icons icon-pencil" @click="startEditSubtask(task)" title="Edit"></i>
                    <i class="tim-icons icon-simple-remove" @click="deleteSubtask(task)" title="Delete"></i>
                  </div>
                </div>
              </div>
              </draggable>

              <!-- Edit subtask form -->
              <div v-if="editingSubtaskId" style="padding: 8px 0;">
                <base-input label="Title" style="margin-bottom: 8px;">
                  <div style="position: relative;">
                    <input v-model="editSubtask.title"
                           ref="editSubtaskTitle"
                           class="form-control"
                           placeholder="Subtask title"
                           @keydown.enter.prevent="saveEditSubtask(drawerData.subtasks.find(t => t.id === editingSubtaskId))"
                           @keydown.esc="cancelEditSubtask" />
                    <span style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
                                 font-size: 10px; color: #8898aa; pointer-events: none;">
                      Enter &#x23CE;
                    </span>
                  </div>
                </base-input>
                <div class="d-flex" style="gap: 8px;">
                  <base-input label="Assignee" style="flex: 1; margin-bottom: 0;">
                    <el-select v-model="editSubtask.assignee" placeholder="Assignee"
                               class="select-primary subtask-inline-select" clearable filterable autocomplete="new-password"
                               :filter-method="filterAssignees" @visible-change="assigneeFilterText = ''">
                      <el-option v-for="u in assigneeOptions" :key="u.id" class="select-primary" :label="u.name || u.email" :value="u.id"></el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Priority" style="flex: 1; margin-bottom: 0;">
                    <el-select v-model="editSubtask.priority" placeholder="Priority"
                               class="select-primary subtask-inline-select">
                      <el-option v-for="p in priorities" :key="p.id" class="select-primary" :label="p.name" :value="p.id">
                        <i :class="p.icon" :style="{ color: p.color, marginRight: '6px' }"></i>{{ p.name }}
                      </el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Due date" style="flex: 1; margin-bottom: 0;">
                    <el-date-picker v-model="editSubtask.due_date" type="date"
                      placeholder="No due date" :format="datePickerFormat"
                      value-format="yyyy-MM-dd" clearable style="width: 100%">
                    </el-date-picker>
                  </base-input>
                </div>
                <div class="subtask-form-actions mt-2">
                  <base-button size="sm" type="default" @click="cancelEditSubtask">Cancel</base-button>
                  <base-button class="ml-2" size="sm" type="primary" @click="saveEditSubtask(drawerData.subtasks.find(t => t.id === editingSubtaskId))"
                               :disabled="!editSubtask.title.trim()">Save</base-button>
                </div>
              </div>

              <div v-if="drawerData.subtasks.length === 0 && !showCreateSubtaskForm && !editingSubtaskId" class="empty-state small">
                <i class="tim-icons icon-check-2 text-muted mb-2" style="font-size: 20px; display: block;"></i>
                <p class="text-muted small mb-0">No subtasks yet</p>
              </div>

              <!-- Create subtask form -->
              <div v-if="showCreateSubtaskForm" style="margin-top: 8px;">
                <base-input label="Title" style="margin-bottom: 8px;">
                  <div style="position: relative;">
                    <input v-model="newSubtask.title"
                           ref="createSubtaskTitle"
                           class="form-control"
                           placeholder="Subtask title"
                           @keydown.enter.prevent="createSubtask"
                           @keydown.esc="cancelCreateSubtask" />
                    <span style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
                                 font-size: 10px; color: #8898aa; pointer-events: none;">
                      Enter &#x23CE;
                    </span>
                  </div>
                </base-input>
                <div class="d-flex" style="gap: 8px;">
                  <base-input label="Assignee" style="flex: 1; margin-bottom: 0;">
                    <el-select v-model="newSubtask.assignee" placeholder="Assignee"
                               class="select-primary subtask-inline-select" clearable filterable autocomplete="new-password"
                               :filter-method="filterAssignees" @visible-change="assigneeFilterText = ''">
                      <el-option v-for="u in assigneeOptions" :key="u.id" class="select-primary" :label="u.name || u.email" :value="u.id"></el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Priority" style="flex: 1; margin-bottom: 0;">
                    <el-select v-model="newSubtask.priority" placeholder="Priority"
                               class="select-primary subtask-inline-select">
                      <el-option v-for="p in priorities" :key="p.id" class="select-primary" :label="p.name" :value="p.id">
                        <i :class="p.icon" :style="{ color: p.color, marginRight: '6px' }"></i>{{ p.name }}
                      </el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Due date" style="flex: 1; margin-bottom: 0;">
                    <el-date-picker v-model="newSubtask.due_date" type="date"
                      placeholder="No due date" :format="datePickerFormat"
                      value-format="yyyy-MM-dd" clearable style="width: 100%">
                    </el-date-picker>
                  </base-input>
                </div>
                <div class="subtask-form-actions mt-2">
                  <base-button size="sm" type="default" @click="cancelCreateSubtask">Cancel</base-button>
                  <base-button class="ml-2" size="sm" type="primary" @click="createSubtask"
                               :disabled="!newSubtask.title.trim()">Save</base-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Attachments -->
          <div v-if="isKanban && !activeChild" class="bd-section">
            <div
              class="section-header collapsible text-white"
              :class="{ expanded: sections.attachments }"
              @click="toggleAttachments"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-attach-87 mr-2"></i>
                <span>Attachments</span>
                <span v-if="attachmentDisplayCount > 0" class="count-badge">{{ attachmentDisplayCount }}</span>
              </div>
              <i class="tim-icons" :class="sections.attachments ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            </div>
            <div v-show="sections.attachments" class="section-content">
              <!-- Drop zone -->
              <div class="attachment-dropzone"
                   :class="{ 'dropzone-active': isDragging }"
                   @dragenter.prevent="isDragging = true"
                   @dragover.prevent="isDragging = true"
                   @dragleave.prevent="isDragging = false"
                   @drop.prevent="handleAttachmentDrop"
                   @click="$refs.attachmentInput.click()">
                <i class="tim-icons icon-cloud-upload-94"></i>
                <span>Drop files here or click to browse</span>
                <span class="dropzone-hint">Max 25 MB per file</span>
              </div>
              <input ref="attachmentInput" type="file" multiple
                     accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip"
                     style="display: none;" @change="handleAttachmentFileSelect" />

              <!-- Upload progress -->
              <div v-for="(f, i) in uploadingFiles" :key="'uploading-' + i" class="attachment-uploading">
                <i class="el-icon-loading"></i>
                <span>
                  {{ f.name }}<span v-if="f.progress != null"> — {{ f.progress }}%</span><span v-if="f.retrying"> (retrying)</span>
                </span>
              </div>

              <!-- Error messages -->
              <div v-for="(err, i) in uploadErrors" :key="'att-err-' + i" class="attachment-error">
                <span>{{ err }}</span>
                <i class="tim-icons icon-simple-remove" @click="uploadErrors.splice(i, 1)" style="cursor: pointer;"></i>
              </div>

              <!-- Attachment rows -->
              <div v-for="att in attachments" :key="'att-' + att.id"
                   class="attachment-row"
                   :class="{ 'attachment-deleting': deletingAttachmentId === att.id }">
                <div class="attachment-icon" @click="previewAttachment(att)">
                  <i v-if="isImage(att.content_type)" class="tim-icons icon-image-02" style="color: #e14eca;"></i>
                  <i v-else-if="att.content_type === 'application/pdf'" class="tim-icons icon-paper" style="color: #ef5350;"></i>
                  <i v-else-if="isSpreadsheet(att.content_type)" class="tim-icons icon-chart-bar-32" style="color: #66bb6a;"></i>
                  <i v-else-if="isDocument(att.content_type)" class="tim-icons icon-single-copy-04" style="color: #42a5f5;"></i>
                  <i v-else class="tim-icons icon-attach-87" style="color: rgba(255,255,255,0.4);"></i>
                </div>
                <div class="attachment-info">
                  <span class="attachment-name" :title="att.file_name">{{ att.file_name }}</span>
                  <span class="attachment-meta">{{ formatFileSize(att.file_size) }}</span>
                </div>
                <div class="attachment-actions">
                  <i class="tim-icons icon-cloud-download-93" title="Download" @click="downloadAttachment(att)"></i>
                  <i class="tim-icons icon-trash-simple" title="Delete" @click="confirmDeleteAttachment(att)"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Image lightbox -->
          <div v-if="lightboxImage" class="attachment-lightbox" @click.self="lightboxImage = null">
            <div class="lightbox-content">
              <img :src="lightboxImage" />
              <i class="tim-icons icon-simple-remove lightbox-close" @click="lightboxImage = null"></i>
            </div>
          </div>

          <!-- Event details (completed only, hidden when viewing a child group) -->
          <div v-if="isCompleted && !activeChild" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.gameInstances }"
              @click="toggleSection('gameInstances')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-shape-star mr-2"></i>
                <span>Event details</span>
                <el-tooltip v-if="totalInstanceBreaks > 0" :content="totalInstanceBreaks + (totalInstanceBreaks === 1 ? ' issue' : ' issues')" placement="top">
                  <i class="tim-icons icon-alert-circle-exc ml-2" style="font-size: 12px; color: #F5365C;"></i>
                </el-tooltip>
              </div>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <template v-if="drawerData.game_instances.length > 0">
                  <span
                    v-for="instance in drawerData.game_instances"
                    :key="'badge-' + instance.id"
                    class="instance-status-badge"
                    :class="instance.completed ? 'instance-status-badge--completed' : 'instance-status-badge--progress'"
                  >
                    {{ instance.completed ? 'Completed' : 'Not completed' }}
                  </span>
                  <a
                    v-if="canViewGameHistory"
                    :href="'/games/history/instance?id=' + drawerData.game_instances[0].id"
                    target="_blank"
                    class="instance-link text-primary"
                    @click.stop
                  >
                    View details <i class="tim-icons el-icon-top-right" style="font-size: 10px;"></i>
                  </a>
                </template>
                <i class="tim-icons" :class="sections.gameInstances ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </div>
            </div>
            <div v-show="sections.gameInstances" class="section-content">
              <div v-if="drawerData.game_instances.length === 0" class="empty-state small">
                <i class="tim-icons icon-shape-star text-muted mb-2" style="font-size: 20px; display: block;"></i>
                <p class="text-muted small mb-0">No events tracked yet</p>
              </div>
              <div v-else class="instances-list-flat">
                <div v-for="instance in drawerData.game_instances" :key="instance.id" class="instance-row">
                  <div class="instance-info">
                    <span class="text-white">{{ getInstanceDuration(instance) }}</span>
                    <span v-if="instance.game_master_name" class="text-muted small">Host: {{ instance.game_master_name }}</span>
                    <span v-if="instance.number_of_hints" class="instance-hints small">
                      <i class="tim-icons icon-chat-33" style="font-size: 10px;"></i>
                      {{ instance.number_of_hints }} {{ instance.number_of_hints === 1 ? 'interaction' : 'interactions' }}
                    </span>
                    <span v-if="instance.number_of_breaks" class="instance-breaks small">
                      <i class="tim-icons icon-alert-circle-exc" style="font-size: 10px;"></i>
                      {{ instance.number_of_breaks }} {{ instance.number_of_breaks === 1 ? 'issue' : 'issues' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Slot pricing (non-booked/non-completed only, hidden when viewing a child group) -->
          <div v-if="drawerData.booking && !hasTransactionData && !activeChild" class="bd-section">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.slotPricing }"
              @click="toggleSection('slotPricing')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-money-coins mr-2"></i>
                <span>Slot pricing</span>
              </div>
              <i class="tim-icons" :class="sections.slotPricing ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            </div>
            <div v-show="sections.slotPricing" class="section-content">
              <div class="detail-row text-white">
                <span>Pricing</span>
                <span>{{ pricingSourceLabel }}</span>
              </div>
              <div class="detail-row text-white">
                <span>Pricing type</span>
                <span>{{ pricingTypeLabel }}</span>
              </div>
              <div class="detail-row text-white">
                <span>Reservation style</span>
                <span>{{ reservationStyleLabel }}</span>
              </div>
              <div class="detail-row text-white" v-if="drawerData.pricing">
                <span>Cumulative</span>
                <span>{{ drawerData.pricing.cumulative_category_pricing ? 'Yes' : 'No' }}</span>
              </div>
              <div class="detail-row text-white" v-if="drawerData.pricing">
                <span>Limit one category</span>
                <span>{{ drawerData.pricing.limit_purchase_category ? 'Yes' : 'No' }}</span>
              </div>
              <div class="detail-row text-white" v-if="durationsLabel">
                <span>Durations</span>
                <span>{{ durationsLabel }}</span>
              </div>
              <div class="detail-row text-white" v-if="bookedDurationLabel">
                <span>Booked duration</span>
                <span>{{ bookedDurationLabel }}</span>
              </div>
              <div class="detail-row text-white">
                <span>Deposit</span>
                <span>{{ depositLabel }}</span>
              </div>
              <div class="mt-3" v-if="canEdit">
                <button class="pricing-edit-link text-primary" @click="drawerView = 'edit-pricing'">
                  <i class="tim-icons icon-pencil mr-1"></i> Edit pricing
                </button>
              </div>
            </div>
          </div>

          <!-- Linked resources (hidden when viewing a child group) -->
          <div v-if="!activeChild" class="bd-section" ref="resourcesSection">
            <div
              class="section-header collapsible"
              :class="{ expanded: sections.resources }"
              @click="toggleSection('resources')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-badge mr-2"></i>
                <span>Resources</span>
                <span v-if="visibleResources.length > 0" class="count-badge">{{ visibleResources.length }}</span>
              </div>
              <i class="tim-icons" :class="sections.resources ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            </div>
            <div v-show="sections.resources" class="section-content">
              <div v-if="visibleResources.length > 0" class="resources-list">
                <div v-for="resource in visibleResources" :key="resource.link_id" class="resource-item">
                  <div class="resource-item__left">
                    <el-tooltip :content="resource.is_locked ? 'Locked - will not be auto-reassigned' : 'Unlocked - can be auto-reassigned'" placement="top" :open-delay="300">
                      <i
                        class="resource-item__lock-icon"
                        :class="resource.is_locked ? 'el-icon-lock locked' : 'el-icon-unlock unlocked'"
                        :style="!canEditCurrent ? 'cursor: default; opacity: 0.5;' : ''"
                        @click.stop="canEditCurrent && toggleResourceLock(resource)"
                      ></i>
                    </el-tooltip>
                    <div class="resource-item__info">
                      <span class="resource-item__name text-white">{{ resource.resource_label || 'Unnamed' }}</span>
                      <span class="resource-item__category small">
                        <span v-if="resource.category_color" class="category-dot" :style="{ background: resource.category_color }"></span>
                        {{ resource.category_name }}
                      </span>
                      <span v-if="resourceParty(resource)" class="badge badge-default ml-1">{{ resourceParty(resource) }}</span>
                    </div>
                  </div>
                  <div class="resource-item__right">
                    <span v-if="resource.time_assigned" class="resource-item__time small">
                      {{ formatTimeRaw(resource.time_assigned) }} – {{ formatTimeRaw(resource.time_freed_up) }}
                    </span>
                    <el-dropdown v-if="canUpdateResourceSlot" trigger="click" @command="(cmd) => handleResourceCommand(cmd, resource)">
                      <button type="button" class="resource-item__menu-btn" title="More actions">
                        <i class="tim-icons el-icon-more"></i>
                      </button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <el-dropdown-item command="edit">
                            <i class="tim-icons icon-pencil mr-2"></i>Edit resource slot
                          </el-dropdown-item>
                          <el-dropdown-item command="reassign">
                            <i class="tim-icons icon-refresh-02 mr-2"></i>Reassign to…
                          </el-dropdown-item>
                          <el-dropdown-item command="unassign" divided>
                            <i class="tim-icons icon-simple-remove mr-2"></i>Unassign
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                </div>
              </div>

              <p v-else class="resources-empty small">
                No resources assigned yet.
              </p>

              <!-- Assign / Reassign resource autocomplete -->
              <div v-if="showAddResource" class="mt-3">
                <info-hint v-if="reassignContext" variant="primary" class="mb-2">
                  Reassigning <strong>{{ reassignContext.name }}</strong>
                  <span v-if="reassignContext.category_name">
                    (<span v-if="reassignContext.category_color" class="category-dot" :style="{ background: reassignContext.category_color }"></span>{{ reassignContext.category_name }})
                  </span>
                </info-hint>
                <base-input :label="reassignContext ? 'Replace with' : 'Search resource'">
                  <el-autocomplete
                    style="width: 100%"
                    v-model="addResourceSearchValue"
                    :fetch-suggestions="queryAssignableResources"
                    :placeholder="reassignContext ? 'Search replacement' : 'Search by label or staff name'"
                    :trigger-on-focus="true"
                    @select="handleSelectAssignableResource"
                  >
                    <template #default="{ item }">
                      <div class="assignable-suggestion">
                        <span v-if="item.category_color" class="category-dot" :style="{ background: item.category_color }"></span>
                        <span class="text-primary">{{ item.display_name }}</span>
                        <small v-if="item.category_name" class="font-italic ml-2">{{ item.category_name }}</small>
                      </div>
                    </template>
                  </el-autocomplete>
                </base-input>
                <div class="d-flex align-items-center justify-content-between">
                  <base-checkbox
                    v-if="reassignContext"
                    v-model="reassignShowAll"
                  >
                    <span class="small">Show all categories</span>
                  </base-checkbox>
                  <base-button size="sm" type="secondary" @click="closeAddResource">Cancel</base-button>
                </div>
              </div>

              <div v-else class="mt-3">
                <base-button
                  v-if="canUpdateResourceSlot"
                  size="sm"
                  type="primary"
                  @click="openAddResource"
                >
                  <i class="tim-icons icon-simple-add mr-1"></i>
                  Assign resource
                </base-button>
              </div>
            </div>
          </div>

          <!-- Activity log (all statuses, hidden when viewing a child group) -->
          <div v-if="!activeChild" class="bd-section">
            <div
              class="section-header collapsible text-white"
              @click="handleAction('activity-log')"
            >
              <div class="d-flex align-items-center">
                <i class="tim-icons icon-bullet-list-67 mr-2"></i>
                <span>Activity log</span>
              </div>
              <i class="tim-icons icon-minimal-right"></i>
            </div>
          </div>

          <!-- Reschedule sub-booking (public booking child view only). Private
               slots carry their Reschedule booking row inside the Slot
               details card above. -->
          <div
            v-if="isPublicWithChildren && activeChild && isBooked && canUpdateBooking"
            class="neutral-action-row"
          >
            <div class="neutral-action-info">
              <span class="neutral-action-label text-white">Reschedule sub-booking</span>
              <span class="neutral-action-hint text-muted">Move this group's booking to a different time slot</span>
            </div>
            <base-button
              size="sm"
              simple
              class="neutral-action-btn"
              @click="drawerView = 'reschedule'"
            >
              <i class="tim-icons icon-refresh-02 mr-1" style="font-size: 11px;"></i>
              Reschedule
            </base-button>
          </div>

          <!-- Delete / Cancel slot (not shown for completed). For public
               sub-bookings (activeChild), the row stays visible but its
               labels and target switch — Cancel applies to that child only,
               not the master. Delete-slot is master-only.
               Permission-gated per branch: cancel branch needs bookings_cancel,
               delete branch needs booking_slot_delete. Mirrors BookingDetailModal. -->
          <div
            v-if="!isCompleted && (
              ((isBooked || activeChild) && canCancel) ||
              (!isBooked && !activeChild && canDeleteSlot)
            )"
            class="danger-action-row"
          >
            <div class="danger-action-info">
              <span class="danger-action-label text-white">{{ activeChild ? 'Cancel sub-booking' : (isBooked ? 'Cancel booking' : 'Delete slot') }}</span>
              <span class="danger-action-hint text-muted">{{ activeChild ? "Cancel this group's booking only" : (isBooked ? 'Cancel this booking and release the slot' : 'Permanently remove this time slot') }}</span>
            </div>
            <base-button
              size="sm"
              class="danger-action-btn"
              @click="(isBooked || activeChild) ? handleAction('cancel') : confirmDeleteSlot()"
            >
              <i class="tim-icons mr-1" :class="(isBooked || activeChild) ? 'icon-simple-remove' : 'icon-trash-simple'" style="font-size: 11px;"></i>
              {{ (isBooked || activeChild) ? 'Cancel' : 'Delete' }}
            </base-button>
          </div>

        </div>

        </template>

        <!-- ===== NOTES VIEW ===== -->
        <!-- Wait for fetchDrawerData when launched as initial view, otherwise
             notes are read from drawerData before the lambda resolves. -->
        <div v-if="drawerView === 'notes' && loading" class="drawer-body">
          <div class="skeleton-loader">
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 30%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 3" :key="'notes-'+n">
                <div class="skeleton-row" style="width: 100%; height: 12px;"></div>
              </div>
            </div>
          </div>
        </div>
        <notes-view
          v-if="drawerView === 'notes' && !loading"
          :booking="drawerData.booking"
          :notes="drawerData.notes"
          :can-manage="canManageNotesCurrent"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :date-format="dateFormat"
          :time-format="timeFormat"
          :refresh-data="fetchDrawerData"
          @back="drawerView = 'detail'"
        />

        <!-- ===== ACTIVITY LOG VIEW ===== -->
        <activity-log-view
          v-if="drawerView === 'activity-log'"
          :activity-log="activityLog"
          :loading="activityLogLoading"
          :date-format="dateFormat"
          :time-format="timeFormat"
          :show-day-of-week="showDayOfWeek"
          @back="drawerView = 'detail'"
        />

        <!-- ===== SEND EMAIL VIEW ===== -->
        <!-- Pass currentBookingData so public sub-bookings send against the
             child's own transaction (master record's transaction_id is null). -->
        <send-email-view
          v-if="drawerView === 'send-email'"
          :booking="currentBookingData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :currency-symbol="currencySymbol"
          :email-settings="drawerData.email_settings || []"
          @back="drawerView = 'detail'"
          @sent="onEmailSent"
        />

        <!-- ===== COMMUNICATION HISTORY VIEW ===== -->
        <communication-history-view
          v-if="drawerView === 'communication-history'"
          :booking="currentBookingData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :date-format="dateFormat"
          :time-format="timeFormat"
          @back="drawerView = 'detail'"
        />

        <!-- ===== CANCEL VIEW ===== -->
        <!-- Pass currentBookingData so public sub-bookings cancel against
             the child's slot id (the master record's never gets cancelled
             when only one group is leaving). -->
        <cancel-view
          v-if="drawerView === 'cancel'"
          :booking="currentBookingData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :slot-statuses="slotStatuses"
          @back="drawerView = 'detail'"
          @cancelled="onBookingCancelled"
        />

        <!-- ===== DENY BOOKING REQUEST VIEW ===== -->
        <request-decision-view
          v-if="drawerView === 'deny_request' && pendingRequest"
          :request-id="pendingRequest.id"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :refund-amount-label="requestRefundLabel"
          @back="drawerView = 'detail'"
          @denied="onRequestDecided('denied')"
          @error="onRequestActionError"
        />

        <!-- ===== EDIT SLOT VIEW ===== -->
        <edit-slot-view
          v-if="drawerView === 'edit-slot'"
          :booking="drawerData.booking"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :date-format="dateFormat"
          :time-format="timeFormat"
          :first-day-of-week="firstDayOfWeek"
          :show-day-of-week="showDayOfWeek"
          :custom-statuses="customStatuses"
          :resource-slot="resourceSlot"
          @back="drawerView = 'detail'"
          @updated="onSlotUpdated"
        />

        <!-- ===== EDIT PRICING VIEW ===== -->
        <edit-pricing-view
          v-if="drawerView === 'edit-pricing'"
          :booking="drawerData.booking"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :currency-symbol="currencySymbol"
          :currency="currency"
          :pricing="drawerData.pricing || {}"
          @back="drawerView = 'detail'"
          @updated="onSlotUpdated"
        />

        <!-- ===== RESCHEDULE VIEW ===== -->
        <!-- currentBookingData so public sub-bookings reschedule their own
             slot, not the shared master (which has no transaction_id). -->
        <reschedule-view
          v-if="drawerView === 'reschedule'"
          :booking="currentBookingData"
          :drawer-data="drawerData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :date-format="dateFormat"
          :time-format="timeFormat"
          :first-day-of-week="firstDayOfWeek"
          :show-day-of-week="showDayOfWeek"
          :currency="currency"
          :currency-symbol="currencySymbol"
          :currency-format="currencyFormat"
          :number-format="numberFormat"
          @back="drawerView = 'detail'"
          @rescheduled="onRescheduled"
        />

        <!-- ===== EDIT CUSTOMER VIEW ===== -->
        <!-- Pass currentBookingData so public sub-bookings PUT against their
             own transaction. The master record's transaction_id is null. -->
        <edit-customer-view
          v-if="drawerView === 'edit-customer'"
          :booking="currentBookingData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :country-code="countryCode"
          @back="drawerView = 'detail'"
          @updated="onCustomerUpdated"
        />

        <!-- ===== EDIT TICKETS VIEW ===== -->
        <!-- Pass currentBookingData so public sub-bookings edit against the
             child's tickets. The master record has no tickets of its own. -->
        <edit-tickets-view
          v-if="drawerView === 'edit-tickets'"
          :booking="currentBookingData"
          :drawer-data="drawerData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :currency-symbol="currencySymbol"
          @back="drawerView = 'detail'"
          @updated="onTicketsUpdated"
        />

        <!-- ===== ADD ITEM VIEW ===== -->
        <!-- Triggered from TransactionSummaryBlock's "Add item" link.
             Hits the same POST /transactions/{id}/items endpoint that
             TransactionDetails uses. On success: returns to detail, refetches,
             and auto-expands the transaction summary so the new item is
             immediately visible in the receipt. -->
        <add-item-view
          v-if="drawerView === 'add-item' && currentBookingData.transaction_id"
          :transaction-id="currentBookingData.transaction_id"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :game-id="currentBookingData.game_id"
          :game-name="currentBookingData.game_name"
          :party-size="Number(currentBookingData.group_size) || 0"
          :currency="currency"
          :currency-symbol="currencySymbol"
          :currency-format="currencyFormat"
          :number-format="numberFormat"
          @back="drawerView = 'detail'"
          @updated="onItemAdded"
        />

        <!-- ===== COLLECT PAYMENT VIEW ===== -->
        <!-- Pass currentBookingData so public sub-bookings collect against
             the child's transaction (its own paid/due/total). The master
             record's financial fields are zero/null for public slots. -->
        <!-- Wait for fetchDrawerData when launched as initial view, otherwise
             financials/line-items/gift-cards are computed off empty drawerData. -->
        <div v-if="drawerView === 'collect-payment' && loading" class="drawer-body">
          <div class="skeleton-loader">
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 35%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 3" :key="'cp-a'+n">
                <div class="skeleton-row" style="width: 40%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 30%; height: 12px;"></div>
              </div>
            </div>
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 30%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 2" :key="'cp-b'+n">
                <div class="skeleton-row" style="width: 25%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 50%; height: 12px;"></div>
              </div>
            </div>
          </div>
        </div>
        <collect-payment-view
          v-if="drawerView === 'collect-payment' && !loading"
          :booking="currentBookingData"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :currency-symbol="currencySymbol"
          :currency="currency"
          :currency-format="currencyFormat"
          :number-format="numberFormat"
          :payment-settings="paymentSettings"
          :financials="transactionFinancials"
          :sibling-bookings="siblingBookings"
          :line-items="currentLineItems"
          :gift-cards="currentGiftCards"
          :tax-lines="transactionTaxLines"
          :fee-lines="transactionFeeLines"
          :discount-lines="transactionDiscountLines"
          :tip-lines="transactionTipLines"
          :can-update="canUpdateBooking"
          @back="drawerView = 'detail'"
          @payment-collected="onPaymentCollected"
          @updated="onTransactionSummaryUpdated"
        />

        <!-- ===== CREATE BOOKING VIEW ===== -->
        <!-- Wait for fetchDrawerData to resolve before mounting; the view's
             pricing init runs once on the booking watcher and reads
             resolvedPricing.tiers, which only arrives via the drawer endpoint. -->
        <div v-if="drawerView === 'create-booking' && loading" class="drawer-body">
          <div class="skeleton-loader">
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 35%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 3" :key="'cb-a'+n">
                <div class="skeleton-row" style="width: 40%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 30%; height: 12px;"></div>
              </div>
            </div>
            <div class="skeleton-section">
              <div class="skeleton-row" style="width: 30%; height: 14px; margin-bottom: 16px;"></div>
              <div class="skeleton-detail-row" v-for="n in 2" :key="'cb-b'+n">
                <div class="skeleton-row" style="width: 25%; height: 12px;"></div>
                <div class="skeleton-row" style="width: 50%; height: 12px;"></div>
              </div>
            </div>
          </div>
        </div>
        <create-booking-view
          v-if="drawerView === 'create-booking' && !loading"
          :booking="bookingForCreate"
          :resolved-pricing="drawerData.pricing || null"
          :slot-has-existing-bookings="slotHasExistingBookings"
          :company-id="companyId"
          :company-group-id="companyGroupId"
          :date-format="dateFormat"
          :time-format="timeFormat"
          :currency-symbol="currencySymbol"
          :country-code="countryCode"
          :payment-settings="paymentSettings"
          :taxes="drawerData.taxes || taxes"
          :fees="drawerData.fees || fees"
          :pricing-type-id="pricingTypeId"
          :assigning-request="assigningRequest"
          @back="drawerView = 'detail'"
          @cancel-cart="onClose"
          @created="onBookingCreated"
          @request-approved="$emit('request-approved', $event)"
        />

      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch, getCurrentInstance, onBeforeUnmount, nextTick } from 'vue';
import moment from 'moment';
import _ from 'lodash';
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { tagChipStyle } from '@/util/tagChip';
import { classifySlotNotice, NOTICE_TICK_MS } from '@/util/slotNotice';
import { Auth } from 'aws-amplify';
import currencies from '@/assets/currencies.json';
import { roundCents, sortDurations } from '@/util/pricing';
// Tooltip, Select, Option, InputNumber are registered globally in
// plugins/globalComponents.js — no need to import them locally.
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Autocomplete,
  Switch
} from 'element-ui';
import Badge from '@/components/Badge';
import { formatPhoneDisplay } from '@/util/phoneFormatter';
import { fetchWaiverDetail } from '@/util/waiverSignature';
import { pickerDateFormat as toPickerDateFormat, formatGroupDate, formatGroupTime } from '@/plugins/dates';
import { formatCurrency } from '@/util/currencyFormat';
import { enterAppendMode, exitAppendMode, appendModeState, canAppendToSlot } from '@/util/appendMode';
import { multiSelectState } from '@/util/multiSelect';
import swal from 'sweetalert2';
import draggable from 'vuedraggable';
import NotesView from './drawer-views/NotesView.vue';
import ActivityLogView from './drawer-views/ActivityLogView.vue';
import CancelView from './drawer-views/CancelView.vue';
import RequestDecisionView from './drawer-views/RequestDecisionView.vue';
import EditSlotView from './drawer-views/EditSlotView.vue';
import EditPricingView from './drawer-views/EditPricingView.vue';
import CreateBookingView from './drawer-views/CreateBookingView.vue';
import CollectPaymentView from './drawer-views/CollectPaymentView.vue';
import RescheduleView from './drawer-views/RescheduleView.vue';
import EditCustomerView from './drawer-views/EditCustomerView.vue';
import EditTicketsView from './drawer-views/EditTicketsView.vue';
import AddItemView from './drawer-views/AddItemView.vue';
import SendEmailView from '@/components/SendEmailView.vue';
import CommunicationHistoryView from './drawer-views/CommunicationHistoryView.vue';
import TransactionSummaryBlock from './components/TransactionSummaryBlock.vue';
import SendToPosButton from '@/components/POS/SendToPosButton.vue';
import TransactionWatcher from '@/services/transactionWatcher';

export default {
  name: 'BookingDrawer',

  components: {
    Badge,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Autocomplete.name]: Autocomplete,
    [Switch.name]: Switch,
    NotesView,
    ActivityLogView,
    CancelView,
    RequestDecisionView,
    EditSlotView,
    EditPricingView,
    CreateBookingView,
    CollectPaymentView,
    RescheduleView,
    EditCustomerView,
    EditTicketsView,
    AddItemView,
    SendEmailView,
    CommunicationHistoryView,
    TransactionSummaryBlock,
    SendToPosButton,
    draggable
  },

  // ──────────────────────────────────────────────────────────────────────────
  // BookingDrawer is rendered in FIVE places. Any new prop added below MUST
  // be wired in ALL FIVE or it silently defaults to its `default:` value when
  // the drawer is opened from the un-wired views — silently breaking permission
  // gates, currency formatting, etc. (OTC-2301 hit this exact failure mode.)
  //
  //   1. src/pages/Bookings/Bookings/Bookings.vue
  //   2. src/pages/Bookings/Bookings/Calendar.vue
  //   3. src/pages/Bookings/Bookings/Kanban/KanbanView.vue
  //   4. src/pages/Bookings/Bookings/ResourceCalendar/ResourceCalendarView.vue
  //
  // Quick check before shipping:  grep -rn "<booking-drawer\|<BookingDrawer" src/
  // ──────────────────────────────────────────────────────────────────────────
  props: {
    show: { type: Boolean, default: false },
    booking: { type: Object, default: () => ({}) },
    companyId: { type: [Number, String], default: null },
    companyGroupId: { type: [Number, String], default: null },
    canEdit: { type: Boolean, default: false },
    canCancel: { type: Boolean, default: false },
    canDeleteSlot: { type: Boolean, default: false },
    canUpdateBooking: { type: Boolean, default: false },
    canManageNotes: { type: Boolean, default: false },
    canCreateBooking: { type: Boolean, default: false },
    canUpdateResourceSlot: { type: Boolean, default: false },
    canViewProject: { type: Boolean, default: false },
    canViewCustomer: { type: Boolean, default: false },
    canViewTransaction: { type: Boolean, default: false },
    // Minimum booking notice indicators (OTC-384): { show, mode, anchor } from
    // the parent view's group policy. Null hides the header notice entirely.
    noticePolicy: { type: Object, default: null },
    canViewGameHistory: { type: Boolean, default: false },
    canCreatePayment: { type: Boolean, default: false },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: '12h' },
    currency: { type: String, default: 'USD' },
    currencySymbol: { type: String, default: '$' },
    currencyFormat: { type: Number, default: 0 },
    numberFormat: { type: Number, default: 0 },
    workflowStates: { type: Array, default: () => [] },
    customStatusMap: { type: Map, default: () => new Map() },
    systemStatusOverrideMap: { type: Map, default: () => new Map() },
    slotStatuses: {
      type: Array,
      default: () => [
        { id: 1, label: 'Available', status: 'available' },
        { id: 2, label: 'Blocked', status: 'blocked' },
        { id: 3, label: 'Call to book', status: 'call_to_book' }
      ]
    },
    isKanban: { type: Boolean, default: true },
    customStatuses: { type: Array, default: () => [] },
    resourceSlot: { type: Object, default: null },
    firstDayOfWeek: { type: Number, default: 0 },
    showDayOfWeek: { type: Boolean, default: false },
    paymentSettings: { type: Object, default: () => ({}) },
    taxes: { type: Array, default: () => [] },
    fees: { type: Array, default: () => [] },
    countryCode: { type: String, default: 'US' },
    pricingTypeId: { type: Number, default: 1 },
    initialView: { type: String, default: 'detail' },
    initialSection: { type: String, default: null },
    // OTC-482: the form-mode booking request being assigned a slot from the
    // calendar banner. Passed through to the create view, which prefills the
    // customer and sends booking_request_id with the transaction.
    assigningRequest: { type: Object, default: null },
    // For a public slot opened on a specific group: the child slot id whose tab
    // should be active on open. Null shows the overview (default).
    initialChildId: { type: [Number, String], default: null },
    // Cart mode: drawer renders non-modal (transparent overlay,
    // pointer-events: none) so the calendar behind stays clickable for
    // adding more slots to the same in-progress transaction. Defaults to
    // false so the other 4 surfaces that render this drawer don't have
    // to know about cart mode.
    cartMode: { type: Boolean, default: false }
  },

  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const notify = (opts) => instance.proxy.$notify(opts);

    const loading = ref(false);
    const approvingRequest = ref(false);
    const savingStatus = ref(false);
    const savingWorkflowState = ref(false);
    const drawerView = ref('detail');
    const activityLog = ref([]);
    const activityLogLoading = ref(false);

    // Customer communication section — stats lazy-fetched on expand.
    // email_settings comes from drawerData (loaded with the drawer GET) and
    // gates which Quick-send options appear in SendEmailView's picker.
    const commSectionLoading = ref(false);
    const commSectionLoaded = ref(false);
    const commStats = ref({ sent: 0, opened: 0, bounced: 0 });
    const drawerData = ref({
      booking: {},
      notes: [],
      waivers: [],
      game_instances: [],
      subtasks: [],
      custom_fields: [],
      payments: [],
      transaction_items: [],
      pricing: {},
      tickets: [],
      gift_cards: [],
      resources: [],
      staff: [],
      activity_log: [],
      is_public: false,
      children: [],
      aggregated: {},
      users: [],
      statuses: [],
      bookings_project_id: null,
      parent_ticket_id: null,
      attachment_count: 0,
      tags: [],
      customer_tags: []
    });

    // Collapsible section states
    const sections = ref({
      slotDetails: true,
      tags: true,
      customer: true,
      transaction: false,
      notes: false,
      payments: false,
      waivers: false,
      customFields: false,
      customerCommunication: false,
      gameInstances: false,
      staff: false,
      subtasks: false,
      attachments: false,
      tickets: false,
      resources: false,
      slotPricing: false
    });

    const pricingModelExpanded = ref(false);

    // Public booking state
    const activeChildId = ref(null);

    // Attachment state
    const attachments = ref([]);
    const attachmentsLoaded = ref(false);
    const uploadingFiles = ref([]);
    const uploadErrors = ref([]);
    const isDragging = ref(false);
    const deletingAttachmentId = ref(null);
    const lightboxImage = ref(null);

    // Subtask editing state
    const showCreateSubtaskForm = ref(false);
    const newSubtask = ref({ title: '', priority: 3, assignee: null, due_date: null });
    const editingSubtaskId = ref(null);
    const editSubtask = ref({ title: '', priority: null, assignee: null, due_date: null });

    // Staff assignment state
    const showStaffPicker = ref(false);
    const staffSearchQuery = ref(null);
    const staffSaving = ref(false);
    const staffColors = ['#1d8cf8', '#e14eca', '#ff8d72', '#00f2c3', '#f3a218', '#5e72e4'];

    const priorities = [
      { id: 1, name: 'Highest', icon: 'fa fa-angle-double-up', color: '#ec250d' },
      { id: 2, name: 'High', icon: 'fa fa-angle-up', color: '#ec250d' },
      { id: 3, name: 'Medium', icon: 'tim-icons icon-simple-delete', color: '#ff8d72' },
      { id: 4, name: 'Low', icon: 'fa fa-angle-down', color: '#1d8cf8' },
      { id: 5, name: 'Lowest', icon: 'fa fa-angle-double-down', color: '#1d8cf8' },
    ];

    // Scroll lock
    let previousOverflow = '';

    // ============================================
    // Computed
    // ============================================
    // Effective status: prefer drawerData (refreshed after save) over props (stale kanban card)
    const effectiveStatus = computed(() => {
      if (drawerData.value.booking && drawerData.value.booking.status) return drawerData.value.booking.status;
      return props.booking ? props.booking.status : '';
    });

    const isBooked = computed(() => effectiveStatus.value === 'booked');
    const isCompleted = computed(() => effectiveStatus.value === 'completed');
    const isRequested = computed(() => effectiveStatus.value === 'requested');

    // The pending booking request claiming this slot (served by
    // get-booking-drawer-details). Approve/deny act on its id.
    const pendingRequest = computed(() => {
      const r = drawerData.value.booking_request;
      return r && (r.status === 'pending' || r.status === 'denying') ? r : null;
    });

    // Display-only refund total for the deny confirmation: remaining balance
    // across active payments, formatted with the group currency helper. Never
    // recomputed from prices.
    const requestRefundLabel = computed(() => {
      if (!drawerData.value.payments) return '';
      const total = drawerData.value.payments
        .filter(p => p.status === 1)
        .reduce((sum, p) => sum + Math.max(0, parseFloat(p.amount || 0) - parseFloat(p.refunded || 0)), 0);
      return total > 0 ? getAmount(total) : '';
    });

    // Has transaction data to display (booked, completed, or custom status with transaction)
    const hasTransactionData = computed(() => {
      return isBooked.value || isCompleted.value ||
        (drawerData.value.booking && drawerData.value.booking.transaction_id);
    });

    const hasTransaction = computed(() => {
      return drawerData.value.booking && drawerData.value.booking.transaction_id;
    });

    // Public booking slot that's currently booked — can still add another group
    const isPublicBooked = computed(() => {
      const b = drawerData.value.booking;
      const isPublic = (b && b.is_public_booking) || (props.booking && props.booking.isPublicBooking);
      return isBooked.value && isPublic;
    });

    // Show Book now / Add to cart CTA (never on a requested slot — it is
    // claimed by a pending booking request and decided via approve/deny)
    const showBookingCta = computed(() => {
      if (!effectiveStatus.value) return false;
      if (isRequested.value) return false;
      if (isPublicBooked.value) return true;
      return !isBooked.value && !isCompleted.value;
    });

    // Slot pricing display values (reads from resolved pricing object)
    const pricingTypeLabel = computed(() => {
      const p = drawerData.value.pricing;
      if (!p || !p.pricing_type_id) return '';
      return p.pricing_type_id === 2 ? 'Flat rate' : 'Per person';
    });

    const reservationStyleLabel = computed(() => {
      const p = drawerData.value.pricing;
      if (!p) return '';
      return p.is_public_booking ? 'Public' : 'Private';
    });

    const pricingSourceLabel = computed(() => {
      const p = drawerData.value.pricing;
      if (!p) return '';
      return p.is_custom_pricing_enabled ? 'Custom' : 'Default';
    });

    const depositLabel = computed(() => {
      const p = drawerData.value.pricing;
      if (!p || !p.allow_deposit) return 'Disabled';
      const amount = p.deposit_amount || 0;
      const prefix = p.require_deposit ? 'Required' : 'Allowed';
      return `${prefix}, ${getAmount(amount)}`;
    });

    // Multiple durations: the offered lengths (default marked) and, on a booked slot, the one it was sold under
    const durationsLabel = computed(() => {
      const p = drawerData.value.pricing;
      if (!p || !p.enable_multiple_durations || !Array.isArray(p.durations) || p.durations.length === 0) return '';
      return sortDurations(p.durations).map(d => `${d.duration_minutes} min${Number(d.is_default) === 1 ? ' (default)' : ''}`).join(', ');
    });

    // Length a slot row was sold under: its tag, else the default duration
    function durationLabelFor(durationId) {
      const p = drawerData.value.pricing;
      if (!p || !p.enable_multiple_durations || !Array.isArray(p.durations)) return '';
      const d = durationId == null
        ? p.durations.find(x => Number(x.is_default) === 1)
        : p.durations.find(x => Number(x.id) === Number(durationId));
      return d ? `${d.duration_minutes} min` : '';
    }

    // Minutes a slot row actually occupies (its own window). A party booked before
    // durations existed, or whose end staff edited, carries no tag but still has a window.
    function windowMinutes(row, fallbackStart, fallbackDate) {
      if (!row) return null;
      const date = String(row.booking_date || fallbackDate || '').substring(0, 10);
      const start = row.start_time || fallbackStart;
      const endDate = String(row.end_date || date).substring(0, 10);
      if (!date || !start || !row.end_time) return null;
      const a = moment(`${date} ${start}`, 'YYYY-MM-DD HH:mm:ss');
      const b = moment(`${endDate} ${row.end_time}`, 'YYYY-MM-DD HH:mm:ss');
      if (!a.isValid() || !b.isValid()) return null;
      const minutes = b.diff(a, 'minutes');
      return minutes > 0 ? minutes : null;
    }

    function childDurationLabel(child) {
      if (!child || !['booked', 'requested', 'completed'].includes(child.status || '')) return '';
      const p = drawerData.value.pricing;
      if (!p || !p.enable_multiple_durations) return '';
      const master = drawerData.value.booking || {};
      const minutes = windowMinutes(child, master.start_time, master.booking_date);
      if (minutes !== null) return `${minutes} min`;
      return durationLabelFor(child.game_duration_id);
    }

    const bookedDurationLabel = computed(() => {
      const p = drawerData.value.pricing;
      if (!p || !p.enable_multiple_durations || !Array.isArray(p.durations)) return '';
      // Public master: the parties own the durations; the master only spans the longest
      if (isPublicWithChildren.value && activeChildId.value === null) {
        const labels = publicChildren.value.map(c => childDurationLabel(c)).filter(Boolean);
        if (labels.length === 0) return '';
        const distinct = [...new Set(labels)];
        return distinct.length === 1 ? distinct[0] : 'Varies by party';
      }
      if (isPublicWithChildren.value && activeChild.value) return childDurationLabel(activeChild.value);
      const status = (currentBookingData.value && currentBookingData.value.status) || '';
      if (!['booked', 'requested', 'completed'].includes(status)) return '';
      const minutes = windowMinutes(drawerData.value.booking);
      if (minutes !== null) return `${minutes} min`;
      return durationLabelFor(p.selected_duration_id);
    });


    const bookingNotes = computed(() => {
      return drawerData.value.notes || [];
    });

    const sortedNotes = computed(() => {
      return [...bookingNotes.value].sort((a, b) => {
        if (a.is_important && !b.is_important) return -1;
        if (!a.is_important && b.is_important) return 1;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
    });

    const importantNote = computed(() => {
      return bookingNotes.value.find(n => n.is_important);
    });

    async function deleteNoteFromInline(note) {
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${drawerData.value.booking.id}/notes/${note.id}`;
        await axios.delete(url, config);
        notify({ type: 'success', message: 'Note deleted', verticalAlign: 'top', horizontalAlign: 'right' });
        await fetchDrawerData();
      } catch (err) {
        console.error('Failed to delete note:', err);
        notify({ type: 'danger', message: 'Failed to delete note', verticalAlign: 'top', horizontalAlign: 'right' });
      }
    }

    // Booking status label (always shows the booking status, not workflow state)
    const bookingStatusLabel = computed(() => {
      const status = effectiveStatus.value;
      if (!status) return '';

      if (props.customStatusMap.has(status)) {
        return props.customStatusMap.get(status).display_name || status;
      }

      const labels = {
        available: 'Available',
        booked: 'Booked',
        completed: 'Completed',
        blocked: 'Blocked',
        cancelled: 'Cancelled',
        expired: 'Passed',
        call_to_book: 'Call to book',
        requested: 'Requested',
        requestable: 'Requestable'
      };
      return labels[status] || status;
    });

    // Booking status badge style
    const statusBadgeStyle = computed(() => {
      const status = effectiveStatus.value;
      if (!status) return {};

      if (props.systemStatusOverrideMap.has(status)) {
        const override = props.systemStatusOverrideMap.get(status);
        return { background: override.color, color: override.text_color };
      }

      if (props.customStatusMap.has(status)) {
        const custom = props.customStatusMap.get(status);
        return { background: custom.color, color: custom.text_color };
      }

      const colors = {
        available: { background: '#FFFFFF', color: '#171826' },
        booked: { background: '#1D8CF8', color: '#FFFFFF' },
        completed: { background: '#2DCE89', color: '#FFFFFF' },
        blocked: { background: '#D44872', color: '#FFFFFF' },
        cancelled: { background: '#F5365C', color: '#FFFFFF' },
        expired: { background: '#525F7F', color: '#FFFFFF' },
        call_to_book: { background: '#fb6340', color: '#FFFFFF' },
        requested: { background: '#00D6B4', color: '#171826' },
        requestable: { background: '#FFD600', color: '#171826' }
      };
      return colors[status] || { background: '#6B7280', color: '#FFFFFF' };
    });

    // Status is read-only once a slot is booked or completed (matches
    // companies-edit-booking:1590 backend gate and EditSlotView's isStatusLocked).
    const isStatusLocked = computed(() => {
      const status = drawerData.value.booking?.status;
      // 'requested' is claimed by a pending booking request: its only exits
      // are the approve/deny actions (here or on the Requests page).
      return status === 'booked' || status === 'completed' || status === 'requested';
    });

    // Permission to edit slot-attached records (notes, staff, resources,
    // workflow state). Either booking_slot_update OR bookings_update grants
    // access on ANY status — a slot-level admin shouldn't lose these abilities
    // when the slot becomes booked, and a booking-level admin shouldn't be
    // blocked from them on non-booked slots. Booking-specific actions
    // (Reschedule, Edit customer, Edit tickets, Collect) gate on
    // canUpdateBooking directly; resource-slot edits on canUpdateResourceSlot;
    // payments on canCreatePayment.
    const canEditCurrent = computed(() => {
      return props.canEdit || props.canUpdateBooking;
    });

    // Notes have their own breakout permission (booking_notes_update) so
    // view-only roles can be granted note management without slot/booking
    // editing. Anyone who can edit can also manage notes.
    const canManageNotesCurrent = computed(() => {
      return props.canManageNotes || canEditCurrent.value;
    });

    // Selectable statuses for the inline dropdown — built-ins + active custom
    // statuses, plus the current status if it isn't otherwise represented.
    // Mirrors EditSlotView.allStatuses.
    const availableStatuses = computed(() => {
      const merged = (props.slotStatuses || []).map(s => ({
        value: s.status,
        label: s.label
      }));
      for (const c of (props.customStatuses || [])) {
        if (c.is_active) {
          merged.push({ value: c.status_key, label: c.display_name });
        }
      }
      const current = drawerData.value.booking?.status;
      if (current && !merged.some(s => s.value === current)) {
        merged.push({
          value: current,
          label: current.charAt(0).toUpperCase() + current.slice(1).replace(/_/g, ' ')
        });
      }
      return merged;
    });

    // Workflow state object (separate from booking status). Prefer drawerData
    // (refreshed after PUT) over props.booking (stale calendar card) so the
    // badge updates after onWorkflowStateChange.
    const effectiveWorkflowStateId = computed(() => {
      // Kanban only: each group carries its own workflow state, so when a child
      // tab is active the badge/picker reflect that child, not the master. In
      // calendar/list views the drawer always shows the parent slot's state.
      if (props.isKanban && isPublicWithChildren.value && activeChild.value) {
        return activeChild.value.workflow_state_id || null;
      }
      // Once drawerData.booking is loaded it's canonical — even when its
      // workflow_state_id is null (user set to Unassigned). Only fall back
      // to props.booking before the initial fetch resolves.
      const drawerBooking = drawerData.value.booking;
      if (drawerBooking && Object.keys(drawerBooking).length > 0) {
        return drawerBooking.workflow_state_id || null;
      }
      return props.booking ? (props.booking.workflowStateId || null) : null;
    });

    const workflowState = computed(() => {
      const id = effectiveWorkflowStateId.value;
      if (!id || !props.workflowStates.length) return null;
      return props.workflowStates.find(s => s.id === id) || null;
    });

    const currentWorkflowStateId = computed(() => effectiveWorkflowStateId.value);

    async function onWorkflowStateChange(newStateId) {
      if (savingWorkflowState.value) return;
      // Kanban only: target the active group for a public sub-booking. Calendar
      // and list views always target the parent slot.
      const targetChild = (props.isKanban && isPublicWithChildren.value && activeChild.value) ? activeChild.value : null;
      const targetId = targetChild ? targetChild.id : (props.booking ? props.booking.id : null);
      if (!targetId) return;
      savingWorkflowState.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        await axios.put(
          `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${targetId}/workflow-state`,
          { workflow_state_id: newStateId },
          config
        );
        await fetchDrawerData();
        emit('workflow-state-changed', targetId, newStateId);
      } catch (error) {
        console.error('Failed to update workflow state:', error);
        const message = error.response?.data || 'Failed to update workflow state';
        notify({ type: 'danger', message, verticalAlign: 'top', horizontalAlign: 'right' });
      } finally {
        savingWorkflowState.value = false;
      }
    }

    // ── Booking tags ────────────────────────────────────────────────────
    // Applied tags live in drawerData.tags (from the drawer GET). The group
    // catalog loads lazily on first dropdown open. Saves use replace
    // semantics: PUT the full id set; the response returns the fresh set.
    const groupTags = ref([]);
    const groupTagsLoaded = ref(false);
    const groupTagsLoading = ref(false);
    const savingTags = ref(false);
    const tagAddDropdown = ref(null);

    const availableTags = computed(() => {
      const appliedIds = new Set((drawerData.value.tags || []).map(t => t.id));
      return groupTags.value.filter(t => !appliedIds.has(t.id));
    });

    async function loadGroupTags() {
      if (groupTagsLoaded.value || groupTagsLoading.value) return;
      groupTagsLoading.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const response = await axios.get(
          `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/tags`,
          config
        );
        groupTags.value = response.data;
        groupTagsLoaded.value = true;
      } catch (error) {
        console.error('Failed to load tags:', error);
      } finally {
        groupTagsLoading.value = false;
      }
    }

    function onTagDropdownVisible(visible) {
      if (visible) loadGroupTags();
    }

    // Header "+ Add": expand the section and open the add-tag dropdown.
    function openAddTag() {
      sections.value.tags = true;
      loadGroupTags();
      nextTick(() => {
        if (tagAddDropdown.value && tagAddDropdown.value.show) tagAddDropdown.value.show();
      });
    }

    async function saveBookingTags(tagIds) {
      if (savingTags.value || !props.booking || !props.booking.id) return;
      savingTags.value = true;
      const previous = drawerData.value.tags;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const response = await axios.put(
          `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${props.booking.id}/tags`,
          { tag_ids: tagIds },
          config
        );
        drawerData.value.tags = response.data.tags || [];
        emit('booking-changed');
      } catch (error) {
        console.error('Failed to update tags:', error);
        drawerData.value.tags = previous;
        notify({ type: 'danger', message: 'Failed to update tags', verticalAlign: 'top', horizontalAlign: 'right' });
      } finally {
        savingTags.value = false;
      }
    }

    function onTagCommand(command) {
      if (command === 'manage') {
        // Same-precedent as viewCustomer: open in a new tab so the booking
        // context is not lost.
        window.open('/settings/custom-fields?tab=tags', '_blank');
        return;
      }
      const ids = (drawerData.value.tags || []).map(t => t.id);
      saveBookingTags([...ids, command]);
    }

    function removeTag(tag) {
      const ids = (drawerData.value.tags || []).map(t => t.id).filter(id => id !== tag.id);
      saveBookingTags(ids);
    }

    // TransactionSummaryBlock owns its own tip + promo entry state and
    // POSTs to /transactions/{id}/tips and /transactions/{id}/discounts.
    // After a successful POST it emits `updated`; the parent re-fetches and
    // forwards `booking-changed` so the calendar/list refresh.
    async function onTransactionSummaryUpdated() {
      await fetchDrawerData();
      emit('booking-changed');
    }

    // Multi-booking append entry point. Hands the calendar/list parent into
    // append mode and closes the drawer so the user can pick the next slot.
    // The active surface listens to appendModeState and routes slot-click into
    // an append flow instead of opening a fresh drawer.
    function onAddAnotherBooking() {
      const b = currentBookingData.value || {};
      const f = transactionFinancials.value || {};
      const customerName = [b.customer_first_name, b.customer_last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      enterAppendMode({
        transactionId: b.transaction_id,
        orderNumber: b.order_number || null,
        customerName,
        balanceDue: f.due
      });
      emit('close');
    }

    // Inline status change from the drawer's slot details. Hits the same
    // /bookings/{id}/v2 endpoint EditSlotView uses, but with a minimal payload —
    // only status (+ status_type_id for custom statuses) plus the existing
    // booking_date as an anchor (companies-edit-booking:371 starts the SET
    // clause unprefixed and would build invalid SQL if status were the only
    // field).
    async function onStatusChange(newStatus) {
      const b = drawerData.value.booking;
      if (!b || newStatus === b.status || isStatusLocked.value) return;

      const customStatus = (props.customStatuses || []).find(c => c.status_key === newStatus);
      const statusTypeId = customStatus ? customStatus.id : null;

      savingStatus.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${b.id}/v2`;
        await axios.put(url, {
          booking_date: String(b.booking_date).substring(0, 10),
          status: newStatus,
          status_type_id: statusTypeId
        }, config);
        await fetchDrawerData();
        emit('booking-changed');
      } catch (error) {
        console.error('Failed to update status:', error);
        const message = error.response?.data || 'Failed to update status';
        notify({ type: 'danger', message });
      } finally {
        savingStatus.value = false;
      }
    }

    // Slot-details workflow state badge — matches the inline status pattern
    // (solid state color background, luminance-based contrast text). Falls
    // back to a neutral "Unassigned" treatment when no state is set.
    const workflowDetailBadgeStyle = computed(() => {
      const state = workflowState.value;
      if (!state) {
        return { background: 'rgba(255, 255, 255, 0.08)', color: 'rgba(255, 255, 255, 0.7)' };
      }
      const hex = (state.color || '#6B7280').replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
      return {
        background: state.color,
        color: luminance > 0.55 ? '#1E1E2F' : '#FFFFFF'
      };
    });

    // Header top border uses workflow state color if present, otherwise booking status color
    const headerBorderStyle = computed(() => {
      if (!props.booking) return {};
      const state = workflowState.value;
      if (state) {
        return { borderTop: `3px solid ${state.color}` };
      }
      const badgeStyle = statusBadgeStyle.value;
      return {
        borderTop: `3px solid ${badgeStyle.background || 'transparent'}`
      };
    });

    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      return moment(String(dateStr).substring(0, 10), 'YYYY-MM-DD');
    };

    const datePickerFormat = computed(() => toPickerDateFormat(props.dateFormat));

    const formattedBookingDate = computed(() => {
      if (!drawerData.value.booking || !drawerData.value.booking.booking_date) return '';
      return parseDate(drawerData.value.booking.booking_date).format(props.dateFormat);
    });

    const formattedEndDate = computed(() => {
      if (!drawerData.value.booking || !drawerData.value.booking.end_date) return '';
      return parseDate(drawerData.value.booking.end_date).format(props.dateFormat);
    });

    const isMultiDay = computed(() => {
      const b = drawerData.value.booking;
      if (!b || !b.booking_date || !b.end_date) return false;
      return String(b.end_date).substring(0, 10) !== String(b.booking_date).substring(0, 10);
    });

    const formattedStartTime = computed(() => {
      const b = drawerData.value.booking;
      if (b && b.start_time) return getLocalTime(b.start_time);
      return props.booking ? props.booking.startTime : '';
    });

    const formattedEndTime = computed(() => {
      const b = drawerData.value.booking;
      if (b && b.end_time) return getLocalTime(b.end_time);
      return props.booking ? props.booking.endTime : '';
    });

    const completedSubtaskCount = computed(() => {
      return drawerData.value.subtasks.filter(t => t.completed).length;
    });

    const allSubtasksDone = computed(() => {
      return drawerData.value.subtasks.length > 0 && completedSubtaskCount.value === drawerData.value.subtasks.length;
    });

    const attachmentDisplayCount = computed(() => {
      if (attachmentsLoaded.value) return attachments.value.length;
      return drawerData.value.attachment_count || 0;
    });

    const activePayments = computed(() => {
      return drawerData.value.payments.filter(p => p.status === 1);
    });

    // ============================================
    // Public booking computed
    // ============================================
    const isPublicWithChildren = computed(() => {
      return drawerData.value.is_public && drawerData.value.children && drawerData.value.children.length > 0;
    });

    const publicChildren = computed(() => drawerData.value.children || []);

    // Lane attribution: a locked lane stamped with a party's child slot id shows that party.
    function resourceParty(resource) {
      if (!resource || !resource.child_booking_slot_id) return null;
      const child = publicChildren.value.find(c => Number(c.id) === Number(resource.child_booking_slot_id));
      if (!child) return null;
      return `${child.customer_first_name || ''} ${child.customer_last_name || ''}`.trim() || 'Group';
    }

    const publicAggregated = computed(() => drawerData.value.aggregated || {});

    const activeChild = computed(() => {
      if (!activeChildId.value || !publicChildren.value.length) return null;
      return publicChildren.value.find(c => c.id === activeChildId.value) || null;
    });

    // On a group tab, show that group's lanes plus any unattributed lane; the overview shows all.
    const visibleResources = computed(() => {
      const all = drawerData.value.resources || [];
      if (!activeChild.value) return all;
      return all.filter(r => !r.child_booking_slot_id || Number(r.child_booking_slot_id) === Number(activeChild.value.id));
    });

    // Customer tag chips follow the shown customer: the group's own customer
    // on a public booking's child tab, the master's customer otherwise (the
    // master-level field is always empty on public bookings, by design).
    const displayedCustomerTags = computed(() => {
      if (activeChild.value) return activeChild.value.customer_tags || [];
      return drawerData.value.customer_tags || [];
    });

    // Data source for per-child sections: child data when selected, otherwise master booking data.
    // Public booking children inherit game_name/game_image from the master (one game, many groups);
    // the lambda doesn't redundantly attach those fields per child, so we fall back here so the
    // receipt line item, header, etc. render correctly when activeChild is selected.
    const currentBookingData = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) {
        const master = drawerData.value.booking || {};
        return {
          ...activeChild.value,
          game_name: activeChild.value.game_name || master.game_name,
          game_image: activeChild.value.game_image || master.game_image
        };
      }
      return drawerData.value.booking || {};
    });

    // Booking object handed to CreateBookingView. Same stale-prop guard the
    // effectiveWorkflowStateId computed uses above: the calendar card in
    // props.booking is a snapshot from click time and doesn't pick up
    // in-drawer edits (EditSlotView's slot moves, game changes, etc.). Without
    // this overlay, staff who moved a slot then hit "Create booking" from the
    // same drawer would POST the pre-move time and the lambda would write
    // that back to the booking_slots row, silently reverting the move.
    // CreateBookingView's computeds rely on the wrapper shape
    // (gameId / gameName / startTimeRaw), so we keep the prop's shape and
    // only overlay the fields EditSlotView is allowed to change.
    const bookingForCreate = computed(() => {
      const fresh = drawerData.value.booking;
      if (!fresh || !props.booking || Object.keys(fresh).length === 0) {
        return props.booking;
      }
      return {
        ...props.booking,
        booking_date: fresh.booking_date,
        end_date: fresh.end_date,
        start_time: fresh.start_time,
        end_time: fresh.end_time,
        startTimeRaw: fresh.start_time,
        endTimeRaw: fresh.end_time,
        is_all_day: fresh.is_all_day,
        game_id: fresh.game_id,
        gameId: fresh.game_id,
        game_name: fresh.game_name,
        gameName: fresh.game_name,
        originalData: {
          ...(props.booking.originalData || {}),
          booking_date: fresh.booking_date,
          end_date: fresh.end_date,
          start_time: fresh.start_time,
          end_time: fresh.end_time,
          is_all_day: fresh.is_all_day,
          game_id: fresh.game_id,
          game_name: fresh.game_name
        }
      };
    });

    // Whether to show per-child sections (tickets, customer, transaction, payments, waivers, custom fields)
    // Hidden when public booking overview is showing (activeChildId is null)
    const showPerChildSections = computed(() => {
      if (isPublicWithChildren.value && activeChildId.value === null) return false;
      return hasTransactionData.value || (isPublicWithChildren.value && activeChild.value);
    });

    // True when the slot already has at least one group booked. Used to gate
    // the upgrade-to-private toggle in CreateBookingView — once any group has
    // booked the slot it can't be flipped to a private (entire-slot) reservation.
    const slotHasExistingBookings = computed(() => {
      const children = drawerData.value.children || [];
      return children.length > 0;
    });

    // True when this booking is a private (upgraded) reservation.
    // Mirrors legacy Bookings.vue:10115 / Calendar.vue:11985–11987:
    //   selectedTransaction = item.public_slots[0] if has children, else item
    //   then read selectedTransaction.is_reservation_upgraded
    // For a non-public slot or a public master with no children, we read from
    // the booking itself. For a public master with children, we read from the
    // first child (which is the actual booking record — the master never gets
    // is_reservation_upgraded set; the upgrade lives on the child).
    const isPrivateReservation = computed(() => {
      const children = drawerData.value.children || [];
      const source = children.length > 0 ? children[0] : drawerData.value.booking;
      return !!(source && source.is_reservation_upgraded);
    });

    // Remaining-seats hold (OTC-1459). Staff close the leftover capacity on a
    // public slot to further customer bookings without making a dummy booking —
    // used to hold the slot for an accommodation group they add manually. The
    // flag lives on the master row; the customer availability query and the
    // make-booking guard both honor it, while the staff add-booking path does not.
    const savingRemainingBlocked = ref(false);
    const remainingBlocked = computed(() =>
      !!(drawerData.value.booking && Number(drawerData.value.booking.remaining_blocked) === 1)
    );

    async function toggleRemainingBlocked(next) {
      const b = drawerData.value.booking;
      if (!b || savingRemainingBlocked.value) return;
      savingRemainingBlocked.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${b.id}/v2`;
        await axios.put(url, { remaining_blocked: next ? 1 : 0 }, config);
        notify({
          type: 'success',
          message: next ? 'Remaining seats blocked' : 'Remaining seats reopened',
          verticalAlign: 'top',
          horizontalAlign: 'right',
          timeout: 2000
        });
        await fetchDrawerData();
        emit('booking-changed');
      } catch (error) {
        console.error('Failed to update remaining seats hold:', error);
        const message = error.response?.data || 'Failed to update remaining seats';
        notify({ type: 'danger', message, verticalAlign: 'top', horizontalAlign: 'right' });
      } finally {
        savingRemainingBlocked.value = false;
      }
    }

    // Incomplete booking — slot is in 'booked' state but has no transaction
    // (typically a payment processing error left a stale state). Predicate
    // mirrors Bookings.vue:1695. The reset flow uses the same DELETE endpoint
    // as cancel, with the user-chosen status as the new slot status.
    const isIncompleteBooking = computed(() => {
      const b = drawerData.value.booking;
      if (!b) return false;
      const noChildren = !drawerData.value.children || drawerData.value.children.length === 0;
      return b.status === 'booked' && !b.transaction_id && noChildren;
    });

    const incompleteResetStatus = ref(null);
    const resettingIncomplete = ref(false);

    async function resetIncompleteSlot() {
      const b = drawerData.value.booking;
      if (!b || !incompleteResetStatus.value || resettingIncomplete.value) return;
      const newStatus = incompleteResetStatus.value.status;
      resettingIncomplete.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${b.id}?new_status=${newStatus}&delete_waivers=false`;
        await axios.delete(url, config);
        notify({
          type: 'success',
          message: 'Slot reset',
          verticalAlign: 'top',
          horizontalAlign: 'right',
          timeout: 2000
        });
        emit('booking-changed');
        emit('close');
      } catch (error) {
        console.error('Failed to reset slot:', error);
        const message = error.response?.data || 'Failed to reset slot';
        notify({ type: 'danger', message, verticalAlign: 'top', horizontalAlign: 'right' });
      } finally {
        resettingIncomplete.value = false;
      }
    }

    // Header game name/image — prefer drawerData (refreshed after reschedule)
    // and fall back to the prop while the initial fetch is in flight or if the
    // lambda didn't return a game record. The prop comes from the kanban/calendar
    // card and is frozen at open time, so it's stale after a reschedule.
    const headerGameName = computed(() => {
      const fresh = drawerData.value.booking && drawerData.value.booking.game_name;
      return fresh || (props.booking && props.booking.gameName) || '';
    });

    const headerGameImage = computed(() => {
      const fresh = drawerData.value.booking && drawerData.value.booking.game_image;
      return fresh || (props.booking && props.booking.gameImage) || '';
    });

    // Minimum booking notice indicator in the slot header (OTC-384). Reads the
    // live drawer record first (all notice fields come back from
    // get-booking-drawer-details), falling back to the parent card's raw row
    // during the initial-load window. Ticks so a countdown stays current while
    // the drawer sits open.
    const noticeNow = ref(0);
    const noticeTickInterval = setInterval(() => {
      noticeNow.value = Date.now();
    }, NOTICE_TICK_MS);
    onBeforeUnmount(() => clearInterval(noticeTickInterval));

    const headerSlotNotice = computed(() => {
      const policy = props.noticePolicy;
      if (!policy || !policy.show) return null;
      void noticeNow.value;
      const raw = drawerData.value.booking || (props.booking && props.booking.originalData) || null;
      if (!raw) return null;
      return classifySlotNotice(raw, policy.anchor || null, policy.mode || 'indicator');
    });

    // Compact meta line under the game name: date · time · participants · customer.
    // Reads from drawerData first so it live-updates after reschedule/edit; falls
    // back to the parent-supplied card (startTimeRaw / bookingDate / customerName /
    // groupSize) during the initial-load window so the meta line never blanks out
    // between drawer-open and fetch-complete. All formatting honors the group's
    // date_format / time_format via props.dateFormat and getLocalTime.
    const headerMetaParts = computed(() => {
      const parts = [];
      const live = drawerData.value.booking || {};
      const card = props.booking || {};

      // Date — group-format via props.dateFormat
      const rawStart = live.booking_date || card.bookingDate;
      const rawEnd = live.end_date || card.endDate;
      if (rawStart) {
        const startStr = moment(String(rawStart).substring(0, 10), 'YYYY-MM-DD').format(props.dateFormat);
        const isMulti = rawEnd && String(rawEnd).substring(0, 10) !== String(rawStart).substring(0, 10);
        if (isMulti) {
          const endStr = moment(String(rawEnd).substring(0, 10), 'YYYY-MM-DD').format(props.dateFormat);
          parts.push(`${startStr} → ${endStr}`);
        } else {
          parts.push(startStr);
        }
      }

      // Time — group-format via getLocalTime (reads props.timeFormat)
      // On a party tab the window is the party's own (the master spans the longest party)
      const party = isPublicWithChildren.value && activeChild.value ? activeChild.value : null;
      const rawStartTime = live.start_time || card.startTimeRaw;
      const rawEndTime = (party && party.end_time) || live.end_time || card.endTimeRaw;
      if (rawStartTime) {
        const startTimeStr = getLocalTime(rawStartTime);
        const endTimeStr = rawEndTime ? getLocalTime(rawEndTime) : '';
        if (startTimeStr && endTimeStr) parts.push(`${startTimeStr} – ${endTimeStr}`);
        else if (startTimeStr) parts.push(startTimeStr);
      }
      const partyLength = party ? childDurationLabel(party) : '';
      if (partyLength) parts.push(partyLength);

      const onPublicOverview = isPublicWithChildren.value && activeChildId.value === null;

      // Customer name — skip on public master overview (no single customer).
      // Live record holds first/last separately; the parent card holds a combined
      // string used as a load-window fallback only. The card uses the strings
      // "Blocked" / "Available" as placeholders for slots without a customer
      // (Bookings.vue:6320) — those aren't real names and must not render here.
      if (!onPublicOverview) {
        const b = currentBookingData.value || {};
        const fn = (b.customer_first_name || '').trim();
        const ln = (b.customer_last_name || '').trim();
        let fullName = [fn, ln].filter(Boolean).join(' ');
        if (!fullName && card.customerName) {
          const cardName = String(card.customerName).trim();
          if (cardName !== 'Blocked' && cardName !== 'Available') fullName = cardName;
        }
        if (fullName) parts.push(fullName);
      }

      // Participants — public master overview shows aggregated total; otherwise
      // the active record's group_size, falling back to the card during load.
      // Check-in count uses the same predicate as the list view
      // (Bookings.vue:getWaiverCount → not_participating === 0).
      let participantCount = 0;
      let checkedInCount = 0;
      if (onPublicOverview) {
        participantCount = Number(publicAggregated.value.total_guests) || 0;
        for (const child of (publicChildren.value || [])) {
          for (const w of (child.waivers || [])) {
            if (Number(w.not_participating) === 0) checkedInCount++;
          }
        }
      } else {
        const b = currentBookingData.value || {};
        participantCount = Number(b.group_size) || Number(card.groupSize) || 0;
        for (const w of (currentWaivers.value || [])) {
          if (Number(w.not_participating) === 0) checkedInCount++;
        }
      }
      if (participantCount > 0) {
        const label = `${participantCount} ${participantCount === 1 ? 'participant' : 'participants'}`;
        // Show check-in count whenever this is a booked slot, matching list view
        // ("X booked / Y checked in"). Skip on unbooked/available slots.
        const isBookedLike = hasTransactionData.value || onPublicOverview;
        parts.push(isBookedLike ? `${label} / ${checkedInCount} checked in` : label);
      }

      return parts;
    });

    // Cart slot count for the header badge. Reads from the shared
    // multi-select observable so it stays in sync with the toolbar button.
    const cartCount = computed(() => (multiSelectState.cartSlots || []).length);

    const currentTickets = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.tickets || [];
      return drawerData.value.tickets || [];
    });

    const currentWaivers = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.waivers || [];
      return drawerData.value.waivers || [];
    });

    // Waiver PDF download — routes through the shared, normalized WaiverPdfDocument
    // (white bg, dark text, fixed font, rendered signature) just like the customer
    // details, transaction details, and waivers-list surfaces. Per-row spinner via
    // pdfGeneratingId so only the clicked row shows progress.
    const pdfGeneratingId = ref(null);
    const waiverPdf = ref(null);
    async function downloadWaiver(waiver) {
      if (pdfGeneratingId.value !== null) return;
      pdfGeneratingId.value = waiver.id;
      try {
        const { waiver: detail, custom_field_responses, consent_fields } =
          await fetchWaiverDetail(waiver.id, {
            includeSignature: true,
            companyId: props.companyId,
            groupId: props.companyGroupId,
          });
        await waiverPdf.value.generate({
          waiver: { ...waiver, ...detail },
          consent_fields,
          custom_field_responses,
        });
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          swal({ title: 'Error', html: 'You do not have access to this feature.', type: 'error' });
        } else {
          swal('Error', 'Could not download waiver PDF', 'error');
        }
      } finally {
        pdfGeneratingId.value = null;
      }
    }

    const currentActivePayments = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) {
        return (activeChild.value.payments || []).filter(p => p.status === 1);
      }
      return activePayments.value;
    });

    const currentCustomFields = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.custom_fields || [];
      return drawerData.value.custom_fields || [];
    });

    // Custom fields flagged with add_alert that have a response — surfaced as a
    // peek above the collapsed Custom fields section so they're never buried.
    const alertedCustomFields = computed(() => {
      return currentCustomFields.value.filter(f => {
        if (!f.add_alert) return false;
        return f.response || f.response === 0 || f.response === '0';
      });
    });

    const currentLineItems = computed(() => {
      // Receipt template renders the booking slot separately, so only
      // return add-on items here. For public sub-bookings, the items live
      // on the active child's transaction (master.transaction_id is null);
      // for non-public bookings they're at the top level.
      if (isPublicWithChildren.value && activeChild.value) {
        return activeChild.value.transaction_items || [];
      }
      return drawerData.value.transaction_items || [];
    });

    // Transaction-level financials (from the transaction record, not the booking slot)
    const transactionFinancials = computed(() => {
      const b = currentBookingData.value || {};
      return {
        price: b.transaction_price != null ? b.transaction_price : (b.price || 0),
        total: b.transaction_total != null ? b.transaction_total : (b.total || 0),
        tax: b.transaction_tax != null ? b.transaction_tax : (b.tax || 0),
        fee: b.transaction_fee != null ? b.transaction_fee : (b.fee || 0),
        discount: b.transaction_discount != null ? b.transaction_discount : (b.discount || 0),
        tips: b.transaction_tips != null ? b.transaction_tips : (b.tips || 0),
        paid: b.paid || 0,
        due: b.due || 0,
        refunded: b.refunded || 0,
        waive_taxes: Number(b.waive_taxes || 0),
        waive_fees: Number(b.waive_fees || 0),
        inclusive_tax_waived: Number(b.inclusive_tax_waived || 0)
      };
    });

    // Per-link tax and fee breakdowns. For public bookings the active child
    // has its own arrays; otherwise we fall back to the top-level master arrays.
    const transactionTaxLines = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.tax_lines || [];
      return drawerData.value.tax_lines || [];
    });
    const transactionFeeLines = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.fee_lines || [];
      return drawerData.value.fee_lines || [];
    });
    const transactionDiscountLines = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.discount_lines || [];
      return drawerData.value.discount_lines || [];
    });
    const transactionTipLines = computed(() => {
      if (isPublicWithChildren.value && activeChild.value) return activeChild.value.tip_lines || [];
      return drawerData.value.tip_lines || [];
    });

    const currentGiftCards = computed(() => {
      // Same per-child sourcing as currentLineItems — gift cards bought on
      // a public sub-booking live on the child's transaction.
      if (isPublicWithChildren.value && activeChild.value) {
        return activeChild.value.gift_cards || [];
      }
      return drawerData.value.gift_cards || [];
    });

    // Other bookings on the same transaction (multi-booking carts). When a
    // public child is selected via the sub-booking tab, each child has its
    // OWN transaction_id, so siblings come from the child's own
    // sibling_bookings array (populated per-child by the lambda) rather than
    // the top-level master's transaction siblings.
    //
    // Each row is enriched with formatted display_date / display_time so the
    // TransactionSummaryBlock can render a date · time meta line under the
    // game name without duplicating the parent's group-settings logic.
    const siblingBookings = computed(() => {
      const rows = (isPublicWithChildren.value && activeChild.value)
        ? (activeChild.value.sibling_bookings || [])
        : (drawerData.value.sibling_bookings || []);
      return rows.map(sib => ({
        ...sib,
        display_date: sib.booking_date ? parseDate(sib.booking_date).format(props.dateFormat) : '',
        display_time: sib.start_time ? getLocalTime(sib.start_time) : ''
      }));
    });

    const totalTicketPlayers = computed(() => {
      return currentTickets.value.reduce((sum, t) => sum + (t.number_of_players || 0), 0);
    });

    const totalInstanceBreaks = computed(() => {
      return drawerData.value.game_instances.reduce((sum, i) => sum + (i.number_of_breaks || 0), 0);
    });

    const totalTicketPrice = computed(() => {
      return currentTickets.value.reduce((sum, t) => sum + parseFloat(t.price || 0), 0);
    });

    // Pricing mode snapshotted on the booking. Source from currentBookingData so
    // it tracks the SELECTED child (matches currentTickets/totalTicketPrice),
    // not always children[0].
    const upgradePricingMode = computed(() => {
      const source = currentBookingData.value || {};
      return source.reservation_upgrade_pricing_mode || 'buyout';
    });

    // Nominal private-booking fee for a tickets+fee upgrade, derived as the
    // booking subtotal minus the real per-ticket prices (booking_slots.price
    // already folds the fee into the subtotal). 0 for buyout / non-upgraded.
    // Uses currentBookingData (the resolved child) so price and ticket sum are
    // for the same slot.
    const privateBookingFee = computed(() => {
      if (!isPrivateReservation.value || upgradePricingMode.value !== 'tickets_plus_fee') return 0;
      const source = currentBookingData.value || {};
      const slotPrice = parseFloat(source.price) || 0;
      return roundCents(slotPrice - totalTicketPrice.value);
    });

    // Tickets-card total: per-ticket prices + the nominal fee. Rounded through
    // roundCents so the displayed sum can't show float residue. Equals the
    // booking subtotal in tickets+fee; equals the ticket sum otherwise.
    const ticketsCardTotal = computed(() => roundCents(totalTicketPrice.value + privateBookingFee.value));

    // Buyout upgrade: a flat fee buys the whole slot, so the per-ticket prices
    // are a meaningless distribution artifact. Collapse them — show counts only
    // and a single "Private buyout" line at the booking's authoritative price.
    const isBuyoutUpgrade = computed(() =>
      isPrivateReservation.value && upgradePricingMode.value === 'buyout');
    const buyoutAmount = computed(() => {
      if (!isBuyoutUpgrade.value) return 0;
      const source = currentBookingData.value || {};
      return roundCents(parseFloat(source.price) || 0);
    });

    // ============================================
    // Methods
    // ============================================
    async function getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    }

    // Currency symbol per group settings (e.g. $ for USD, £ for GBP).
    // Falls back to the raw currencySymbol prop if the code isn't in
    // currencies.json — kept consistent with getAmount() below.
    const resolvedCurrencySymbol = computed(() => {
      const currencyInfo = _.find(currencies.currencies, { code: props.currency });
      return currencyInfo ? currencyInfo.symbol : props.currencySymbol;
    });

    function getAmount(value) {
      return formatCurrency(value, {
        currency: props.currency,
        currencySymbol: props.currencySymbol,
        currencyFormat: props.currencyFormat,
        numberFormat: props.numberFormat
      });
    }

    function getLocalDate(dateStr) {
      return formatGroupDate(dateStr, { dayOfWeek: false });
    }

    function getLocalTime(timeStr) {
      if (!timeStr) return '';
      // Time-of-day strings (booking_slots.start_time / end_time) are wall-clock —
      // no T or space separator; render as-is (no tz conversion).
      if (!timeStr.includes('T') && !timeStr.includes(' ')) {
        const fmt = props.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';
        return moment(timeStr, 'HH:mm:ss').format(fmt);
      }
      // Full timestamps (booking_made_at, payment.timestamp, etc.) → group tz.
      return formatGroupTime(timeStr);
    }

    function formatTimeRaw(timeStr) {
      if (!timeStr) return '';
      const timePart = timeStr.includes('T') ? timeStr.split('T')[1] : timeStr.includes(' ') ? timeStr.split(' ')[1] : timeStr;
      const fmt = props.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';
      return moment(timePart.substring(0, 8), 'HH:mm:ss').format(fmt);
    }

    function getInstanceDuration(instance) {
      if (!instance.completed) return 'Not completed';
      if (!instance.start_time || !instance.end_time) return 'Not completed';
      const start = moment(instance.start_time);
      const end = moment(instance.end_time);
      const mins = Math.round(end.diff(start, 'minutes', true));
      return 'Total time: ' + mins + ' minutes';
    }

    function formatFieldResponse(field) {
      if (!field.response && field.response !== 0) return 'Not provided';
      if (field.type === 1) return field.response === '1' || field.response === 1 ? 'Yes' : 'No';
      // Dropdown (type 3) with follow-up is stored as JSON: {option, follow_up}
      if (field.type === 3 && typeof field.response === 'string' && field.response.startsWith('{')) {
        try {
          const parsed = JSON.parse(field.response);
          if (parsed && parsed.option) {
            return parsed.follow_up ? `${parsed.option} (${parsed.follow_up})` : parsed.option;
          }
        } catch (e) {
          // fall through to raw response
        }
      }
      return field.response;
    }

    function toggleSection(section) {
      sections.value[section] = !sections.value[section];
    }

    // --- Attachment methods ---

    function toggleAttachments() {
      sections.value.attachments = !sections.value.attachments;
      if (sections.value.attachments && !attachmentsLoaded.value) {
        loadAttachments();
      }
    }

    async function loadAttachments() {
      if (!drawerData.value.parent_ticket_id || !drawerData.value.bookings_project_id) return;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/projects/${drawerData.value.bookings_project_id}/tickets/${drawerData.value.parent_ticket_id}/attachments`;
        const response = await axios.get(url, config);
        attachments.value = response.data;
        attachmentsLoaded.value = true;
      } catch (err) {
        console.log(err);
      }
    }

    function handleAttachmentDrop(e) {
      isDragging.value = false;
      const files = e.dataTransfer.files;
      if (files.length) uploadAttachmentFiles(files);
    }

    function handleAttachmentFileSelect(e) {
      const files = e.target.files;
      if (files.length) uploadAttachmentFiles(files);
      e.target.value = '';
    }

    const ALLOWED_ATTACHMENT_TYPES = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/csv', 'text/plain',
      'application/zip',
    ];

    function isHeicFile(file) {
      if (file.type === 'image/heic' || file.type === 'image/heif') return true;
      const name = (file.name || '').toLowerCase();
      return name.endsWith('.heic') || name.endsWith('.heif');
    }

    async function putToS3WithRetry(uploadUrl, file, entry) {
      const buildConfig = () => ({
        headers: { 'Content-Type': file.type },
        onUploadProgress: (e) => {
          if (e.total) entry.progress = Math.round((e.loaded / e.total) * 100);
        }
      });
      try {
        return await axios.put(uploadUrl, file, buildConfig());
      } catch (err) {
        // Retry once on network/connection errors only (no HTTP response received)
        if (err.response) throw err;
        entry.progress = 0;
        entry.retrying = true;
        return await axios.put(uploadUrl, file, buildConfig());
      }
    }

    async function uploadAttachmentFiles(files) {
      const MAX_SIZE = 25 * 1024 * 1024;
      for (const file of files) {
        if (isHeicFile(file)) {
          uploadErrors.value.push(`${file.name} — HEIC photos aren't supported. On iPhone, go to Settings → Camera → Formats and select "Most Compatible", or save the photo as JPEG before uploading.`);
          continue;
        }
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
          uploadErrors.value.push(`${file.name} — file type not supported`);
          continue;
        }
        if (file.size > MAX_SIZE) {
          uploadErrors.value.push(`${file.name} — exceeds 25 MB limit`);
          continue;
        }

        const entry = { name: file.name, progress: null, retrying: false };
        uploadingFiles.value.push(entry);

        try {
          const token = await getAuthToken();
          const config = { headers: { Authorization: token } };

          const projectId = drawerData.value.bookings_project_id || 0;
          const ticketId = drawerData.value.parent_ticket_id || 0;
          const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/projects/${projectId}/tickets/${ticketId}/attachments`;

          const body = {
            file_name: file.name,
            content_type: file.type,
            file_size: file.size
          };
          // When no parent ticket exists, pass booking_slot_id so the lambda auto-creates it
          if (!drawerData.value.parent_ticket_id) {
            body.booking_slot_id = props.booking.id;
          }

          const uploadRes = await axios.post(url, body, config);
          const { upload_url, project_id, ticket_id } = uploadRes.data;

          // Update drawer data if project/ticket were auto-created
          if (project_id) drawerData.value.bookings_project_id = project_id;
          if (ticket_id) drawerData.value.parent_ticket_id = ticket_id;

          await putToS3WithRetry(upload_url, file, entry);

          attachmentsLoaded.value = false;
          await loadAttachments();
          notify({ type: 'success', message: 'File uploaded', timeout: 2000 });
        } catch (err) {
          console.log(err);
          uploadErrors.value.push(`${file.name} — upload failed`);
        } finally {
          const idx = uploadingFiles.value.indexOf(entry);
          if (idx > -1) uploadingFiles.value.splice(idx, 1);
        }
      }
    }

    async function downloadAttachment(att) {
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/projects/${drawerData.value.bookings_project_id}/tickets/${drawerData.value.parent_ticket_id}/attachments/${att.id}`;
        const response = await axios.get(url, config);
        const a = document.createElement('a');
        a.href = response.data.download_url;
        a.download = att.file_name;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        console.log(err);
      }
    }

    async function previewAttachment(att) {
      if (isImage(att.content_type)) {
        try {
          const token = await getAuthToken();
          const config = { headers: { Authorization: token } };
          const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/projects/${drawerData.value.bookings_project_id}/tickets/${drawerData.value.parent_ticket_id}/attachments/${att.id}`;
          const response = await axios.get(url, config);
          lightboxImage.value = response.data.download_url;
        } catch (err) {
          console.log(err);
        }
      } else if (att.content_type === 'application/pdf' || att.content_type === 'text/plain') {
        downloadAttachment(att);
      }
    }

    async function confirmDeleteAttachment(att) {
      const result = await swal({
        title: 'Delete attachment?',
        html: `This will remove <strong>${att.file_name}</strong>.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      });
      if (!result.value) return;
      deleteAttachment(att);
    }

    async function deleteAttachment(att) {
      deletingAttachmentId.value = att.id;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/projects/${drawerData.value.bookings_project_id}/tickets/${drawerData.value.parent_ticket_id}/attachments/${att.id}`;
        await axios.delete(url, config);
        attachments.value = attachments.value.filter(a => a.id !== att.id);
        notify({ type: 'success', message: 'Attachment deleted', timeout: 2000 });
      } catch (err) {
        console.log(err);
      } finally {
        deletingAttachmentId.value = null;
      }
    }

    function isImage(contentType) {
      return contentType && contentType.startsWith('image/');
    }

    function isSpreadsheet(contentType) {
      return contentType && (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv');
    }

    function isDocument(contentType) {
      return contentType && (contentType.includes('word') || contentType.includes('presentation') || contentType.includes('powerpoint'));
    }

    function formatFileSize(bytes) {
      if (!bytes) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    function viewCustomer() {
      const data = currentBookingData.value;
      if (data && data.end_user_id) {
        window.open(`/customers/details?id=${data.end_user_id}`, '_blank');
      }
    }

    function viewTransaction() {
      const data = currentBookingData.value;
      if (data && data.transaction_id) {
        window.open(`/purchases/transactions/details?id=${data.transaction_id}`, '_blank');
      }
    }

    function handleAction(command) {
      switch (command) {
        case 'edit-slot':
          drawerView.value = 'edit-slot';
          break;
        case 'notes':
          drawerView.value = 'notes';
          break;
        case 'activity-log':
          fetchActivityLog();
          drawerView.value = 'activity-log';
          break;
        case 'cancel':
          // Sub-booking cancel: skip the CancelView "freed slot status"
          // picker — the master slot's status doesn't change when one
          // group leaves. Just confirm and DELETE the child directly.
          if (activeChild.value) {
            confirmCancelSubBooking();
          } else {
            drawerView.value = 'cancel';
          }
          break;
        default:
          emit(command, drawerData.value.booking);
      }
    }

    async function fetchActivityLog() {
      if (!props.booking || !props.booking.id) return;
      activityLogLoading.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${props.booking.id}/log`;
        const response = await axios.get(url, config);
        activityLog.value = response.data.activity_log || response.data || [];
      } catch (error) {
        console.error('Failed to fetch activity log:', error);
        activityLog.value = [];
      } finally {
        activityLogLoading.value = false;
      }
    }

    function openTransactionPage() {
      if (drawerData.value.booking && drawerData.value.booking.transaction_id) {
        window.open(`/purchases/transactions/details?id=${drawerData.value.booking.transaction_id}`, '_blank');
      }
    }

    // ============================================
    // Booking request approve / deny
    // ============================================
    async function approveRequest() {
      if (!pendingRequest.value) return;
      const result = await swal({
        title: 'Approve this booking request?',
        text: 'The slot becomes a confirmed booking and the customer receives a confirmation email.',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Approve',
        cancelButtonText: 'Go back',
      });
      if (!result.value) return;

      approvingRequest.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/booking-requests/${pendingRequest.value.id}/approve`;
        const response = await axios.post(url, {}, config);

        // Balance due (charge-after-approval requests, or deposit requests):
        // chain the existing payment request email so its expiry clock starts.
        if (parseFloat(response.data.due) > 0 && response.data.transaction_id) {
          try {
            const prUrl = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${response.data.transaction_id}/payments/request`;
            await axios.post(prUrl, {
              email: response.data.customer_email,
              bookings: drawerData.value.booking ? [drawerData.value.booking] : [],
            }, config);
          } catch (prError) {
            notify({ type: 'warning', message: 'Request approved. Payment request could not be sent, send it from the transaction page.', timeout: 6000 });
            onRequestDecided('approved', true);
            return;
          }
        }
        notify({ type: 'success', message: 'Booking request approved' });
        onRequestDecided('approved', true);
      } catch (error) {
        const message = error?.response?.data?.error || 'The request could not be approved. Please try again.';
        notify({ type: 'danger', message });
      } finally {
        approvingRequest.value = false;
      }
    }

    function onRequestDecided(outcome, notified) {
      if (outcome === 'denied' && !notified) {
        notify({ type: 'success', message: 'Booking request denied' });
      }
      emit('booking-changed');
      drawerView.value = 'detail';
      fetchDrawerData();
    }

    function onRequestActionError(message) {
      notify({ type: 'danger', message });
      drawerView.value = 'detail';
      fetchDrawerData();
    }

    function onBookingCancelled() {
      emit('booking-changed');
      // Cancelling a public sub-booking should leave the master drawer open
      // so the user can manage the remaining groups; reset to overview.
      // Cancelling the slot itself (no active child) closes the drawer.
      if (isPublicWithChildren.value) {
        activeChildId.value = null;
        drawerView.value = 'detail';
        fetchDrawerData();
        return;
      }
      emit('close');
    }

    async function confirmCancelSubBooking() {
      const child = activeChild.value;
      if (!child || !child.id) return;
      const customerName = (child.customer_first_name || child.customer_last_name)
        ? `${child.customer_first_name || ''} ${child.customer_last_name || ''}`.trim()
        : 'this group';
      const result = await swal({
        title: 'Cancel sub-booking?',
        html: `<small>${customerName}'s booking will be cancelled. The slot stays open for the remaining groups.</small>`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Cancel sub-booking',
        cancelButtonText: 'Keep'
      });
      if (!result.value) return;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        // Sub-booking cancel: hit the MASTER's path with the child's id in
        // the `booking_id` query param. That's the sentinel the lambda uses
        // (booking-delete-by-id:325) to enter the sub-booking branch
        // instead of cancelling the entire master slot.
        const masterId = drawerData.value.booking?.id;
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${masterId}?booking_id=${child.id}&new_status=available&delete_waivers=false`;
        await axios.delete(url, config);
        notify({ type: 'success', message: 'Sub-booking has been cancelled', verticalAlign: 'top', horizontalAlign: 'right' });
        onBookingCancelled();
      } catch (error) {
        console.error('Failed to cancel sub-booking:', error);
        notify({ type: 'danger', message: 'Failed to cancel sub-booking', verticalAlign: 'top', horizontalAlign: 'right' });
      }
    }

    async function toggleResourceLock(resource) {
      const currentLockState = resource.is_locked || 0;
      const newLockState = currentLockState ? 0 : 1;

      // Optimistically update UI
      resource.is_locked = newLockState;

      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/staff/${resource.resource_slot_id}/assign`;
        await axios.put(url, {
          booking_slot_id: props.booking.id
        }, config);
      } catch (error) {
        console.error('Failed to toggle lock:', error);
        // Revert on error
        resource.is_locked = currentLockState;
      }
    }

    // Resource assignment add/remove/edit
    // ============================================
    const showAddResource = ref(false);
    const addResourceSearchValue = ref('');
    const reassignContext = ref(null);
    const reassignShowAll = ref(false);

    function openAddResource() {
      addResourceSearchValue.value = '';
      reassignContext.value = null;
      reassignShowAll.value = false;
      showAddResource.value = true;
    }

    function openReassign(resource) {
      addResourceSearchValue.value = '';
      reassignShowAll.value = false;
      reassignContext.value = {
        resource_slot_id: resource.resource_slot_id,
        name: resource.resource_label || 'Unnamed',
        resource_category_id: resource.resource_category_id || null,
        category_name: resource.category_name || null,
        category_color: resource.category_color || null
      };
      showAddResource.value = true;
    }

    function closeAddResource() {
      showAddResource.value = false;
      addResourceSearchValue.value = '';
      reassignContext.value = null;
      reassignShowAll.value = false;
    }

    watch(reassignShowAll, () => {
      if (showAddResource.value && reassignContext.value) {
        addResourceSearchValue.value = '';
      }
    });

    async function queryAssignableResources(queryString, cb) {
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const params = new URLSearchParams({
          booking_id: String(props.booking.id),
          q: queryString || ''
        });
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/staff/assignable?${params.toString()}`;
        const response = await axios.get(url, config);
        const rows = Array.isArray(response.data) ? response.data : [];
        const scopedCategoryId = reassignContext.value && !reassignShowAll.value
          ? reassignContext.value.resource_category_id
          : null;
        const results = rows
          .filter(r => scopedCategoryId == null || r.resource_category_id === scopedCategoryId)
          .map(r => {
            const displayName = (r.first_name && r.last_name)
              ? `${r.first_name} ${r.last_name}`
              : (r.label || 'Unnamed');
            return {
              ...r,
              value: displayName,
              display_name: displayName
            };
          });
        cb(results);
      } catch (error) {
        console.error('Failed to load assignable resources:', error);
        cb([]);
      }
    }

    async function handleSelectAssignableResource(item) {
      if (!item || !item.id) return;
      const isReassign = reassignContext.value != null;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${props.booking.id}/resource-assignment`;
        const body = isReassign
          ? {
              resource_slot_id: item.id,
              unassign_slot_id: reassignContext.value.resource_slot_id,
              is_locked: 1
            }
          : { resource_slot_id: item.id };
        await axios.put(url, body, config);
        notify({
          type: 'success',
          message: isReassign ? 'Resource reassigned' : 'Resource assigned',
          timeout: 2000
        });
        closeAddResource();
        await fetchDrawerData();
        emit('booking-changed');
      } catch (error) {
        console.error('Failed to assign resource:', error);
        notify({
          type: 'danger',
          message: error.response?.data || (isReassign ? 'Failed to reassign resource' : 'Failed to assign resource'),
          timeout: 3000
        });
      }
    }

    async function unassignResource(resource) {
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${props.booking.id}/resource-assignment`;
        await axios.put(url, {
          resource_slot_id: resource.resource_slot_id,
          unassign: true
        }, config);
        notify({ type: 'success', message: 'Resource unassigned', timeout: 2000 });
        await fetchDrawerData();
        emit('booking-changed');
      } catch (error) {
        console.error('Failed to unassign resource:', error);
        notify({ type: 'danger', message: error.response?.data || 'Failed to unassign resource', timeout: 3000 });
      }
    }

    function handleResourceCommand(cmd, resource) {
      if (cmd === 'edit') {
        // Let the parent open ResourceDrawer for this slot.
        // Parent should re-open BookingDrawer when ResourceDrawer closes.
        emit('edit-resource-slot', {
          resource_slot_id: resource.resource_slot_id,
          booking_id: props.booking.id
        });
      } else if (cmd === 'reassign') {
        openReassign(resource);
      } else if (cmd === 'unassign') {
        unassignResource(resource);
      }
    }

    // Staff assignment operations
    // ============================================

    // el-select's built-in filterable prop only matches the option label, which
    // hides the email whenever a name exists. Filter over name AND email instead.
    const staffFilterText = ref('');
    function filterStaff(query) {
      staffFilterText.value = (query || '').toLowerCase();
    }
    function userMatchesFilter(u, q) {
      if (!q) return true;
      const name = (u.name || ((u.first_name || '') + ' ' + (u.last_name || ''))).toLowerCase();
      return name.includes(q) || (u.email || '').toLowerCase().includes(q);
    }
    const availableStaff = computed(() => {
      const assignedIds = new Set((drawerData.value.staff || []).map(s => s.id));
      return (drawerData.value.users || []).filter(u =>
        !assignedIds.has(u.id) && userMatchesFilter(u, staffFilterText.value));
    });

    const assigneeFilterText = ref('');
    function filterAssignees(query) {
      assigneeFilterText.value = (query || '').toLowerCase();
    }
    const assigneeOptions = computed(() =>
      (drawerData.value.users || []).filter(u => userMatchesFilter(u, assigneeFilterText.value)));

    async function handleStaffSelect(userId) {
      if (!userId) return;
      if (staffSaving.value) return;
      const user = drawerData.value.users.find(u => u.id === userId);
      if (!user) return;

      const updatedStaff = [...drawerData.value.staff, {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email
      }];

      await saveStaff(updatedStaff);
      staffSearchQuery.value = null;
      staffFilterText.value = '';
      showStaffPicker.value = false;
    }

    async function removeStaff(member) {
      if (staffSaving.value) return;
      const updatedStaff = drawerData.value.staff.filter(s => s.id !== member.id);
      await saveStaff(updatedStaff);
    }

    async function saveStaff(staffArray) {
      staffSaving.value = true;
      const previousStaff = [...drawerData.value.staff];

      // Optimistic UI update
      drawerData.value.staff = staffArray;

      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/staff/bookings`;

        await axios.post(url, {
          staff: staffArray.map(s => ({
            id: s.id,
            first_name: s.first_name,
            last_name: s.last_name,
            email: s.email
          })),
          booking_slot_id: props.booking.id,
          ticket_id: drawerData.value.parent_ticket_id || null
        }, config);

        emit('booking-changed');
      } catch (error) {
        console.error('Failed to save staff:', error);
        drawerData.value.staff = previousStaff;
      } finally {
        staffSaving.value = false;
      }
    }

    async function confirmDeleteSlot() {
      const gameName = drawerData.value.booking ? drawerData.value.booking.game_name : 'this';
      const result = await swal({
        title: 'Delete slot?',
        html: `This will permanently remove the <strong>${gameName}</strong> slot.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true
      });

      if (!result.value) return;

      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${props.booking.id}?new_status=undefined&delete_waivers=false`;
        await axios.delete(url, config);
        emit('close');
        emit('booking-changed');
      } catch (error) {
        console.error('Failed to delete slot:', error);
      }
    }

    function onSlotUpdated() {
      drawerView.value = 'detail';
      fetchDrawerData();
      emit('booking-changed');
    }

    function onBookingCreated() {
      // Append mode is one-shot: after a successful append, auto-exit so the
      // banner clears immediately. Multi-add is uncommon enough that the
      // friction of forgetting to hit "Done" (and then clicking a booked
      // slot expecting to edit) outweighs the convenience of a persistent
      // session. To add another, staff re-enters append mode from the new
      // booking's drawer.
      if (appendModeState.isActive) {
        exitAppendMode();
        emit('booking-changed');
        onClose();
        return;
      }
      drawerView.value = 'detail';
      fetchDrawerData();
      emit('booking-changed');
    }

    function onPaymentCollected() {
      drawerView.value = 'detail';
      fetchDrawerData();
      emit('booking-changed');
    }

    function onRescheduled() {
      drawerView.value = 'detail';
      fetchDrawerData();
      emit('booking-changed');
    }

    function onCustomerUpdated() {
      drawerView.value = 'detail';
      fetchDrawerData();
      emit('booking-changed');
    }

    async function onTicketsUpdated() {
      drawerView.value = 'detail';
      await fetchDrawerData();
      sections.value.tickets = true;
      emit('booking-changed');
    }

    // After an item is added from AddItemView: return to detail, refetch so
    // totals reflect the new line, and auto-expand the transaction summary
    // section so the receipt is visible without the user having to click.
    async function onItemAdded() {
      drawerView.value = 'detail';
      await fetchDrawerData();
      sections.value.transaction = true;
      emit('booking-changed');
    }

    function onEmailSent() {
      drawerView.value = 'detail';
      // Keep the section expanded so the user sees the refreshed stats.
      sections.value.customerCommunication = true;
      // Optimistic bump — the send was successful, the count should reflect
      // it immediately. The lambda's Send-event row write is often async
      // enough that an immediate /email-events re-fetch races it and returns
      // the pre-send count. fetchCommSection below uses max-merge so the
      // optimistic value can't be clobbered if that race plays out.
      commStats.value = {
        ...commStats.value,
        sent: commStats.value.sent + 1
      };
      commSectionLoaded.value = false;
      fetchDrawerData();
      fetchCommSection();
    }

    async function fetchCommSection() {
      const txId = currentBookingData.value?.transaction_id;
      if (!txId) {
        commStats.value = { sent: 0, opened: 0, bounced: 0 };
        commSectionLoaded.value = true;
        return;
      }
      if (commSectionLoading.value) return;
      commSectionLoading.value = true;
      try {
        const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
        const config = { headers: { Authorization: token } };
        // Lean fetch — only the events we need for the stats line. Settings
        // for the active-state gate come from drawerData.email_settings (loaded
        // once with the drawer GET, shared across public sub-bookings).
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/email-events?transaction_id=${txId}&count=500`;
        const response = await axios.get(url, config);
        const events = response.data?.email_events || [];
        const fetched = computeCommStats(events);
        // Max-merge so a stale fetch (one that raced the lambda's event
        // write) can never decrement an optimistic onEmailSent bump. The
        // transaction_id watch resets commStats to zero before fetching a
        // different booking, so the merge can't carry stats across.
        commStats.value = {
          sent: Math.max(commStats.value.sent, fetched.sent),
          opened: Math.max(commStats.value.opened, fetched.opened),
          bounced: Math.max(commStats.value.bounced, fetched.bounced)
        };
        commSectionLoaded.value = true;
      } catch (error) {
        console.error('Failed to load communication section:', error);
        commStats.value = { sent: 0, opened: 0, bounced: 0 };
      } finally {
        commSectionLoading.value = false;
      }
    }

    // Send-anchored grouping: each Send event opens a new email group.
    // Subsequent events for the same (email_type, recipient) attach to the
    // most recent group. Matches the aggregation in CommunicationHistoryView
    // so the section stats line up with the cards on the history view.
    function computeCommStats(events) {
      const sorted = [...events]
        .filter(e => e && e.timestamp)
        .sort((a, b) => {
          const diff = new Date(a.timestamp) - new Date(b.timestamp);
          if (diff !== 0) return diff;
          if (a.event_type === 'Send' && b.event_type !== 'Send') return -1;
          if (b.event_type === 'Send' && a.event_type !== 'Send') return 1;
          return 0;
        });

      const groups = [];
      const lastForKey = new Map();
      for (const e of sorted) {
        const key = `${e.email_type || 'unknown'}|${(e.customer_email || '').toLowerCase()}`;
        if (e.event_type === 'Send' || !lastForKey.has(key)) {
          const g = { events: [e] };
          groups.push(g);
          lastForKey.set(key, g);
        } else {
          lastForKey.get(key).events.push(e);
        }
      }

      const failureTypes = new Set(['Bounce', 'Complaint', 'Reject', 'Rendering Failure']);
      let sent = 0, opened = 0, bounced = 0;
      for (const g of groups) {
        sent++;
        if (g.events.some(e => e.event_type === 'Open')) opened++;
        if (g.events.some(e => failureTypes.has(e.event_type))) bounced++;
      }
      return { sent, opened, bounced };
    }

    // Fetch when the section is expanded for the first time (or after a send
    // resets commSectionLoaded). Avoids paying the round trip on every drawer
    // open for users who don't care about email status.
    watch(() => sections.value.customerCommunication, (expanded) => {
      if (expanded && !commSectionLoaded.value) {
        fetchCommSection();
      }
    });

    // Reset the section stats when the drawer switches to a different booking
    // or a different sub-booking. email_settings doesn't need resetting — it
    // comes from drawerData and is automatically refreshed when the drawer
    // re-fetches.
    watch(() => currentBookingData.value?.transaction_id, () => {
      commSectionLoaded.value = false;
      commStats.value = { sent: 0, opened: 0, bounced: 0 };
      if (sections.value.customerCommunication) fetchCommSection();
    });

    // Live transaction watcher — subscribes to the transaction's WS channel
    // so updates from a POS payment (or a future webhook) auto-refresh the
    // drawer instead of requiring a manual close-reopen.
    let txWatcher = null;
    function stopTxWatcher() {
      if (txWatcher) {
        txWatcher.disconnect();
        txWatcher = null;
      }
    }
    watch(
      () => [props.show, currentBookingData.value?.transaction_id],
      ([showing, txId]) => {
        stopTxWatcher();
        if (showing && txId) {
          txWatcher = new TransactionWatcher(Number(txId), () => {
            // Re-fetch the drawer data. The WS message only carries an ID;
            // we trust the server's view over any in-memory state.
            fetchDrawerData();
          });
        }
      },
      { immediate: true },
    );
    onBeforeUnmount(stopTxWatcher);

    function capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    // ============================================
    function copySlotId() {
      if (!props.booking) return;
      navigator.clipboard.writeText(String(props.booking.id));
    }

    function copyCustomFields(format) {
      const fields = currentCustomFields.value;
      if (!fields.length) return;

      let text;
      const getTimestamp = (f) => {
        if (!f.timestamp) return '';
        return `${getLocalDate(f.timestamp)}, ${getLocalTime(f.timestamp)}`;
      };

      if (format === 'csv') {
        const header = 'Field,Response,Date';
        const rows = fields.map(f => {
          const label = (f.label || '').replace(/"/g, '""');
          const value = (formatFieldResponse(f) || '').replace(/"/g, '""');
          const ts = getTimestamp(f).replace(/"/g, '""');
          return `"${label}","${value}","${ts}"`;
        });
        text = [header, ...rows].join('\n');
      } else {
        text = fields.map(f => {
          const line = `${f.label}: ${formatFieldResponse(f)}`;
          const ts = getTimestamp(f);
          return ts ? `${line} (${ts})` : line;
        }).join('\n');
      }

      navigator.clipboard.writeText(text);
    }

    // Subtask operations — uses standard project ticket endpoints
    // ============================================

    function getPriority(id) {
      return priorities.find(p => p.id === id) || null;
    }

    function formatDueDate(d) {
      if (!d) return '';
      return moment(String(d).substring(0, 10), 'YYYY-MM-DD').format(props.dateFormat);
    }

    function isSubtaskOverdue(task) {
      if (!task.due_date || task.completed) return false;
      const dueStr = task.due_date.substring(0, 10);
      const today = new Date().toISOString().substring(0, 10);
      return dueStr < today;
    }

    function getProjectTicketUrl(ticketId) {
      const projectId = drawerData.value.bookings_project_id || 0;
      const base = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/projects/${projectId}/tickets`;
      return ticketId ? `${base}/${ticketId}` : base;
    }

    function openCreateSubtaskForm() {
      editingSubtaskId.value = null;
      showCreateSubtaskForm.value = true;
      newSubtask.value = { title: '', priority: 3, assignee: null, due_date: null };
      if (!sections.value.subtasks) sections.value.subtasks = true;
    }

    function cancelCreateSubtask() {
      showCreateSubtaskForm.value = false;
      newSubtask.value = { title: '', priority: 3, assignee: null, due_date: null };
    }

    // Create via POST /projects/{projectId}/tickets (same as ProjectTicket.vue)
    async function createSubtask() {
      if (!newSubtask.value.title.trim()) return;

      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };

        const statuses = drawerData.value.statuses;
        const defaultStatus = statuses.find(s => s.lifecycle_status === 'open') || statuses[0];

        const gameName = drawerData.value.booking.game_name || 'Booking';
        const parentTicketTitle = `${gameName} - ${formattedBookingDate.value} ${formattedStartTime.value} #${props.booking.id}`;

        const response = await axios.post(getProjectTicketUrl(), {
          title: newSubtask.value.title.trim(),
          description: '',
          assignee: newSubtask.value.assignee,
          status: defaultStatus ? defaultStatus.id : 1,
          priority: newSubtask.value.priority,
          due_date: newSubtask.value.due_date,
          booking_slot_id: props.booking.id,
          parent_ticket_id: drawerData.value.parent_ticket_id,
          parent_ticket_title: parentTicketTitle
        }, config);

        const created = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

        // Cache project_id and parent_ticket_id from response (set on first create)
        if (created.project_id) {
          drawerData.value.bookings_project_id = created.project_id;
        }
        if (created.parent_ticket_id) {
          drawerData.value.parent_ticket_id = created.parent_ticket_id;
        }

        const selectedUser = newSubtask.value.assignee
          ? drawerData.value.users.find(u => u.id === newSubtask.value.assignee)
          : null;

        drawerData.value.subtasks.push({
          id: created.id,
          title: newSubtask.value.title.trim(),
          priority: newSubtask.value.priority,
          assignee: newSubtask.value.assignee,
          assignee_name: selectedUser ? selectedUser.name : null,
          status: defaultStatus ? defaultStatus.id : 1,
          status_name: defaultStatus ? defaultStatus.name : 'Open',
          lifecycle_status: defaultStatus ? defaultStatus.lifecycle_status : 'open',
          completed: false,
          due_date: newSubtask.value.due_date,
          project_id: created.project_id || drawerData.value.bookings_project_id
        });

        showCreateSubtaskForm.value = false;
        newSubtask.value = { title: '', priority: 3, assignee: null, due_date: null };
      } catch (error) {
        console.error('Failed to create subtask:', error);
      }
    }

    // Toggle via PUT /projects/{projectId}/tickets/{ticketId} (same as ProjectTicket.vue)
    async function toggleSubtask(task) {
      const wasCompleted = task.completed;
      task.completed = !task.completed;

      try {
        const statuses = drawerData.value.statuses;
        const targetStatus = wasCompleted
          ? (statuses.find(s => s.lifecycle_status === 'open') || statuses[0])
          : (statuses.find(s => s.lifecycle_status === 'done'));

        if (!targetStatus) return;

        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        await axios.put(getProjectTicketUrl(task.id), {
          title: task.title,
          priority: task.priority,
          status: targetStatus.id,
          assignee: task.assignee,
          due_date: task.due_date || null
        }, config);

        task.status = targetStatus.id;
        task.status_name = targetStatus.name;
        task.lifecycle_status = targetStatus.lifecycle_status;
      } catch (error) {
        task.completed = wasCompleted;
        console.error('Failed to toggle subtask:', error);
      }
    }

    function startEditSubtask(task) {
      showCreateSubtaskForm.value = false;
      editingSubtaskId.value = task.id;
      editSubtask.value = {
        title: task.title,
        priority: task.priority,
        assignee: task.assignee || null,
        due_date: task.due_date ? task.due_date.substring(0, 10) : null
      };
    }

    function cancelEditSubtask() {
      editingSubtaskId.value = null;
      editSubtask.value = { title: '', priority: null, assignee: null, due_date: null };
    }

    // Update via PUT /projects/{projectId}/tickets/{ticketId} (same as ProjectTicket.vue)
    async function saveEditSubtask(task) {
      if (!editSubtask.value.title.trim()) return;

      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        await axios.put(getProjectTicketUrl(task.id), {
          title: editSubtask.value.title.trim(),
          priority: editSubtask.value.priority,
          status: task.status,
          assignee: editSubtask.value.assignee,
          due_date: editSubtask.value.due_date
        }, config);

        const selectedUser = editSubtask.value.assignee
          ? drawerData.value.users.find(u => u.id === editSubtask.value.assignee)
          : null;

        task.title = editSubtask.value.title.trim();
        task.priority = editSubtask.value.priority;
        task.assignee = editSubtask.value.assignee;
        task.assignee_name = selectedUser ? selectedUser.name : null;
        task.due_date = editSubtask.value.due_date;

        editingSubtaskId.value = null;
      } catch (error) {
        console.error('Failed to update subtask:', error);
      }
    }

    // Delete via DELETE /projects/{projectId}/tickets/{ticketId} (same as ProjectTicket.vue)
    async function deleteSubtask(task) {
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        await axios.delete(getProjectTicketUrl(task.id), config);

        const idx = drawerData.value.subtasks.findIndex(s => s.id === task.id);
        if (idx > -1) drawerData.value.subtasks.splice(idx, 1);
      } catch (error) {
        console.error('Failed to delete subtask:', error);
      }
    }

    async function onSubtaskDragEnd() {
      const previousOrder = [...drawerData.value.subtasks];
      const subtaskIds = drawerData.value.subtasks.map(s => s.id);
      const parentTicketId = drawerData.value.parent_ticket_id;

      if (!parentTicketId) return;

      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        await axios.put(getProjectTicketUrl(parentTicketId), { subtask_positions: subtaskIds }, config);
      } catch (err) {
        console.error('Failed to save subtask order:', err);
        drawerData.value.subtasks = previousOrder;
      }
    }

    // ============================================
    // Data loading
    // ============================================
    async function fetchDrawerData() {
      if (!props.booking || !props.booking.id) return;

      // Reset attachment state
      attachments.value = [];
      attachmentsLoaded.value = false;
      uploadingFiles.value = [];
      uploadErrors.value = [];
      deletingAttachmentId.value = null;
      lightboxImage.value = null;

      loading.value = true;
      try {
        const token = await getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/bookings/${props.booking.id}/drawer`;
        const response = await axios.get(url, config);

        drawerData.value = {
          booking: response.data.booking || {},
          notes: response.data.notes || [],
          waivers: response.data.waivers || [],
          game_instances: response.data.game_instances || [],
          subtasks: (response.data.subtasks || []).map(t => ({
            ...t,
            completed: t.lifecycle_status === 'done' || t.completed
          })),
          custom_fields: response.data.custom_fields || [],
          payments: response.data.payments || [],
          transaction_items: response.data.transaction_items || [],
          pricing: response.data.pricing || {},
          // OTC-2964: group tax and fee config with linked_games and linked_items
          // (get-booking-drawer-details v48+). Null on an older lambda, so the
          // create view falls back to the view-level props.
          taxes: response.data.taxes || null,
          fees: response.data.fees || null,
          tickets: response.data.tickets || [],
          gift_cards: response.data.gift_cards || [],
          sibling_bookings: response.data.sibling_bookings || [],
          tax_lines: response.data.tax_lines || [],
          fee_lines: response.data.fee_lines || [],
          discount_lines: response.data.discount_lines || [],
          tip_lines: response.data.tip_lines || [],
          resources: response.data.resources || [],
          staff: response.data.staff || [],
          activity_log: response.data.activity_log || [],
          is_public: response.data.is_public || false,
          children: response.data.children || [],
          aggregated: response.data.aggregated || {},
          users: response.data.users || [],
          statuses: response.data.statuses || [],
          email_settings: response.data.email_settings || [],
          bookings_project_id: response.data.bookings_project_id || null,
          parent_ticket_id: response.data.parent_ticket_id || null,
          attachment_count: response.data.attachment_count || 0,
          tags: response.data.tags || [],
          customer_tags: response.data.customer_tags || [],
          booking_request: response.data.booking_request || null
        };

        // Public bookings have per-child transactions; the master record's
        // financial fields are zero. When the drawer is opened directly into
        // collect-payment from a row, auto-select the child that has a balance
        // due so currentBookingData / transactionFinancials bind to the right
        // transaction. Done before loading flips false so CollectPaymentView
        // mounts with the correct booking on first render.
        if (drawerView.value === 'collect-payment'
            && drawerData.value.is_public
            && drawerData.value.children.length > 0
            && activeChildId.value === null) {
          const child = drawerData.value.children.find(c => parseFloat(c.due) > 0)
            || drawerData.value.children[0];
          if (child) activeChildId.value = child.id;
        }
      } catch (error) {
        console.error('Failed to fetch drawer data:', error);
        drawerData.value = {
          booking: props.booking.originalData || {},
          notes: [],
          waivers: [],
          game_instances: [],
          subtasks: [],
          custom_fields: [],
          payments: [],
          transaction_items: [],
          pricing: {},
          tickets: [],
          gift_cards: [],
          sibling_bookings: [],
          tax_lines: [],
          fee_lines: [],
          discount_lines: [],
          tip_lines: [],
          resources: [],
          staff: [],
          activity_log: [],
          is_public: false,
          children: [],
          aggregated: {},
          users: [],
          statuses: [],
          email_settings: [],
          bookings_project_id: null,
          parent_ticket_id: null,
          attachment_count: 0,
          tags: [],
          customer_tags: [],
          booking_request: null
        };
      } finally {
        loading.value = false;
      }
    }

    // ============================================
    // ============================================
    // Add to cart
    // ============================================
    function emitAddToCart() {
      const b = drawerData.value.booking;
      const p = drawerData.value.pricing;
      if (!b) return;

      // Build a cart-ready object using drawer data (avoids bloating the kanban endpoint)
      const cartItem = {
        id: b.id,
        booking_date: b.booking_date,
        end_date: b.end_date,
        start_time: b.start_time,
        end_time: b.end_time,
        status: b.status,
        game_id: b.game_id,
        game_name: b.game_name,
        group_size: b.group_size,
        is_public_booking: b.is_public_booking,
        is_custom_pricing_enabled: p.is_custom_pricing_enabled,
        pricing_type_id: p.pricing_type_id,
        cumulative_category_pricing: p.cumulative_category_pricing,
        hide_pricing_rate: p.hide_pricing_rate,
        limit_purchase_category: p.limit_purchase_category,
        custom_pricing: p.tiers || [],
        allow_deposit: p.allow_deposit,
        require_deposit: p.require_deposit,
        deposit_type: p.deposit_type,
        deposit_amount: p.deposit_amount,
      };
      emit('add-to-cart', cartItem);
    }

    // Drawer open/close
    // ============================================
    function onClose() {
      emit('update:show', false);
      emit('close');
    }

    // Overlay backdrop click. In normal mode it dismisses the drawer; in
    // cart mode the overlay is transparent + pointer-events: none so this
    // handler shouldn't fire at all, but we gate defensively in case CSS
    // is overridden somewhere upstream.
    function onOverlayClick() {
      if (props.cartMode) return;
      onClose();
    }

    function onEscKey(e) {
      if (e.key === 'Escape' && props.show) {
        onClose();
      }
    }

    // Watch show prop
    watch(() => props.show, (newVal) => {
      if (newVal) {
        // Cart mode keeps the calendar behind interactive, so don't lock
        // body scroll — otherwise the page underneath becomes inert and
        // additional slot clicks won't reach FullCalendar.
        previousOverflow = document.body.style.overflow;
        if (!props.cartMode) {
          document.body.style.overflow = 'hidden';
        }
        document.addEventListener('keydown', onEscKey);
        // Append mode hijacks the initial view to CreateBookingView — but only
        // when the clicked slot is actually appendable. If a surface bypasses
        // its canAppendToSlot guard (e.g., legacy action-popover paths), we
        // don't want to drop staff into the unfamiliar create-booking view on
        // a booked private slot — open it normally so they can still see what
        // they clicked. The defense-in-depth check below the fetchDrawerData
        // call closes the drawer with a toast in that case.
        // Card objects from calendar surfaces wrap the row in `originalData`,
        // so we check there too (the top level often only has display fields).
        const cardSlot = props.booking || {};
        const slotForGuard = cardSlot.originalData || cardSlot;
        const shouldHijackView = appendModeState.isActive
          && canAppendToSlot(slotForGuard);
        drawerView.value = shouldHijackView
          ? 'create-booking'
          : (props.initialView || 'detail');
        // Pre-select a group's tab when opened on a specific public child;
        // resolves once children load from fetchDrawerData. Null = overview.
        activeChildId.value = props.initialChildId || null;
        // Reset all sections — only slot details and customer expanded by default
        Object.keys(sections.value).forEach(key => {
          sections.value[key] = (key === 'slotDetails' || key === 'customer' || key === props.initialSection);
        });
        fetchDrawerData().then(() => {
          // Append-mode click guard: private slots that already have a booking
          // (transaction attached or completed) can't accept another one. Public
          // slots are the exception — clicking them in append mode adds a new
          // child group under the master, which add-booking-to-transaction
          // already handles via handlePublicBookingSlot.
          if (appendModeState.isActive) {
            const slot = drawerData.value.booking || {};
            const isPublic = !!drawerData.value.is_public;
            const slotIsTaken = !!slot.transaction_id || slot.status === 'completed';
            if (slotIsTaken && !isPublic) {
              notify({
                type: 'warning',
                message: 'This slot is already booked. Pick an open slot to add to the transaction.',
                verticalAlign: 'top',
                horizontalAlign: 'right',
                timeout: 4000
              });
              onClose();
              return;
            }
          }
          if (props.initialSection === 'attachments') {
            loadAttachments();
          }
        });
      } else {
        document.body.style.overflow = previousOverflow;
        document.removeEventListener('keydown', onEscKey);
        // Reset state
        drawerView.value = 'detail';
        showCreateSubtaskForm.value = false;
        editingSubtaskId.value = null;
        showStaffPicker.value = false;
        staffSearchQuery.value = null;
        activityLog.value = [];
        // TransactionSummaryBlock unmounts with the drawer (v-if), so its
        // internal tip/promo draft state clears automatically — no manual
        // reset needed here.
      }
    });

    return {
      loading,
      drawerView,
      activityLog,
      activityLogLoading,
      drawerData,
      sections,
      isBooked,
      isCompleted,
      hasTransactionData,
      hasTransaction,
      isPublicBooked,
      showBookingCta,
      isRequested,
      pendingRequest,
      requestRefundLabel,
      approvingRequest,
      approveRequest,
      onRequestDecided,
      onRequestActionError,
      pricingTypeLabel,
      reservationStyleLabel,
      pricingSourceLabel,
      depositLabel,
      durationsLabel,
      bookedDurationLabel,
      childDurationLabel,
      bookingStatusLabel,
      statusBadgeStyle,
      isStatusLocked,
      canEditCurrent,
      canManageNotesCurrent,
      availableStatuses,
      onStatusChange,
      savingStatus,
      resolvedCurrencySymbol,
      onTransactionSummaryUpdated,
      onAddAnotherBooking,
      bookingNotes,
      sortedNotes,
      importantNote,
      deleteNoteFromInline,
      workflowState,
      workflowDetailBadgeStyle,
      savingWorkflowState,
      currentWorkflowStateId,
      onWorkflowStateChange,
      savingTags,
      groupTagsLoading,
      availableTags,
      onTagCommand,
      removeTag,
      onTagDropdownVisible,
      openAddTag,
      tagAddDropdown,
      tagChipStyle,
      headerBorderStyle,
      formattedBookingDate,
      formattedEndDate,
      isMultiDay,
      formattedStartTime,
      formattedEndTime,
      completedSubtaskCount,
      allSubtasksDone,
      capitalizeFirst,
      onCustomerUpdated,
      onTicketsUpdated,
      onItemAdded,
      onEmailSent,
      commSectionLoading,
      commStats,
      totalTicketPlayers,
      totalInstanceBreaks,
      totalTicketPrice,
      privateBookingFee,
      ticketsCardTotal,
      isBuyoutUpgrade,
      buyoutAmount,
      activeChildId,
      isPublicWithChildren,
      publicChildren,
      publicAggregated,
      activeChild,
      displayedCustomerTags,
      currentBookingData,
      bookingForCreate,
      showPerChildSections,
      slotHasExistingBookings,
      isPrivateReservation,
      remainingBlocked,
      savingRemainingBlocked,
      toggleRemainingBlocked,
      isIncompleteBooking,
      incompleteResetStatus,
      resettingIncomplete,
      resetIncompleteSlot,
      headerGameName,
      headerGameImage,
      headerMetaParts,
      headerSlotNotice,
      cartCount,
      currentTickets,
      currentWaivers,
      pdfGeneratingId,
      waiverPdf,
      downloadWaiver,
      currentActivePayments,
      currentCustomFields,
      alertedCustomFields,
      currentLineItems,
      currentGiftCards,
      siblingBookings,
      transactionFinancials,
      transactionTaxLines,
      transactionFeeLines,
      transactionDiscountLines,
      transactionTipLines,
      getAmount,
      getLocalDate,
      getLocalTime,
      formatTimeRaw,
      getInstanceDuration,
      formatFieldResponse,
      toggleSection,
      viewCustomer,
      viewTransaction,
      onPaymentCollected,
      onRescheduled,
      handleAction,
      confirmDeleteSlot,
      toggleResourceLock,
      showAddResource,
      addResourceSearchValue,
      reassignContext,
      reassignShowAll,
      openAddResource,
      closeAddResource,
      queryAssignableResources,
      resourceParty,
      visibleResources,
      handleSelectAssignableResource,
      unassignResource,
      handleResourceCommand,
      showStaffPicker,
      staffSearchQuery,
      staffSaving,
      staffColors,
      availableStaff,
      staffFilterText,
      filterStaff,
      assigneeFilterText,
      filterAssignees,
      assigneeOptions,
      handleStaffSelect,
      removeStaff,
      copySlotId,
      copyCustomFields,
      priorities,
      showCreateSubtaskForm,
      newSubtask,
      editingSubtaskId,
      editSubtask,
      getPriority,
      formatDueDate,
      isSubtaskOverdue,
      datePickerFormat,
      openCreateSubtaskForm,
      cancelCreateSubtask,
      createSubtask,
      toggleSubtask,
      startEditSubtask,
      cancelEditSubtask,
      saveEditSubtask,
      deleteSubtask,
      onSubtaskDragEnd,
      attachments,
      uploadingFiles,
      uploadErrors,
      isDragging,
      deletingAttachmentId,
      lightboxImage,
      toggleAttachments,
      handleAttachmentDrop,
      handleAttachmentFileSelect,
      downloadAttachment,
      previewAttachment,
      confirmDeleteAttachment,
      isImage,
      isSpreadsheet,
      isDocument,
      formatFileSize,
      attachmentDisplayCount,
      onBookingCancelled,
      onSlotUpdated,
      onBookingCreated,
      emitAddToCart,
      onClose,
      onOverlayClick,
      pricingModelExpanded,
      formatPhoneDisplay,
      fetchDrawerData
    };
  }
};
</script>

<style lang="scss" scoped>
// ============================================
// Drawer overlay & container
// ============================================
.booking-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

// Cart mode: overlay becomes a transparent passthrough so clicks on the
// calendar behind reach FullCalendar. The drawer panel itself re-enables
// pointer-events so its UI stays interactive.
.booking-drawer-overlay--cart {
  background: transparent;
  pointer-events: none;

  .booking-drawer {
    pointer-events: auto;
  }
}

.booking-drawer {
  position: fixed;
  top: 0;
  /* Shift left when the wizard side panel is open so both are visible side by side.
     Variable set by OnboardingSidePanel; fallback 0 when no wizard panel. */
  right: var(--wizard-panel-offset, 0);
  transition: right 0.3s ease;
  width: 520px;

  // Ensure labels are readable regardless of parent page styles
  label {
    color: rgba(255, 255, 255, 0.7);
  }
  // 100vh includes mobile browser chrome (Safari address bar / bottom
  // toolbar) — the footer ends up below the visible area. 100dvh (dynamic
  // viewport height) accounts for chrome. 100vh stays as fallback for
  // older browsers (iOS <15.4, Chrome <108) which ignore the dvh declaration.
  height: 100vh;
  height: 100dvh;
  background: #171826;
  display: flex;
  flex-direction: column;
  z-index: 1051;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.3);

  // Sub-views must flex within the drawer and scroll internally
  // (.drawer-subview covers any view built on the shared partial)
  > .drawer-subview,
  > .notes-view,
  > .activity-log-view,
  > .cancel-view,
  > .edit-slot-view,
  > .edit-pricing-view,
  > .create-booking-view,
  > .edit-customer-view,
  > .edit-tickets-view,
  > .add-item-view,
  > .collect-payment-view,
  > .reschedule-view {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  @media (max-width: 1200px) {
    width: 450px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100vw;
  }
}

// ============================================
// Header
// ============================================
.drawer-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(180deg, #2d2d44 0%, #252538 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &__top {
    display: flex;
    align-items: start;
    justify-content: flex-end;
    gap: 8px;
  }

  &__ctas {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.header-title-block {
  flex: 1;
  min-width: 0;
}

.header-title {
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
}

.header-meta {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 6px;

  &__part {
    white-space: nowrap;
  }

  &__sep {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 700;
  }
}

.header-date {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.game-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-action-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.drawer-header-cta {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 30px;
  border: none;
  border-radius: 6px;
  background: #e14eca;
  color: #fff;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #c839b2;
    box-shadow: 0 0 0 3px rgba(225, 78, 202, 0.25);
  }

  &:active {
    background: #b032a0;
  }
}

.drawer-header-cta-secondary {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 30px;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 6px;
  background: rgba(225, 78, 202, 0.1);
  color: #e14eca;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  display: inline-flex;
  align-items: center;

  &:hover {
    background: rgba(225, 78, 202, 0.18);
    border-color: rgba(225, 78, 202, 0.6);
  }
}

// ============================================
// Workflow badge (matches Bookings.vue pattern)
// ============================================
// ============================================
// Quick stats
// ============================================
.quick-stats {
  display: flex;
  gap: 16px;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.quick-stat {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 4px;

  i { font-size: 11px; }

  &--ok { color: #00f2c3; }
  &--alert { color: #ff8d72; }
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;

  &.bg-success { background: #00f2c3; }
  &.bg-warning { background: #ff8d72; }
}

// ============================================
// Body
// ============================================
.drawer-body {
  flex: 1 1 auto;
  // min-height: 0 is required for flex children with overflow on iOS Safari —
  // without it, the body expands to fit content and the drawer can't scroll.
  // Pattern matches BaseDrawer.vue (used by ResourceDrawer / BulkActionsDrawer).
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
}

// ============================================
// Private reservation banner (slot upgraded to private)
// ============================================
.private-reservation-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: #1d8cf8;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  line-height: 1.4;

  i {
    font-size: 16px;
    flex-shrink: 0;
  }

  strong { font-weight: 600; }
}

// Incomplete booking banner — warning treatment (matches Bookings.vue's
// base-alert type="warning"). Shown when a slot is stuck in 'booked'
// without a transaction; offers a reset CTA back to a chosen status.
// Pending-request decision. Rendered as an extension of the drawer header
// chrome (edge to edge, no radius, no card border) rather than as another
// .bd-section, so it doesn't read as an insert above the section stack.
// The header already carries the teal status color as its 3px top border,
// so this strip only washes, it never re-borders.
.request-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  // break out of .drawer-body padding (16px 24px) to sit flush under the header
  margin: -16px -24px 16px;
  padding: 12px 24px;
  background: rgba(0, 214, 180, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
  }

  &__hint {
    font-size: 12px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.7);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  &__approve {
    height: 30px;
    padding: 0 14px;
    border: none;
    border-radius: 6px;
    background: #00d6b4;
    color: #1e1e2f;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
      background: #33dfc3;
      box-shadow: 0 0 0 3px rgba(0, 214, 180, 0.25);
    }
    &:disabled { opacity: 0.6; cursor: default; }
  }

  &__deny {
    height: 30px;
    padding: 0 12px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(253, 93, 147, 0.12);
      color: #fd5d93;
    }
    &:disabled { opacity: 0.6; cursor: default; }
  }
}

@media (max-width: 768px) {
  .request-strip {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;

    &__actions { justify-content: flex-end; }
  }
}

.incomplete-booking-banner {
  margin-bottom: 16px;
  padding: 14px 16px;
  background: rgba(255, 178, 54, 0.08);
  border: 1px solid rgba(255, 178, 54, 0.4);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  line-height: 1.4;

  &__alert {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    color: #ffb236;
    margin-bottom: 10px;

    i {
      font-size: 16px;
      flex-shrink: 0;
      margin-top: 1px;
    }

    strong {
      font-weight: 600;
      color: #ffb236;
    }
  }

  &__meta {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);

    i { font-size: 12px; }
  }

  &__form {
    margin-bottom: 8px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }
}

// ============================================
// Public booking nav & overview
// ============================================
.public-booking-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 14px;
  margin-bottom: 2px;
  overflow-x: auto;
  flex-wrap: nowrap;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { height: 0; }
}

.nav-pill {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.08);
  }

  &.active {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.15);
    border-color: #e14eca;
  }
}

.overview-section {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.overview-stat {
  text-align: center;
  padding: 6px 4px;

  & + & {
    border-left: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__value {
    display: block;
    font-size: 15px;
    font-weight: 600;
  }

  &__label {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 1px;
  }
}

.overview-groups-list {
  display: flex;
  flex-direction: column;
}

.overview-group-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  cursor: pointer;
  transition: background 0.15s;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.04);
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}

// Block remaining seats (OTC-1459) — flat settings-style row, no card chrome
.remaining-block-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 4px;
  padding: 14px 4px 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  &__text {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  &__title {
    font-weight: 600;
    font-size: 0.875rem;

    i {
      margin-right: 8px;
      color: #e14eca;
    }
  }

  &__desc {
    font-size: 0.75rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.55);
  }
}

.white-content .remaining-block-row {
  border-top-color: rgba(0, 0, 0, 0.08);

  .remaining-block-row__desc {
    color: rgba(0, 0, 0, 0.5);
  }
}

// ============================================
// Sections
// ============================================
.bd-section {
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;

  &--danger {
    border-color: rgba(245, 54, 92, 0.2);
    background: rgba(245, 54, 92, 0.03);
  }
}

.danger-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin-bottom: 16px;
  border-top: 2px solid rgba(245, 54, 92, 0.85);
  background: rgba(245, 54, 92, 0.04);
  border-radius: 0 0 8px 8px;
  gap: 16px;
}

.danger-action-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.danger-action-label {
  font-size: 13px;
  font-weight: 600;
}

.danger-action-hint {
  font-size: 11px;
  line-height: 1.3;
}

.danger-action-btn {
  flex-shrink: 0;
  background: transparent !important;
  border: 1px solid rgba(245, 54, 92, 0.7) !important;
  color: #f5364c !important;
  font-size: 12px !important;
  padding: 6px 14px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(245, 54, 92, 0.15) !important;
    border-color: rgba(245, 54, 92, 0.85) !important;
  }
}

.neutral-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin-bottom: 16px;
  border-top: 2px solid rgba(225, 78, 202, 0.85);
  background: rgba(225, 78, 202, 0.04);
  border-radius: 0 0 8px 8px;
  gap: 16px;

  // Hangs off the bottom of a .bd-section card (card clips the corners).
  &--attached {
    margin-bottom: 0;
  }
}

.neutral-action-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.neutral-action-label {
  font-size: 13px;
  font-weight: 600;
}

.neutral-action-hint {
  font-size: 11px;
  line-height: 1.3;
}

.neutral-action-btn {
  flex-shrink: 0;
  background: transparent !important;
  border: 1px solid rgba(225, 78, 202, 0.7) !important;
  color: #e14eca !important;
  font-size: 12px !important;
  padding: 6px 14px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(225, 78, 202, 0.15) !important;
    border-color: rgba(225, 78, 202, 0.85) !important;
  }
}

.section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.02);

  i { flex-shrink: 0; }
  > span { flex-shrink: 0; }

  &.collapsible {
    cursor: pointer;
    transition: background 0.2s ease;
    justify-content: space-between;

    &:hover {
      background: rgba(255, 255, 255, 0.04);
    }
  }

  &.always-visible {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.section-content {
  padding: 16px;
}

// ============================================
// Financial grid (booking details)
// ============================================
.financial-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.financial-item {
  display: flex;
  flex-direction: column;

  .label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  .value {
    font-size: 18px;
    font-weight: 600;
    color: white;
  }
}

.financial-details {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

// ============================================
// Status badge (inline in slot details)
// ============================================
.status-badge-inline {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

// Unassigned workflow state — JS computed returns white-rgba colors tuned for
// dark mode; on light mode those wash into the white card. Override with
// dark-on-light tones. !important needed because inline :style sits at
// inline-specificity (1000).
.white-content .status-badge-inline--unassigned {
  background: rgba(0, 0, 0, 0.06) !important;
  color: rgba(0, 0, 0, 0.65) !important;
}

.status-badge-inline--editable {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: relative;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.92);
  }
}

.status-badge-inline__icon {
  font-size: 10px;
  opacity: 0.85;
}

// Invisible el-select stretched over the badge so clicking the badge opens
// the native popper menu. We keep el-select for its body-level popper
// (avoids being clipped by the drawer's overflow:auto).
.status-badge-inline__select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;

  ::v-deep .el-input,
  ::v-deep .el-input__inner,
  ::v-deep .el-input__suffix {
    height: 100%;
    border: none;
    padding: 0;
    background: transparent;
  }
}

// ============================================
// Tickets section
// ============================================
.tickets-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ticket-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
}

.ticket-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tickets-total {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  margin-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
}

.pricing-reference {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding-bottom: 4px;
  }
}

// ============================================
// Custom message row
// ============================================
.detail-row--stack {
  align-items: flex-start !important;
  & > span:first-child {
    flex-shrink: 0;
  }
}
.custom-message-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: right;
  max-width: 70%;
  &__text {
    word-break: normal;
    overflow-wrap: break-word;
    line-height: 1.3;
  }
  &__meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
  }
  &__badge {
    background: rgba(225, 78, 202, 0.15) !important;
    color: #e14eca !important;
    font-size: 10px !important;
    padding: 2px 6px !important;
    border-radius: 4px !important;
    font-weight: 500;
  }
  &__hidden {
    color: rgba(255, 255, 255, 0.5);
    font-style: italic;
    font-size: 10px;
  }
}

// ============================================
// Customer link
// ============================================
.customer-link {
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: none;
  }
}

.section-header__link {
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover { text-decoration: none; opacity: 0.8; }
}

// ============================================
// Drawer actions (available slots)
// ============================================
// ============================================
// Waivers
// ============================================
.waivers-list-flat {
  display: flex;
  flex-direction: column;
}

.waiver-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  & + .waiver-row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.waiver-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.waiver-name {
  font-weight: 500;
  font-size: 13px;
}

.waiver-name-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.waiver-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1.4;
  white-space: nowrap;

  &.waiver-tag--minor {
    background: rgba(0, 188, 212, 0.18);
    color: #4dd0e1;
    border: 1px solid rgba(0, 188, 212, 0.4);
  }

  &.waiver-tag--not-participating {
    background: rgba(125, 125, 125, 0.18);
    color: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(125, 125, 125, 0.4);
  }
}

.white-content .waiver-tag--minor {
  background: rgba(0, 150, 175, 0.12);
  color: #00838f;
  border-color: rgba(0, 150, 175, 0.35);
}

.white-content .waiver-tag--not-participating {
  background: rgba(34, 42, 66, 0.08);
  color: rgba(34, 42, 66, 0.7);
  border-color: rgba(34, 42, 66, 0.2);
}

.white-content .comm-action-btn {
  background: #f5f6fa;
  border-color: #e3e3e3;
  color: #344675;

  &:hover {
    background: #e9ecef;
    border-color: #cdd0d6;
    color: #344675;
  }

  &.comm-action-btn--primary {
    background: rgba(225, 78, 202, 0.1);
    border-color: #e14eca;
    color: #e14eca;

    &:hover {
      background: rgba(225, 78, 202, 0.18);
      color: #e14eca;
      border-color: #e14eca;
    }
  }
}

.white-content .comm-stats {
  color: rgba(52, 70, 117, 0.75);

  &--loading,
  &--empty {
    color: rgba(52, 70, 117, 0.55);
  }
}

.white-content .comm-stat-sep {
  color: rgba(52, 70, 117, 0.35);
}

.white-content .comm-stat--negative {
  color: #a3192a;
}

.waiver-contact {
  font-size: 11px;
  display: flex;
  gap: 10px;
  margin-top: 2px;
}

.waiver-date {
  font-size: 11px;
  white-space: nowrap;
  text-align: right;
}

.waiver-row-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.waiver-download-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &.is-busy {
    pointer-events: none;
    opacity: 0.6;
  }
}

// ============================================
// Custom fields
// ============================================
.custom-fields-list {
  display: flex;
  flex-direction: column;
}

.custom-field-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;

  & + .custom-field-item {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.custom-field-item__meta {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.custom-field-item__meta-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.custom-field-item__timestamp {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.custom-field-item--alert {
  background: rgba(255, 214, 0, 0.06);
  border-left: 3px solid #ffd600;
  padding-left: 8px;
  margin-left: -10px;
  border-radius: 4px;

  & + .custom-field-item {
    border-top-color: transparent;
  }
}

.custom-field-item__alert-icon {
  color: #ffd600;
  font-size: 12px;
  margin-right: 4px;
  vertical-align: middle;
}

.alert-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  background: rgba(255, 214, 0, 0.15);
  border: 1px solid rgba(255, 214, 0, 0.4);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #ffd600;

  i {
    font-size: 12px;
  }
}

.alert-field {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 214, 0, 0.08);
  border-left: 3px solid #ffd600;
  border-radius: 6px;

  & + .alert-field {
    margin-top: 6px;
  }
}

.alert-field__icon {
  color: #ffd600;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.alert-field__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.alert-field__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.alert-field__value {
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.field-label {
  font-size: 11px;
}

.field-value {
  font-size: 13px;
}

// ============================================
// Game instances
// ============================================
.instances-list-flat {
  display: flex;
  flex-direction: column;
}

.instance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  & + .instance-row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.instance-info {
  display: flex;
  flex-direction: column;
  font-size: 13px;
}

.instance-hints {
  color: #f3a218;
}

.instance-breaks {
  color: #F5365C;
}

.instance-status-badge {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 500;
  line-height: 1.4;

  &--completed {
    background: rgba(45, 206, 137, 0.1);
    color: rgba(45, 206, 137, 0.7);
  }

  &--progress {
    background: rgba(251, 99, 64, 0.1);
    color: rgba(251, 99, 64, 0.7);
  }
}

.instance-link {
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
    text-decoration: none;
  }
}

// ============================================
// Resources
// ============================================
.resources-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.resource-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child { border-bottom: none; }
}

.resource-item__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.resource-item__lock-icon {
  font-size: 14px;
  flex-shrink: 0;
  cursor: pointer;
  transition: color 0.15s;

  &.locked { color: #ff8d72; }
  &.unlocked { color: rgba(255, 255, 255, 0.25); }
  &:hover { color: white; }
}

.resource-item__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.resource-item__name {
  font-size: 13px;
  font-weight: 500;
}

.resource-item__category {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.category-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}

.resource-item__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.resource-item__time {
  white-space: nowrap;
  flex-shrink: 0;
  color: rgba(125, 125, 125, 0.9);
}

.resource-item__category {
  color: rgba(125, 125, 125, 0.9);
}

.resource-item__menu-btn {
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  color: rgba(125, 125, 125, 0.9);
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }

  i { font-size: 14px; }
}

.resources-empty {
  color: rgba(125, 125, 125, 0.9);
  margin-bottom: 0;
}

.assignable-suggestion {
  display: flex;
  align-items: center;
  gap: 6px;
}

.reassign-banner {
  color: rgba(255, 255, 255, 0.7);
  padding: 6px 8px;
  border-left: 2px solid #e14eca;
  background: rgba(225, 78, 202, 0.08);
  border-radius: 3px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

// ============================================
// Staff section
// ============================================
.staff-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.staff-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child { border-bottom: none; }
  &:hover .staff-item__remove { opacity: 1; }
}

.staff-item__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.staff-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--sm {
    width: 22px;
    height: 22px;
    font-size: 10px;
  }
}

.staff-item__info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.staff-item__name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.staff-item__email {
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.staff-item__remove {
  opacity: 0;
  transition: opacity 0.15s;
  cursor: pointer;
  padding: 4px;

  i {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    transition: color 0.15s;
  }

  &:hover i { color: #fd5d93; }
}

.staff-picker {
  margin-top: 8px;
}

.staff-picker__input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.staff-picker__cancel {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 6px;
  flex-shrink: 0;

  &:hover { color: white; }
}

// ============================================
// Slot ID badge
// ============================================
.slot-id-badge {
  background: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  font-family: monospace;
  font-size: 11px !important;
  letter-spacing: 0.02em;
}

// ============================================
// Payments
// ============================================
.line-items-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.line-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
}

.line-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

// Receipt + tip/promo entry styles live with TransactionSummaryBlock.vue;
// it's the only thing rendering them now.

.payments-list-flat {
  display: flex;
  flex-direction: column;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 0;
}

.payment-row + .payment-row {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.payment-row-left {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.payment-method-label {
  font-size: 13px;
  font-weight: 500;
}

.payment-row-date {
  font-size: 11px;
}

.payment-row-right-col {
  text-align: right;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.collect-payment-link {
  background: none;
  border: none;
  color: #e14eca;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 0 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover { color: #ba54f5; }
}


// ============================================
// Customer communication section
// ============================================
.comm-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 10px;

  &--loading {
    color: rgba(255, 255, 255, 0.55);
  }

  &--empty {
    color: rgba(255, 255, 255, 0.55);
    font-style: italic;
  }
}

.comm-stat-sep {
  color: rgba(255, 255, 255, 0.35);
  user-select: none;
}

.comm-stat--negative {
  color: #ff6f60;
  font-weight: 500;
}

.comm-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.comm-action-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    color: white;
  }

  &--primary {
    background: rgba(225, 78, 202, 0.15);
    border-color: #e14eca;
    color: #e14eca;

    &:hover {
      background: rgba(225, 78, 202, 0.25);
      color: #e14eca;
      border-color: #e14eca;
    }
  }
}

// ============================================
// Subtasks
// ============================================
.subtasks-section {
  padding: 8px 16px 12px;
}

.subtask-drag-handle {
  color: rgba(255, 255, 255, 0.35);
  transition: color 0.15s;
  &:hover { color: rgba(255, 255, 255, 0.7); }
  &:active { color: rgba(255, 255, 255, 1); }
}
.subtask-ghost {
  opacity: 0.5;
  background: rgba(225, 78, 202, 0.08);
  border-left: 2px solid #e14eca;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.subtask-chosen {
  background: rgba(225, 78, 202, 0.06);
}

.subtask-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 4px;
  transition: background 0.15s ease;

  & + .subtask-row { border-top: 1px solid rgba(255, 255, 255, 0.05); }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
    .subtask-actions { opacity: 1; }
  }

  &--done {
    .subtask-title {
      text-decoration: line-through;
      opacity: 0.5;
    }
  }
}

.subtask-cb {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #e14eca;
}

.subtask-title {
  flex: 1;
  font-size: 13px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.subtask-link {
  cursor: pointer;
  color: #e14eca !important;
  &:hover {
    opacity: 0.8 !important;
    text-decoration: none !important;
  }
}

.subtask-assignee-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1d8cf8;
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.subtask-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;

  i {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: color 0.15s;

    &:hover { color: #fff; }
    &.icon-simple-remove:hover { color: #fd5d93; }
  }
}

.subtask-form-actions {
  display: flex;
  justify-content: flex-end;
}

.subtask-inline-select {
  width: 100%;

  ::v-deep .el-input__inner {
    height: calc(2.25rem + 2px);
    background-color: inherit;
    border-color: rgba(29, 37, 59, 0.5);
    color: white;
  }
}

.subtask-progress {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);

  &--done {
    color: #00f2c3;
  }
}

// ============================================
// Notes section
// ============================================
.important-note {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 214, 0, 0.08);
  border-left: 3px solid #ffd600;
  border-radius: 6px;
}

.important-note__star {
  color: #ffd600;
  flex-shrink: 0;
  margin-top: 2px;
}

.important-note__text {
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
  word-wrap: break-word;
  white-space: pre-wrap;
  flex: 1;
  min-width: 0;
}

.important-note__delete {
  flex-shrink: 0;
  align-self: flex-start;
}

.section-content--peek {
  padding: 10px 16px 12px;
}

.section-header__action {
  width: 24px;
  height: 24px;
  background: rgba(225, 78, 202, 0.15);
  border: none;
  border-radius: 5px;
  color: #e14eca;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s ease;

  &:hover { background: rgba(225, 78, 202, 0.25); }

  // Same tint and height as the icon-only square, widened to fit a label.
  &--labeled {
    width: auto;
    padding: 0 10px 0 8px;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
}

.copy-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  padding: 2px;
  font-size: 13px;
  line-height: 1;
  transition: color 0.15s ease;

  &:hover { color: #e14eca; }
}

.pricing-edit-link {
  background: none;
  border: none;
  font-size: 13px;
  cursor: pointer;
  padding: 0;

  &:hover { text-decoration: underline; }
}

.notes-inline-list {
  display: flex;
  flex-direction: column;
  max-height: 200px;
  overflow-y: auto;
}

.note-inline {
  padding: 8px 0;

  & + .note-inline {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.note-inline__text {
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.note-inline__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

.note-inline__meta {
  font-size: 11px;
  flex: 1;
  min-width: 0;
}

.note-inline__actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.note-inline__action-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(253, 93, 147, 0.2);
    color: #fd5d93;
  }
}

// ============================================
// Empty state
// ============================================
.empty-state {
  text-align: center;
  padding: 24px;

  i {
    display: block;
    margin-bottom: 8px;
  }
}

// ============================================
// Footer
// ============================================
.drawer-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
}

.open-full-link {
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: none;
  }
}

// ============================================
// Count badge
// ============================================
.count-badge {
  margin-left: 6px;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.45);
  font-weight: 500;
  line-height: 1.4;
}

// ============================================
// Skeleton loader
// ============================================
.skeleton-loader {
  .skeleton-section {
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);

    &:last-child { border-bottom: none; }
  }

  .skeleton-row {
    height: 20px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0;
  }

  .skeleton-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// ============================================
// Slide transition
// ============================================
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s ease;

  .booking-drawer {
    transition: transform 0.3s ease;
  }
}

.slide-enter,
.slide-leave-to {
  opacity: 0;

  .booking-drawer {
    transform: translateX(100%);
  }
}

// ============================================
// Text utilities (scoped overrides)
// ============================================
.text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
}

.text-success {
  color: #00f2c3 !important;
}

.text-warning {
  color: #ff8d72 !important;
}

.text-danger {
  color: #fd5d93 !important;
}

// ============================================
// Mobile
// ============================================
@media (max-width: 640px) {
  .booking-drawer {
    width: 100% !important;
  }

  .drawer-header {
    padding: 16px;
    position: relative;

    &__top {
      flex-wrap: wrap;
      padding-right: 40px;
    }

    &__ctas {
      flex-wrap: wrap;

      .close-btn {
        position: absolute;
        top: 16px;
        right: 16px;
      }
    }
  }

  .header-title {
    font-size: 15px;
  }

  .drawer-header-cta,
  .drawer-header-cta-secondary {
    height: 28px;
    line-height: 28px;
    padding: 0 10px;
    font-size: 11px;
  }
}

// Tag chips: shared styles live in assets/sass/dashboard/custom/_tag-chip.scss
</style>

<style lang="scss">
// ============================================
// Drawer-views scroll fix for iOS Safari. Every sub-view (drawer-views/*.vue)
// uses `.view-body { flex: 1; overflow-y: auto; }` — without `min-height: 0`
// the body grows to fit content on iOS instead of scrolling, hiding the
// footer behind the Safari toolbar. Pattern matches BaseDrawer.vue.
// ============================================
.booking-drawer .view-body {
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

// View footers (sub-view CTAs) — respect iOS home-indicator safe area so
// CTAs aren't hidden behind the bottom gesture bar.
.booking-drawer .view-footer {
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

// ============================================
// Status picker popper — appended to body, must sit above the drawer
// overlay (z-index: 1050) and drawer (z-index: 1051) so every option
// (including the last one) stays clickable.
// ============================================
.booking-drawer__status-popper {
  z-index: 2000 !important;
}

// Promo autocomplete popper — appended to body, only override is the
// z-index so it sits above the drawer overlay. All item layout/styling
// inherits Element UI's defaults so it matches the legacy promo picker
// in TransactionDetails byte-for-byte.
.booking-drawer__promo-popper {
  z-index: 2000 !important;
}

// ============================================
// LIGHT MODE OVERRIDES
// ============================================
.white-content {
  .booking-drawer {
    background: #ffffff;
    box-shadow: -10px 0 40px rgba(0, 0, 0, 0.1);
  }

  .booking-drawer .drawer-header {
    border-bottom-color: #e3e3e3;
    background: linear-gradient(180deg, #f8f9fa 0%, #f5f6fa 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .booking-drawer .header-title {
    color: #344675;
  }

  .booking-drawer .header-date {
    color: #9A9A9A;
  }

  .booking-drawer .header-meta {
    color: #6b7280;

    .header-meta__sep {
      color: #b0b6bf;
    }
  }

  .booking-drawer .close-btn,
  .booking-drawer .header-action-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;

    &:hover {
      background: #e9ecef;
      color: #344675;
    }
  }

  .booking-drawer .drawer-header-cta-secondary {
    background: rgba(225, 78, 202, 0.08);
    border-color: rgba(225, 78, 202, 0.3);
    color: #c839b2;
  }

  .booking-drawer .drawer-body {
    &::-webkit-scrollbar-track {
      background: #f5f6fa;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
    }
  }

  .booking-drawer .bd-section {
    background: #f8f9fa;
    border-color: #e3e3e3;
  }

  .booking-drawer .section-header {
    color: #344675;
    background: #f5f6fa;

    &.collapsible:hover {
      background: #ebedf0;
    }

    &.always-visible {
      border-bottom-color: #e3e3e3;
    }
  }

  .booking-drawer .financial-item {
    .label { color: #9A9A9A; }
    .value { color: #344675; }
  }

  .booking-drawer .financial-details {
    border-top-color: #e3e3e3;
  }

  .booking-drawer .detail-row {
    color: #525f7f;
  }

  .booking-drawer .count-badge {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.4);
  }

  .booking-drawer .waiver-row + .waiver-row {
    border-color: #e3e3e3;
  }

  .booking-drawer .instance-row + .instance-row {
    border-color: #e3e3e3;
  }

  .booking-drawer .payment-row + .payment-row {
    border-color: #e3e3e3;
  }

  .booking-drawer .line-item-row {
    background: #ffffff;
    border-color: #e3e3e3;
  }

  .booking-drawer .slot-id-badge {
    background: rgba(0, 0, 0, 0.06) !important;
    color: #525f7f !important;
  }

  .booking-drawer .payment-method-label {
    color: #344675;
  }

  .booking-drawer .payment-row-right-col {
    color: #344675;
  }

  .booking-drawer .drawer-footer {
    border-top-color: #e3e3e3;
    background: #f8f9fa;
  }

  .booking-drawer .skeleton-row {
    background: linear-gradient(90deg, #e9ecef 25%, #f5f6fa 50%, #e9ecef 75%);
    background-size: 200% 100%;
  }

  .booking-drawer .skeleton-section {
    border-bottom-color: rgba(0, 0, 0, 0.06);
  }

  .booking-drawer .quick-stats {
    border-top-color: rgba(0, 0, 0, 0.06);
  }

  .booking-drawer .quick-stat {
    color: #9A9A9A;
  }

  .booking-drawer .subtask-title {
    color: #344675;
  }

  .booking-drawer .subtask-drag-handle {
    color: rgba(0, 0, 0, 0.3);
    &:hover { color: rgba(0, 0, 0, 0.5); }
    &:active { color: rgba(0, 0, 0, 0.7); }
  }
  .booking-drawer .subtask-ghost {
    background: rgba(225, 78, 202, 0.06);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .booking-drawer .subtask-row:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  .booking-drawer .subtask-row + .subtask-row {
    border-top-color: rgba(0, 0, 0, 0.06);
  }

  .booking-drawer .subtask-row--done .subtask-title {
    color: #9A9A9A;
  }

  .booking-drawer .subtask-actions i {
    color: rgba(0, 0, 0, 0.3);
    &:hover { color: #344675; }
    &.icon-simple-remove:hover { color: #fd5d93; }
  }

  .booking-drawer .subtask-inline-select ::v-deep .el-input__inner {
    color: #344675;
    border-color: #e3e3e3;
  }

  .booking-drawer .staff-item {
    border-bottom-color: rgba(0, 0, 0, 0.06);
  }

  .booking-drawer .staff-item__name {
    color: #344675;
  }

  .booking-drawer .staff-item__remove i {
    color: rgba(0, 0, 0, 0.3);
    &:hover { color: #fd5d93; }
  }

  .booking-drawer .staff-picker__cancel {
    color: rgba(0, 0, 0, 0.3);
    &:hover { color: #344675; }
  }

  .booking-drawer .custom-field-item .field-label {
    color: #9A9A9A;
  }

  .booking-drawer .custom-field-item .field-value {
    color: #344675;
  }

  .booking-drawer .custom-field-item + .custom-field-item {
    border-color: #e3e3e3;
  }

  .booking-drawer .important-note {
    background: rgba(255, 214, 0, 0.12);
  }

  .booking-drawer .important-note__text {
    color: #344675;
  }

  .booking-drawer .note-inline + .note-inline {
    border-color: #e3e3e3;
  }

  .booking-drawer .note-inline__action-btn {
    background: #e9ebf0;
    color: #525f7f;
    &:hover {
      background: rgba(253, 93, 147, 0.15);
      color: #fd5d93;
    }
  }

  .booking-drawer .ticket-row {
    background: #ffffff;
    border-color: #e3e3e3;
  }

  .booking-drawer .tickets-total {
    border-top-color: #e3e3e3;
  }

  .booking-drawer .pricing-reference {
    border-top-color: #e3e3e3;
  }

  .booking-drawer .resource-item {
    border-bottom-color: #e3e3e3;
  }

  .booking-drawer .resource-item__lock-icon.unlocked {
    color: rgba(0, 0, 0, 0.2);
  }

  .booking-drawer .resource-item__lock-icon:hover {
    color: #344675;
  }

  .booking-drawer .section-header__action {
    background: rgba(225, 78, 202, 0.1);
    &:hover { background: rgba(225, 78, 202, 0.2); }
  }

  .booking-drawer .nav-pill {
    color: #525f7f;
    background: #f0f2f5;
    border-color: #d1d5db;

    &:hover {
      color: #344675;
      background: #e2e6ea;
    }

    &.active {
      color: #e14eca;
      background: rgba(225, 78, 202, 0.1);
      border-color: #e14eca;
    }
  }

  .booking-drawer .overview-section {
    background: #ffffff;
    border-color: #e3e3e3;
  }

  .booking-drawer .overview-stats {
    border-bottom-color: #e3e3e3;
  }

  .booking-drawer .overview-stat {
    & + & { border-left-color: #e3e3e3; }
    &__label { color: #9A9A9A; }
  }

  .booking-drawer .overview-group-row {
    &:hover { background: #f5f6fa; }
    & + & { border-top-color: #e3e3e3; }
  }

  .booking-drawer .danger-action-row {
    border-top-color: rgba(245, 54, 92, 0.2);
    background: rgba(245, 54, 92, 0.04);
  }

  .booking-drawer .danger-action-label {
    color: #344675;
  }

  .booking-drawer .neutral-action-row {
    border-top-color: rgba(225, 78, 202, 0.3);
    background: rgba(225, 78, 202, 0.04);
  }

  .booking-drawer .request-strip {
    background: rgba(63, 193, 171, 0.12);
    border-bottom-color: #e3e3e3;

    .request-strip__title { color: #344675; }
    .request-strip__hint { color: #525f7f; }

    .request-strip__approve {
      background: #3fc1ab;
      color: #ffffff;

      &:hover:not(:disabled) {
        background: #2a9d8a;
        box-shadow: 0 0 0 3px rgba(63, 193, 171, 0.25);
      }
    }

    .request-strip__deny {
      color: #9a9a9a;

      &:hover:not(:disabled) {
        background: rgba(253, 93, 147, 0.1);
        color: #fd5d93;
      }
    }
  }

  .booking-drawer .neutral-action-label {
    color: #344675;
  }

  // Override Bootstrap's text-white !important inside the drawer
  .booking-drawer .text-white {
    color: #344675 !important;
  }

  // Override text-muted inside drawer for light readability
  .booking-drawer .text-muted {
    color: #9A9A9A !important;
  }

  .booking-drawer .overview-stat__value.text-white {
    color: #344675 !important;
  }

  .booking-drawer .overview-group-row .text-white {
    color: #344675 !important;
  }

  .booking-drawer .note-inline__text.text-white {
    color: #344675 !important;
  }

  .booking-drawer .subtask-progress {
    color: #9A9A9A;

    &--done {
      color: #2dce89;
    }
  }

  // Inline-styled icons need class-based overrides
  .booking-drawer .overview-group-row .tim-icons.icon-minimal-right {
    color: rgba(0, 0, 0, 0.3) !important;
  }

  .booking-drawer .pricing-reference__header .tim-icons {
    color: rgba(0, 0, 0, 0.4) !important;
  }

  // Attachments light mode
  .booking-drawer .attachment-dropzone {
    background: rgba(0, 0, 0, 0.02);
    i { color: rgba(0, 0, 0, 0.3); }
    span { color: #525f7f; }
    .dropzone-hint { color: #9A9A9A; }
  }
  .booking-drawer .attachment-uploading {
    color: #525f7f;
  }
  .booking-drawer .attachment-icon {
    background: #f5f6fa;
    border-color: rgba(0, 0, 0, 0.08);
  }
  .booking-drawer .attachment-name {
    color: #344675;
  }
  .booking-drawer .attachment-meta {
    color: #9A9A9A;
  }
  .booking-drawer .attachment-actions i {
    color: #525f7f;
  }
  .booking-drawer .attachment-row {
    border-bottom-color: rgba(0, 0, 0, 0.06);
    &:hover { background: rgba(0, 0, 0, 0.02); }
  }
  .booking-drawer .log-detail {
    color: #525f7f;
  }
}

// Attachments
.attachment-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 16px;
  border: 2px dashed rgba(225, 78, 202, 0.3);
  border-radius: 8px;
  background: rgba(30, 30, 47, 0.4);
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 8px;
  i { font-size: 14px; color: rgba(255, 255, 255, 0.4); }
  span { font-size: 11px; color: rgba(255, 255, 255, 0.5); }
  .dropzone-hint { font-size: 10px; color: rgba(255, 255, 255, 0.3); }
  &:hover, &.dropzone-active {
    border-color: #e14eca;
    background: rgba(225, 78, 202, 0.08);
    i, span { color: rgba(255, 255, 255, 0.8); }
  }
}

.attachment-uploading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(225, 78, 202, 0.08);
  border-radius: 4px;
  margin-bottom: 4px;
  i { color: #e14eca; }
}

.attachment-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  font-size: 11px;
  color: #ec250d;
  background: rgba(236, 37, 13, 0.1);
  border-left: 3px solid #ec250d;
  border-radius: 4px;
  margin-bottom: 4px;
}

.attachment-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  &:hover { background: rgba(255, 255, 255, 0.03); }
  &:hover .attachment-actions { opacity: 1; }
}
.attachment-deleting { opacity: 0.4; pointer-events: none; }

.attachment-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: rgba(30, 30, 47, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  cursor: pointer;
  i { font-size: 14px; }
}

.attachment-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.attachment-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.attachment-meta {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
}

.attachment-actions {
  display: flex;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.15s ease;
  flex-shrink: 0;
  i {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    &:hover { color: #e14eca; }
    &.icon-trash-simple:hover { color: #ec250d; }
  }
}

.attachment-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.lightbox-content {
  position: relative;
  max-width: 80vw;
  max-height: 80vh;
  img {
    max-width: 80vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 4px;
  }
}
.lightbox-close {
  position: absolute;
  top: -30px;
  right: 0;
  font-size: 16px;
  color: white;
  cursor: pointer;
}
</style>
